// Overview.js — Project dashboard (blue gradient theme)
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useAtom } from "jotai";
import moment from "moment";
import { updateUserDataArray, loadUserData, loadJobOpenCommitmentsCombined, loadCostHistoryCombined, loadPFData, loadRework, readJobBanner, uploadJobBanner, deleteJobBanner, fNum, pNum } from '../db/company';
import { downscale as downscaleImage } from './fieldReport/PhotosStep';
import { fieldBonusRemoved } from '../helpers/fieldBonus';
import { buildCOLogRows, coLogToSheetRows, coLogTotalsRow } from '../helpers/cologRows';
import { invoiceListFrom, invoiceListToSheetRows } from '../helpers/invoiceList';
import { revenueBudgetByCode } from '../helpers/revenueBudget';
import { showToast } from '../helpers/toast';
import XLSX from 'xlsx';
import html2canvas from 'html2canvas';
import { fullCaptureOptions, planPages } from '../helpers/pageCapture';
import { buildLaborWorkbook, plannedHoursFromSheet } from '../helpers/laborWorkbook';
import JSZip from 'jszip';
import { jsPDF } from 'jspdf';
import AISummary from './AISummary';
import ProductivityCombined from './productivityCombined';
import { parseQtyMath, sharedUnit } from '../helpers/disciplineQty';
import VoltaGridPsrSections from './voltaGridPsrSections';
import VoltaGridEquipment from './voltaGridEquipment';
import VoltaGridRevenueReview from './voltaGridRevenueReview';
import VoltaGridLeadership from './voltaGridLeadership';
import VoltaGridCashFlow from './voltaGridCashFlow';
import VoltaGridProgressCurve from './voltaGridProgressCurve';
import { BID_SECTIONS, BID_JOB } from './voltaGridBidSummary';
import useBillingRevenue from './billingRevenue';
import { fuelLineAmt } from './billingFuel';
import { voltaGridWbsFor, voltaGridGroupFor, voltaGridDisciplineRank, foldVoltaGridDisciplines } from '../db/wbsVoltaGrid';
import defaultBannerImg from '../assets/strikefield.jpg';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);
import {
    jobDataAtom,
    jobUserDataAtom,
    jobOpenCommitmentsAtom,
    jobBillingsAtom,
    jobCostDetailAtom,
    jobCostDetailCondensedAtom,
    selectedJobAtom,
    selectedVersionAtom,
    dayZeroAtom,
    dataSourceVersionAtom,
    projectCompleteAtom,
    startDateActualAtom,
    selectedJobDataAtom,
    selectedVersionLockedAtom,
    k2DataAtom,
    jobUserDataPrevAtom,
    jobOpenCommitmentsPrevAtom,
    jobCostDetailPrevAtom,
    selectedWIPAtom,
    jobPhaseCodesAtom,
    sideMenuBudgetsAtom,
    sideMenuCostsAtom,
    phasedForecastAtom,
    budgetDeltaWipAtom,
    laborDeltaWipAtom,
    equipmentDeltaWipAtom,
    materialsDeltaWipAtom,
    subDeltaWipAtom,
    otherDeltaWipAtom,
    overheadDeltaWipAtom,
    contingencyDeltaWipAtom,
    billingUserDataAtom,
    BILLING_VERSION,
    userEmailAtom,
    jobLoadedAtom,
} from '../store/company';

// VoltaGrid sections hidden for now (Seth 10/9). The components are intact;
// flip to true to bring a section back (the cash flow Excel in the Export zip
// comes back with it).
const VG_SHOW_PROGRESS_CURVE = false;
const VG_SHOW_CASH_FLOW = false;

/* ─────────────────────────────   PALETTE (blue gradients)   ───────────────────────────── */
const palette = {
    bg: '#f0f4fa',
    card: '#ffffff',
    border: '#e2e8f0',
    blue50: '#eff6ff',
    blue100: '#dbeafe',
    blue200: '#bfdbfe',
    blue300: '#93c5fd',
    blue400: '#60a5fa',
    blue500: '#3b82f6',
    blue600: '#2563eb',
    blue700: '#1d4ed8',
    blue800: '#1e40af',
    blue900: '#172554',
    accent: '#2563eb',
    accentLight: '#dbeafe',
    text: '#0f172a',
    muted: '#64748b',
    green: '#16a34a',
    red: '#dc2626',
    orange: '#d97706',
    greenLight: '#dcfce7',
    redLight: '#fee2e2',
    orangeLight: '#fef3c7',
};

const shadows = {
    sm: '0 1px 3px rgba(0,0,0,.04), 0 1px 2px rgba(0,0,0,.06)',
    md: '0 4px 16px rgba(15,23,42,.06)',
    lg: '0 10px 40px rgba(15,23,42,.08)',
};

/* ─────────────────── SVG ICONS (black, sharp) ─────────────────── */
const Icon = ({ d, size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={palette.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);
const icons = {
    shield: <Icon d={<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>} />,
    barChart: <Icon d={<><line x1="12" y1="20" x2="12" y2="10" /><line x1="18" y1="20" x2="18" y2="4" /><line x1="6" y1="20" x2="6" y2="16" /></>} />,
    dollar: <Icon d={<><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>} />,
    calendar: <Icon d={<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>} />,
    wrench: <Icon d={<><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></>} />,
    fileText: <Icon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>} />,
    box: <Icon d={<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></>} />,
    users: <Icon d={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>} />,
    check: <Icon d={<><polyline points="20 6 9 17 4 12" /></>} />,
    pin: <Icon d={<><line x1="12" y1="17" x2="12" y2="22" /><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24z" /></>} />,
    message: <Icon d={<><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></>} />,
    printer: <Icon d={<><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></>} />,
    target: <Icon d={<><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>} />,
    clock: <Icon d={<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>} />,
    trending: <Icon d={<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>} />,
    download: <Icon d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>} />,
};

// Defined out here, not inside Overview: a component made in the render is a
// new type every render, so React remounted every card's contents on any
// state change - the Export's 'Exporting...' re-render reloaded the
// Productivity Tracker and closed its open Equipment / Electrical rows just
// as the page was captured.
const Card = ({ children, style, className }) => (
    <div className={className} style={{ background: palette.card, borderRadius: 10, boxShadow: shadows.md, border: `1px solid ${palette.border}`, padding: 16, ...style }}>{children}</div>
);

const SectionTitle = ({ icon, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {icon && <span style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>}
        <h3 style={{ margin: 0, fontSize: 11, fontWeight: 700, color: palette.text, letterSpacing: '-0.01em', textTransform: 'uppercase' }}>{children}</h3>
    </div>
);

/* Normalise cost / phase codes – anything that isn't exactly 6 digits maps to 100101 */
const normCC = (cc) => (cc && /^\d{6}$/.test(cc)) ? cc : '100101';
// A cost code's discipline when the Productivity Tracker doesn't set one: on
// a VoltaGrid job, its section in the client's WBS (the same lookup the
// Billing cost report rolls up on), so a job loaded without tracker rows (the
// 54026070 QPAC Blitz) still shows Indirects / Equipment / Electrical.
// Null elsewhere, where each caller keeps its own default.
const vgDiscipline = (cc, isVoltaGrid) => (isVoltaGrid ? voltaGridWbsFor(cc)?.name || null : null);
// The bid's Indirects section (or a discipline whose codes roll up to it): no
// quantities to earn, so a VoltaGrid job keeps it out of Total Costs' PF / CPI
// / SPI and sets it off with a heavy line (Aeron 10/28: "make this not pull
// into our PFs ... put like a heavy black line ... just have it not calculate
// the indirects into the PF").
const vgIndirect = (discipline, codes) => Math.floor(voltaGridDisciplineRank(discipline, codes)) === 0;
// The Productivity Tracker's discipline per cost code. On a VoltaGrid job a
// discipline the job named itself folds into the bid section its codes roll
// up to (AG Electrical Conduit and UG Electrical into Electrical, Aeron
// 10/22); `subgroup` keeps the job's own name, which shows as a row under the
// section. The budget rows and the figures behind them both read this, so
// they fold the same way.
const trackerDisciplines = (jobUserData, isVoltaGrid) => {
    const mapping = {};
    jobUserData.filter(item => item.Forecast_Tab === 'Productivity Tracker' && item.Forecast_Field_Name === 'discipline')
        .forEach(item => { mapping[normCC(item.Forecast_Row_ID)] = item.Forecast_Value || 'Other'; });
    return isVoltaGrid ? foldVoltaGridDisciplines(mapping) : { mapping, subgroup: {}, order: {} };
};

// `variant` — 'voltagrid' when rendered from the Overview VoltaGrid tab
// (overviewVoltaGrid.js). Same page and same saved rows; VoltaGrid-only
// sections hang off isVoltaGrid.
// One VoltaGrid man = a 60-hour week; weekly hours / 60 = FTE (man count).
const FTE_HOURS_PER_WEEK = 60;
// VoltaGrid labor bars split by craft: the WBS section the cost code rolls
// into — Equipment (section 4), Electrical (section 5), everything else Other.
const VG_CRAFTS = [
    { key: 'equipment', label: 'Equipment', actual: '#F4B183', forecast: '#7CAFDD' },
    { key: 'electrical', label: 'Electrical', actual: '#D9772E', forecast: '#3B78B5' },
    { key: 'other', label: 'Other', actual: '#FBDDC4', forecast: '#C3DAF0' },
];
const vgCraftOf = (code) => {
    const sec = voltaGridWbsFor(code)?.name;
    return sec === 'Equipment' ? 'equipment' : sec === 'Electrical' ? 'electrical' : 'other';
};
const emptyCrafts = () => ({ equipment: 0, electrical: 0, other: 0 });
function Overview({ variant } = {}) {
    const isVoltaGrid = variant === 'voltagrid';
    /* ─────────────────── ATOMS ─────────────────── */
    const [selectedJobDetails] = useAtom(selectedJobDataAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [userEmail] = useAtom(userEmailAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [versionInfo, setVersionInfo] = useState({});
    // Field Bonus (51800) is retired for versions June 18, 2026 forward — its
    // ETC no longer counts toward LEMSO / cost-code totals for those versions.
    const hideFieldBonus = fieldBonusRemoved(versionInfo?.Version_Start_DateTime);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    // After a job switch the loader (loadData.js) empties jobUserData and
    // fills it when the new job's read lands. Until then the Productivity
    // Tracker card and % complete wait, rather than show the last job's
    // numbers (Seth 10/19). Rows from another job are never this job's; an
    // empty read is ready once the loader says it landed.
    const [jobLoaded] = useAtom(jobLoadedAtom);
    const jobDataReady = jobUserData?.length > 0
        ? String(jobUserData[0]?.BU_ID ?? '').trim() === String(selectedJob ?? '').trim()
        : !!jobLoaded;
    const [jobCostDetail] = useAtom(jobCostDetailCondensedAtom);
    const [jobCostDetailRaw] = useAtom(jobCostDetailAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [jobUserDataPrevWip] = useAtom(jobUserDataPrevAtom);
    const [jobCostDetailPrevWip] = useAtom(jobCostDetailPrevAtom);
    const [jobOpenCommitmentsPrevWip] = useAtom(jobOpenCommitmentsPrevAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);
    // Δ EAC Previous compares against the last WIP before the selected
    // version (what the page loads) unless another version is picked on the
    // Budget Overview. A picked version's rows are read here and stay local —
    // the Prev atoms keep feeding every other tab off the WIP.
    const [prevPick, setPrevPick] = useState('');
    const [prevPicked, setPrevPicked] = useState(null); // { guid, userData, cost, commits }
    useEffect(() => { setPrevPick(''); }, [selectedJob, selectedVersion]);
    useEffect(() => {
        if (!prevPick || !selectedJob) { setPrevPicked(null); return undefined; }
        let dead = false;
        setPrevPicked(null);
        Promise.all([
            loadUserData({ id: selectedJob, forecastGuid: prevPick, live: false }),
            loadCostHistoryCombined({ id: selectedJob, versionGuid: prevPick }),
            loadJobOpenCommitmentsCombined({ id: selectedJob, versionGuid: prevPick }),
        ]).then(([u, c, o]) => {
            if (!dead) setPrevPicked({ guid: prevPick, userData: u?.data || [], cost: c?.data || [], commits: o?.data || [] });
        }).catch((err) => {
            if (dead) return;
            showToast(`Could not load that version: ${err?.message || 'error'}`);
            setPrevPick('');
        });
        return () => { dead = true; };
    }, [prevPick, selectedJob]);
    const prevCustom = prevPick && prevPicked && prevPicked.guid === prevPick ? prevPicked : null;
    const prevLoading = !!prevPick && !prevCustom;
    const prevVersion = useMemo(() => (prevCustom ? (versions || []).find((v) => v.Version_GUID === prevCustom.guid) || null : selectedWIP), [prevCustom, versions, selectedWIP]);
    const jobUserDataPrev = prevCustom ? prevCustom.userData : jobUserDataPrevWip;
    const jobCostDetailPrev = prevCustom ? prevCustom.cost : jobCostDetailPrevWip;
    const jobOpenCommitmentsPrev = prevCustom ? prevCustom.commits : jobOpenCommitmentsPrevWip;
    // The versions offered: everything before the one on screen, newest first.
    const prevOptions = useMemo(() => (versions || [])
        .filter((v) => ['WIP', 'Regular', 'Reg-locked', 'Unassigned', 'WIP-locked'].includes(v.Version_Type) && v.Version_GUID < selectedVersion)
        .sort((a, b) => new Date(b.Version_Start_DateTime) - new Date(a.Version_Start_DateTime))
        .map((v) => ({ value: v.Version_GUID, label: moment(v.Version_Start_DateTime).format('MMM D, YYYY') + (v.Version_Type.includes('WIP') ? ' - WIP' : '') })),
    [versions, selectedVersion]);
    const [dayZero] = useAtom(dayZeroAtom);
    const [k2Data] = useAtom(k2DataAtom);
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [smBudgets] = useAtom(sideMenuBudgetsAtom);
    const [smCosts] = useAtom(sideMenuCostsAtom);
    const [projectComplete] = useAtom(projectCompleteAtom);
    const [regular] = useAtom(phasedForecastAtom);
    const [startDateActual] = useAtom(startDateActualAtom);
    const [jobBillings] = useAtom(jobBillingsAtom);
    const [budgetDeltaWip] = useAtom(budgetDeltaWipAtom);
    const [laborDeltaWip] = useAtom(laborDeltaWipAtom);
    const [equipmentDeltaWip] = useAtom(equipmentDeltaWipAtom);
    const [materialsDeltaWip] = useAtom(materialsDeltaWipAtom);
    const [subcontractorsDeltaWip] = useAtom(subDeltaWipAtom);
    const [otherDeltaWip] = useAtom(otherDeltaWipAtom);
    const [overheadDeltaWip] = useAtom(overheadDeltaWipAtom);
    const [contingencyDeltaWip] = useAtom(contingencyDeltaWipAtom);
    // Billing rows live at their own job-level version and are loaded by the
    // Billing tab. Overview only needs them when someone asks for the invoice
    // download, so it borrows the same atom and fills it on demand.
    const [billingData, setBillingData] = useAtom(billingUserDataAtom);
    // VoltaGrid: revenue is the job's billable amount — the Billing tab's
    // pricing pass over the cost history (billingRevenue.js). Loads nothing
    // on the other jobs.
    const billRev = useBillingRevenue({ enabled: isVoltaGrid });

    /* ─────────────────── LOCAL STATE ─────────────────── */
    const [safetyData, setSafetyData] = useState({
        strike: { hours: 0, firstAid: 0, nearMiss: 0, ltas: 0, weatherImpacts: 0, incidentRates: 0, qaqc: 0 },
        subs: []
    });
    // Individual safety metric fields for better save/load control
    const [safetyFirstAid, setSafetyFirstAid] = useState(0);
    const [safetyIncidents, setSafetyIncidents] = useState(0);
    const [safetyNearMiss, setSafetyNearMiss] = useState(0);
    const [safetyStopWork, setSafetyStopWork] = useState(0);
    const [safetyQAQC, setSafetyQAQC] = useState(0);
    const [milestones, setMilestones] = useState([]);
    const [editingMilestoneId, setEditingMilestoneId] = useState(null);
    const [editingMilestone, setEditingMilestone] = useState(null);
    const editingMilestoneRef = React.useRef(null);
    const [reworkHoursData, setReworkHoursData] = useState({});
    const [reworkApiData, setReworkApiData] = useState([]);
    const [reworkApiLoading, setReworkApiLoading] = useState(false);
    const [changeOrderNotes, setChangeOrderNotes] = useState('');
    const [isEditingChangeOrderNotes, setIsEditingChangeOrderNotes] = useState(false);
    const [overviewNotes, setOverviewNotes] = useState('');
    const [isEditingOverviewNotes, setIsEditingOverviewNotes] = useState(false);
    const [billingNotes, setBillingNotes] = useState('');
    const [isEditingBillingNotes, setIsEditingBillingNotes] = useState(false);
    const [editingSafetyKey, setEditingSafetyKey] = useState(null);
    const [statusPopup, setStatusPopup] = useState(null);
    const [pctCompletePopup, setPctCompletePopup] = useState(null);
    const [planDebugPopup, setPlanDebugPopup] = useState(null);
    const [fcastDebugPopup, setFcastDebugPopup] = useState(null);
    const [actualDebugPopup, setActualDebugPopup] = useState(null);
    const [overallStatusPopup, setOverallStatusPopup] = useState(null);
    const [pfDebugPopup, setPfDebugPopup] = useState(null);
    const [cpiDebugPopup, setCpiDebugPopup] = useState(null);
    const [spiDebugPopup, setSpiDebugPopup] = useState(null);
    const [quantityData, setQuantityData] = useState({});
    const [progressData, setProgressData] = useState({});
    const [budgetData, setBudgetData] = useState({});
    const [bannerImage, setBannerImage] = useState(null);
    const bannerFileRef = React.useRef(null);
    const [bannerBusy, setBannerBusy] = useState(false);
    // How the photo sits in the 120px strip: y = which slice shows (% from the
    // top), zoom = magnification. Kept in Forecast_User_Data as a job-level
    // row (Version_GUID 0, like the Billing tab's rows): tab Overview, field
    // BannerView, value {"y","zoom"}. (jobBanner.js can also hold it as blob
    // metadata, but that function is not published yet, so the database is
    // the source for now.)
    const BANNER_VIEW_DEFAULT = { y: 40, zoom: 100 };
    const [bannerView, setBannerView] = useState(BANNER_VIEW_DEFAULT);
    const [bannerAdjust, setBannerAdjust] = useState(null); // { y, zoom } while adjusting, else null
    const [bannerMenuOpen, setBannerMenuOpen] = useState(false); // the small button at top right shows the actions
    const bannerDragRef = React.useRef(null);
    const bannerBoxRef = React.useRef(null);
    const [bannerNatural, setBannerNatural] = useState(null); // { w, h } of the loaded photo
    const overviewNotesRef = React.useRef(null);
    const billingNotesRef = React.useRef(null);
    const laborUploadRef = React.useRef(null);
    const cashFlowExportRef = React.useRef(null);
    const overviewRootRef = React.useRef(null);
    const [exportBusy, setExportBusy] = useState(false);
    const [etcBreakdownExpanded, setEtcBreakdownExpanded] = useState(false);
    const [prevBreakdownExpanded, setPrevBreakdownExpanded] = useState(false);
    const [spiExpanded, setSpiExpanded] = useState(true);
    const [collapsedDisciplines, setCollapsedDisciplines] = useState({});
    const [budgetPivot, setBudgetPivot] = useState('phase'); // 'phase' | 'lemso' | 'L' | 'E' | 'M' | 'S' | 'O'
    const [collapsedLemso, setCollapsedLemso] = useState({});
    const [disciplineTitles, setDisciplineTitles] = useState({});
    const [editingDisciplineTitle, setEditingDisciplineTitle] = useState(null);
    const [budgetNotes, setBudgetNotes] = useState({});
    const [editingBudgetNote, setEditingBudgetNote] = useState(null);
    const [editingPfKey, setEditingPfKey] = useState(null);
    const [editedPfValues, setEditedPfValues] = useState({});
    const [editingCpiKey, setEditingCpiKey] = useState(null);
    const [editedCpiValues, setEditedCpiValues] = useState({});
    const [editingSpiKey, setEditingSpiKey] = useState(null);
    const [editedSpiValues, setEditedSpiValues] = useState({});
    const disciplinesInitRef = React.useRef(false);
    const pfChartRef = React.useRef(null);
    const [pfOverTimeData, setPfOverTimeData] = useState(null);
    const [budgetFilterOpen, setBudgetFilterOpen] = useState(false);
    const [budgetFilterPhase, setBudgetFilterPhase] = useState('');
    const [budgetFilterDiscipline, setBudgetFilterDiscipline] = useState('');

    useEffect(() => {
        if (versions && versions.length > 0 && selectedVersion) {
            setVersionInfo(versions.find((v) => v.Version_GUID === selectedVersion) || {});
        }
    }, [versions, selectedVersion]);

    // Hero banner lives in blob storage (one per job, not per version) — see
    // functions/src/functions/jobBanner.js. Falls back to the default photo.
    useEffect(() => {
        let cancelled = false;
        setBannerImage(null);
        setBannerView({ y: 40, zoom: 100 });
        setBannerAdjust(null);
        setBannerMenuOpen(false);
        if (!selectedJob) return undefined;
        readJobBanner({ buId: selectedJob }).then((res) => {
            if (cancelled) return;
            setBannerImage(res?.sasUrl || null);
        }).catch(() => { });
        loadUserData({ id: selectedJob, forecastGuid: BILLING_VERSION, live: true }).then((res) => {
            if (cancelled) return;
            const rows = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
            const row = rows.find((r) => r?.Forecast_Tab === 'Overview' && r?.Forecast_Field_Name === 'BannerView');
            if (!row?.Forecast_Value) return;
            try {
                const v = JSON.parse(row.Forecast_Value);
                setBannerView({ y: Number(v.y) || 0, zoom: Number(v.zoom) || 100 });
            } catch (_) { /* a bad value just keeps the default */ }
        }).catch(() => { });
        return () => { cancelled = true; };
    }, [selectedJob]);

    // Natural size of the photo, so a drag in pixels maps onto the slice shown.
    useEffect(() => {
        setBannerNatural(null);
        if (!bannerImage || typeof Image === 'undefined') return undefined;
        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setBannerNatural({ w: img.naturalWidth, h: img.naturalHeight }); };
        img.src = bannerImage;
        return () => { cancelled = true; };
    }, [bannerImage]);

    useEffect(() => {
        setSpiExpanded(true);
        if (selectedJob) {
            loadPFData({ id: selectedJob }).then((response) => {
                if (response?.data && Array.isArray(response.data)) {
                    // Group by version, then by discipline
                    const byVersion = {};
                    response.data.forEach((row) => {
                        const vGuid = row.Version_GUID;
                        if (!byVersion[vGuid]) {
                            byVersion[vGuid] = {
                                versionName: row.Version_Notes || '',
                                startDate: row.Version_Start_DateTime,
                                disciplines: {}
                            };
                        }
                        try {
                            const val = JSON.parse(row.Forecast_Value);
                            byVersion[vGuid].disciplines[row.Forecast_Field_Name] = val;
                        } catch (e) {
                            byVersion[vGuid].disciplines[row.Forecast_Field_Name] = { actualPF: row.Forecast_Value, pmPF: '' };
                        }
                    });
                    setPfOverTimeData(byVersion);
                }
            }).catch(err => console.error('Error loading PF data:', err));
        }
    }, [selectedJob]);

    /* ─────────────── LOAD REWORK DATA FROM HCSS ─────────────── */
    useEffect(() => {
        if (selectedJob) {
            setReworkApiLoading(true);
            loadRework({ jobCode: selectedJob }).then(response => {
                setReworkApiData(response?.data || []);
            }).catch(err => {
                console.error('Error loading rework data:', err);
                setReworkApiData([]);
            }).finally(() => setReworkApiLoading(false));
        }
    }, [selectedJob]);

    /* ─────────────── LOAD SAVED DATA ─────────────── */
    useEffect(() => {
        if (jobUserData && selectedJob && selectedVersion) {
            // Reset accumulated state to prevent stale data from previous job/version
            setProgressData({});
            setQuantityData({});
            setReworkHoursData({});
            setBudgetNotes({});
            setDisciplineTitles({});
            // Safety counts are per version: a version with no rows shows 0, not
            // the last version's numbers.
            setSafetyFirstAid(0); setSafetyIncidents(0); setSafetyNearMiss(0); setSafetyStopWork(0); setSafetyQAQC(0);

            const loadedMilestones = [];
            // Load job-level settings (version-independent)
            let spiLoaded = false;
            jobUserData.forEach(item => {
                if (item.Forecast_Tab === 'Overview' && item.BU_ID === selectedJob && item.Forecast_Field_Name === 'SpiEnabled') {
                    setSpiExpanded(item.Forecast_Value === 'true');
                    spiLoaded = true;
                }
            });
            if (!spiLoaded) setSpiExpanded(true);
            // Load version-specific data
            jobUserData.forEach(item => {
                if (item.Forecast_Tab === 'Overview' &&
                    item.Version_GUID === selectedVersion &&
                    item.BU_ID === selectedJob) {
                    switch (item.Forecast_Field_Name) {
                        case 'safetyData':
                            try {
                                const parsed = JSON.parse(item.Forecast_Value || '{}');
                                const defaultStrike = { hours: 0, firstAid: 0, nearMiss: 0, ltas: 0, weatherImpacts: 0, incidentRates: 0, qaqc: 0 };
                                setSafetyData({ strike: { ...defaultStrike, ...parsed.strike }, subs: Array.isArray(parsed.subs) ? parsed.subs : [] });
                            } catch (e) { }
                            break;
                        case 'SafetyFirstAid':
                            setSafetyFirstAid(parseInt(item.Forecast_Value) || 0);
                            break;
                        case 'SafetyIncidents':
                            setSafetyIncidents(parseInt(item.Forecast_Value) || 0);
                            break;
                        case 'SafetyNearMiss':
                            setSafetyNearMiss(parseInt(item.Forecast_Value) || 0);
                            break;
                        case 'SafetyStopWork':
                            setSafetyStopWork(parseInt(item.Forecast_Value) || 0);
                            break;
                        case 'SafetyQAQC':
                            setSafetyQAQC(parseInt(item.Forecast_Value) || 0);
                            break;
                        case 'Milestone':
                            try { const m = JSON.parse(item.Forecast_Value); loadedMilestones.push({ ...m, id: item.Forecast_Row_ID }); } catch (e) { }
                            break;
                        case 'ReworkHours':
                            setReworkHoursData(prev => ({ ...prev, [item.Forecast_Row_ID]: item.Forecast_Value }));
                            break;
                        case 'ChangeOrderNotes': setChangeOrderNotes(item.Forecast_Value); break;
                        case 'OverviewNotes': setOverviewNotes(item.Forecast_Value); break;
                        case 'BillingNotes': setBillingNotes(item.Forecast_Value); break;
                        case 'BudgetNote':
                            setBudgetNotes(prev => ({ ...prev, [item.Forecast_Row_ID]: item.Forecast_Value }));
                            break;
                        case 'DisciplineTitle':
                            setDisciplineTitles(prev => ({ ...prev, [item.Forecast_Row_ID]: item.Forecast_Value }));
                            break;
                        case 'QuantityPlanned':
                        case 'QuantityActual':
                        case 'QuantityUnit':
                            setQuantityData(prev => ({ ...prev, [`${item.Forecast_Row_ID}_${item.Forecast_Field_Name}`]: item.Forecast_Value }));
                            break;
                        case 'ProgressWeight':
                        case 'ProgressPeriodPlan':
                        case 'ProgressPeriodActual':
                        case 'ProgressCumulativePlan':
                        case 'ProgressCumulativeActual':
                            setProgressData(prev => ({ ...prev, [`${item.Forecast_Row_ID}_${item.Forecast_Field_Name}`]: item.Forecast_Value }));
                            break;
                        default: break;
                    }
                }
            });
            // Milestones in the order they were entered (row ids are time-based,
            // and a loaded list carries its order in them), not database order.
            if (loadedMilestones.length > 0) setMilestones(loadedMilestones.sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true })));
        }
    }, [jobUserData, selectedJob, selectedVersion]);

    /* ─────────────── SAVE HELPERS ─────────────── */
    const save = (fieldName, value, rowId = '1') => {
        updateUserDataArray({
            userId: '154045', userName: 'Seth Bailey', tab: 'Overview', fieldName,
            value, version: selectedVersion || '', id: selectedJob || '', rowId, lockedVersion
        });
    };

    /* ─────────── AI SUMMARY PROMPT BUILDER ─────────── */
    const buildOverviewPrompt = useCallback(() => {
        if (!selectedJob || !budgetData || !budgetData['Total Costs']) return null;
        // Wait for job details to match selectedJob (prevents stale data from previous job)
        if (!selectedJobDetails || selectedJobDetails.BU_ID !== selectedJob) return null;
        // Verify budgetData was computed for THIS job (not stale from previous selection)
        if (budgetData._jobId !== selectedJob) return null;
        const job = selectedJobDetails;
        const tc = budgetData['Total Costs'] || {};
        const cont = budgetData['Contingency'] || {};
        // Wait for EAC to be populated (last derived value)
        if (!tc.eac || pNum(tc.eac) === 0) return null;
        // Strip formatting (commas, $, parens) so AI reads clean numbers
        const c = (v) => v == null || v === '-' ? 'N/A' : pNum(v);
        // gather discipline rows (exclude meta keys)
        const disciplines = Object.keys(budgetData).filter(k =>
            !['Total Costs', 'Contract Amount', 'Contingency', '_meta'].includes(k) && !k.startsWith('costCode_') && !k.startsWith('lemso')
        );
        const discSummaries = disciplines.map(d => {
            const r = budgetData[d];
            return `${d}: Budget=${c(r.currentBudget)}, Actual=${c(r.actualCosts)}, ETC=${c(r.etc)}, EAC=${c(r.eac)}, Delta=${c(r.deltaEACBudget)}, PF=${c(r.pf)}, CPI=${c(r.cpi)}, SPI=${c(r.spi)}`;
        }).join('\n');

        let milestonesStr = '';
        if (milestones.length > 0) {
            milestonesStr = '\nMilestones:\n' + milestones.map(m =>
                `- ${m.description || 'Milestone'}: Target ${m.targetDate || 'N/A'}, Status: ${m.status || 'N/A'}`
            ).join('\n');
        }

        return `Analyze this construction project's COST data and provide key insights:

Job: ${job.BU_ID || selectedJob} - ${job.BU_Description || 'N/A'}
Client: ${job.AR_AB_Name || 'N/A'}
PM: ${job.Project_Manager_Name || 'N/A'}
Contract Type: ${job.Cat_Code_BU_08 || 'N/A'}
Status: ${job.Job_Status === 'C' ? 'Complete' : 'Active'}
Scope: ${job.Scope_of_Work || 'N/A'}
Physical Complete: ${projectComplete || 'N/A'}

Total Cost Budget: ${c(tc.currentBudget)}
Actual Costs to Date: ${c(tc.actualCosts)}
Open Commitments: ${c(tc.openCommit)}
Estimate to Complete (ETC): ${c(tc.etc)}
Estimate at Completion (EAC): ${c(tc.eac)}
Delta (EAC vs Cost Budget): ${c(tc.deltaEACBudget)}
Contingency Budget: ${c(cont.currentBudget)}, Contingency ETC: ${c(cont.etc)}
PF=${c(tc.pf)}, CPI=${c(tc.cpi)}, SPI=${c(tc.spi)}

Discipline Breakdown:
${discSummaries}
${milestonesStr}
${overviewNotes ? '\nPM Notes: ' + overviewNotes : ''}

ANALYSIS INSTRUCTIONS — BE AGGRESSIVE AND BLUNT:
1. Do NOT restate margins as simple facts. Explain WHY the margin is what it is — what disciplines or behaviors are driving it. CRITICAL: Delta = EAC minus Cost Budget. A NEGATIVE delta means EAC is LESS than budget — the project is under budget, which is a GOOD thing. A POSITIVE delta means EAC exceeds budget — that is a problem. Do not confuse these.
2. CONTINGENCY: Do NOT mention contingency at all unless Physical Complete is above 85%. If it is above 85% and the team still has most contingency as ETC, they are likely sandbagging — call it out. Estimating they will use their budgeted contingency is meaningless — challenge whether it is a real forecast or a placeholder.
3. CHANGE ORDERS: Question any performed work that does not have an approved change order backing it. This is a financial risk.
4. BUDGET/CODING MISTAKES: Flag any discipline where actual costs exist but the budget is zero or near-zero — this is likely a coding error or misalignment. However, if less than 1% of total costs are miscoded to a wrong phase, do not treat it as severe — just note it briefly.
5. PHASE ALIGNMENT: Compare actual cost distribution across disciplines against the budget distribution. If spending patterns do not follow the budget structure, flag it as poor cost control.
6. GOOD PERFORMANCE: If multiple disciplines are tracking on or under budget with healthy CPI/SPI, acknowledge the good work — call out what the team is doing well.
7. Be direct. Do not soften findings. Never blame the PM individually — always refer to "the project team" or "the team".`;
    }, [selectedJob, selectedJobDetails, budgetData, projectComplete, milestones, overviewNotes]);

    /* ─────────── LABOR GRAPH EXPORT / UPLOAD ─────────── */
    // The labor graph's data as drawn. The standard Overview adds the
    // trending EAC marker at the last week; the VoltaGrid tab (a client view)
    // leaves it off. The graph and the workbook's Graph tab both draw this.
    const laborChartData = () => {
        const trendHrs = progressScheduleData?.['__overall']?.totalTrendingHrs;
        if (isVoltaGrid || !trendHrs || !laborChartConfig.data.labels) return laborChartConfig.data;
        const dotData = laborChartConfig.data.labels.map((_, i) => i === laborChartConfig.data.labels.length - 1 ? trendHrs : null);
        return { ...laborChartConfig.data, datasets: [...laborChartConfig.data.datasets, { label: 'Trending EAC Hours', data: dotData, borderColor: '#6b7280', backgroundColor: '#6b7280', pointRadius: 4, pointHoverRadius: 5, pointStyle: 'circle', showLine: false, fill: false, yAxisID: 'y1', type: 'line', order: 0 }] };
    };

    // The labor graph workbook (Graph and Hours tabs, helpers/laborWorkbook.js)
    // as an xlsx buffer, or null before the graph has data. Its own Export
    // button and the VoltaGrid Export zip both use it.
    const laborWorkbook = async () => {
        if (!laborChartConfig) return null;
        const { weeks, tableRows } = laborChartConfig;
        return buildLaborWorkbook({
            weeks, tableRows, crafts: isVoltaGrid ? VG_CRAFTS : [],
            job: selectedJob, jobName: selectedJobDetails?.BU_Description || '', today: moment(),
            chart: { ...laborChartData(), options: laborChartConfig.options },
        });
    };

    const handleLaborExport = async () => {
        const wbout = await laborWorkbook();
        if (!wbout) return;
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Labor_Graph_${selectedJob}_${moment().format('YYYY-MM-DD')}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleLaborUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                const data = new Uint8Array(e.target.result);
                const wb = XLSX.read(data, { type: 'array' });
                // The Hours tab (the first sheet of an export from before the
                // Graph tab); the Planned Weekly row under its 'Metric' header,
                // wherever the export put them
                const ws = wb.Sheets.Hours || wb.Sheets[wb.SheetNames[0]];
                const planned = plannedHoursFromSheet(XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }));
                if (!planned) { alert('Could not find a "Planned Weekly Hours" row in the Hours tab.'); return; }
                const updates = planned.map(({ week, value }) => ({
                    userId: '154045', userName: 'Seth Bailey', tab: 'Labor Graphs', fieldName: 'plannedHours',
                    rowId: moment(week, 'M/D/YY', true).startOf('isoWeek').format('YYYY-MM-DD'),
                    version: selectedVersion || '', id: selectedJob || '', lockedVersion, value: value + '',
                }));
                if (updates.length > 0) await updateUserDataArray(updates);
                const fresh = await loadUserData({ id: selectedJob, forecastGuid: selectedVersion });
                setJobUserData(fresh?.data);
                alert(`Imported ${updates.length} planned hours values.`);
                event.target.value = '';
            };
            reader.readAsArrayBuffer(file);
        } catch (err) { console.error('Labor upload error:', err); alert('Error importing file.'); }
    };

    /* ─────────── BUDGET OVERVIEW EXPORT ─────────── */
    const handleBudgetExport = () => {
        const rows = (filteredBudgetView?.rows || []).filter((r) => !isVoltaGrid || (r.label !== 'Total Costs' && r.label !== 'Contingency'));
        const data = filteredBudgetView?.data || {};
        if (!rows.length) { alert('No budget data to export.'); return; }

        const isLEMSO = budgetPivot === 'lemso';
        const getDataKey = (row) => {
            if (row.dataKey) return row.dataKey;
            if (row.label === 'Contract Amount') return 'Contract Amount';
            if (row.label === 'Total Costs') return 'Total Costs';
            if (row.label === 'Contingency') return 'Contingency';
            if (row.costCode) return `costCode_${row.costCode}`;
            if (row.discipline && data[row.discipline]) return row.discipline;
            return null;
        };
        const getRowLabel = (row) => {
            if (isLEMSO && row.lemsoCategory && row.level === 0) return row.label;
            if (isLEMSO && row.lemsoCategory && row.discipline && row.level === 1) return disciplineTitles[row.discipline] || row.discipline;
            if ((row.level === 1 || (isLEMSO && row.level === 2)) && row.costCode) {
                return disciplineTitles[row.costCode] || (row.costCode + (jobPhaseCodes?.[row.costCode] ? ' - ' + jobPhaseCodes[row.costCode] : ''));
            }
            if (row.level === 0 && row.discipline) return disciplineTitles[row.discipline] || row.discipline;
            if (isVoltaGrid && row.label === 'Contract Amount') return 'Revenue (Billable)';
            return row.label || '';
        };

        const exportRows = rows.map(row => {
            const dk = getDataKey(row);
            const rd = dk ? data[dk] : null;
            const indent = '  '.repeat(row.level || 0);
            return {
                'Discipline': indent + getRowLabel(row),
                'Bid Budget': rd?.bidBudget || '',
                'Approved COs': rd?.approvedCOs || '',
                'Current Budget': rd?.currentBudget || '',
                'Actual Costs': rd?.actualCosts || '',
                'Open Commit': rd?.openCommit || '',
                'Total Commit': rd?.totalCommit || '',
                'ETC Labor': rd?.etcLabor || '',
                'ETC Equipment': rd?.etcEquipment || '',
                'ETC Materials': rd?.etcMaterials || '',
                'ETC Rentals': rd?.etcRentals || '',
                'ETC Subs': rd?.etcSubs || '',
                'ETC Other': rd?.etcOther || '',
                'ETC Total': rd?.etc || '',
                'EAC': rd?.eac || '',
                'Δ EAC Budget': rd?.deltaEACBudget || '',
                'Prev Actual': rd?.prevActualCosts || '',
                'Prev Open Commit': rd?.prevOpenCommit || '',
                'Prev ETC Total': rd?.prevEtcTotal || '',
                'Prev EAC': rd?.previousEAC || '',
                'Δ EAC Previous': (row.label === 'Total Costs' && (budgetPivot === 'phase' || budgetPivot === 'lemso') && wipCostVar && !prevCustom) ? wipCostVar : (rd?.deltaEACPrevious || ''),
                'PF': rd?.pf || '',
                'CPI': rd?.cpi || '',
                'SPI': rd?.spi || '',
            };
        });

        // Append Job Profit / JP% and billing rows for completeness
        if (jobProfitData) {
            exportRows.push({ 'Discipline': 'Job Profit', 'Bid Budget': jobProfitData.jpBid || '', 'Approved COs': jobProfitData.jpCO || '', 'Current Budget': jobProfitData.jpBudget || '', 'EAC': jobProfitData.jpEAC || '' });
            exportRows.push({ 'Discipline': 'JP %', 'Bid Budget': jobProfitData.jpPctBid || '', 'Approved COs': jobProfitData.jpPctCO || '', 'Current Budget': jobProfitData.jpPctBudget || '', 'EAC': jobProfitData.jpPctEAC || '' });
        }
        exportRows.push({ 'Discipline': 'Over/(Under) Billed', 'Bid Budget': fNum(overUnderBilled, 1) });
        exportRows.push({ 'Discipline': 'Total Billed', 'Bid Budget': fNum(totalBilled, 1) });

        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        const pivotLabel = budgetPivot === 'phase' ? 'Discipline' : budgetPivot === 'lemso' ? 'LEMSO' : budgetPivot;
        XLSX.utils.book_append_sheet(wb, ws, `Budget Overview (${pivotLabel})`.slice(0, 31));
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([wbout], { type: 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Budget_Overview_${selectedJob}_${moment().format('YYYY-MM-DD')}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    /* ─────────── DOWNLOADS: CO LOG / INVOICES / LOOK AHEAD ─────────── */

    // One place to hand a workbook to the browser. The three downloads below
    // all build their sheet as an array-of-arrays and land here.
    const sheetsToArray = (sheets) => {
        const wb = XLSX.utils.book_new();
        sheets.forEach(([name, aoa]) => XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), name.slice(0, 31)));
        return XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    };
    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
    };
    const downloadSheets = (sheets, filename) => downloadBlob(new Blob([sheetsToArray(sheets)], { type: 'application/octet-stream' }), filename);

    // VoltaGrid title-bar Export: the overview as a PDF (the page rendered to
    // canvas and paginated onto letter sheets) plus the labor graph and the
    // cash flow table as Excel, together in one zip. One file on purpose: the
    // PDF is ready only after the page has been rendered to canvas, well
    // after the click, and Chrome blocks a second download that starts that
    // late as an automatic download - the Excel came through and the PDF
    // never did.
    const handleVoltaGridExport = async () => {
        if (exportBusy) return;
        const root = overviewRootRef.current;
        if (!root) return;
        setExportBusy(true);
        const stamp = moment().format('YYYY-MM-DD');
        const jobTag = selectedJob || 'job';
        try {
            const zip = new JSZip();
            // Excel: the labor graph workbook, as its own Export button gives it
            const labor = await laborWorkbook();
            if (labor) zip.file(`Labor_Graph_${jobTag}_${stamp}.xlsx`, labor);
            else showToast('Labor graph is still loading - no labor Excel in this export', 'info');
            // Excel: cash flow rows from the section (null until its data is in)
            const rows = cashFlowExportRef.current ? cashFlowExportRef.current() : null;
            if (rows) zip.file(`CashFlow_${jobTag}_${stamp}.xlsx`, sheetsToArray([['Cash Flow', rows]]));
            else if (VG_SHOW_CASH_FLOW) showToast('Cash flow is still loading - PDF only', 'info');

            // PDF: screen-only chrome hidden while the page is captured. The
            // banner photo stays, unless it is a custom upload the browser
            // won't let the capture read (blob storage without CORS): then the
            // strip is left out rather than printed blank.
            const banner = root.querySelector('.export-banner');
            const photoReadable = !bannerImage || await new Promise((resolve) => {
                const img = new Image();
                img.crossOrigin = 'anonymous';
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = bannerImage;
            });
            if (banner && !photoReadable) banner.setAttribute('data-export-drop', '');
            // `.export-drop` blocks (the internal review sections) leave the
            // page for the capture, so they take no room in the PDF; the rest
            // of the screen chrome is only hidden, keeping its place.
            const dropped = Array.from(root.querySelectorAll('.export-drop'));
            dropped.forEach((el) => { el.dataset.prevDisp = el.style.display; el.style.display = 'none'; });
            const hidden = Array.from(root.querySelectorAll('.no-print:not(.export-banner):not(.export-drop)'));
            hidden.forEach((el) => { el.dataset.prevVis = el.style.visibility; el.style.visibility = 'hidden'; });
            let canvas;
            let layout = null;
            const capture = fullCaptureOptions(root, (l) => { layout = l; });
            try {
                canvas = await html2canvas(root, { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true, allowTaint: true, ...capture });
            } finally {
                hidden.forEach((el) => { el.style.visibility = el.dataset.prevVis || ''; delete el.dataset.prevVis; });
                dropped.forEach((el) => { el.style.display = el.dataset.prevDisp || ''; delete el.dataset.prevDisp; });
                if (banner) banner.removeAttribute('data-export-drop');
            }
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
            const margin = 24;
            const pageW = pdf.internal.pageSize.getWidth() - margin * 2;
            const pageH = pdf.internal.pageSize.getHeight() - margin * 2;
            // Pages end between rows and cards, not at a fixed height (planPages)
            const scale = canvas.width / capture.width; // canvas px per CSS px
            const cssPageH = pageH * capture.width / pageW;
            const pages = planPages({ atoms: [], cards: [], tables: [], ...layout, height: canvas.height / scale }, cssPageH);
            const ratio = pageW / canvas.width;
            pages.forEach((p, i) => {
                const px = (v) => Math.round(v * scale);
                const headH = p.header ? px(p.header[1]) - px(p.header[0]) : 0;
                const bodyH = px(p.bottom) - px(p.top);
                const slice = document.createElement('canvas');
                slice.width = canvas.width; slice.height = headH + bodyH;
                const ctx = slice.getContext('2d');
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, slice.width, slice.height);
                // A table that runs on from the page before gets its header again
                if (headH) ctx.drawImage(canvas, 0, px(p.header[0]), canvas.width, headH, 0, 0, canvas.width, headH);
                ctx.drawImage(canvas, 0, px(p.top), canvas.width, bodyH, 0, headH, canvas.width, bodyH);
                if (i > 0) pdf.addPage();
                pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', margin, margin, pageW, slice.height * ratio);
                if (pages.length > 1) {
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(8);
                    pdf.setTextColor(115, 115, 115);
                    pdf.text(`Page ${i + 1} of ${pages.length}`, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - margin / 2, { align: 'center', baseline: 'middle' });
                }
            });
            zip.file(`Overview_${jobTag}_${stamp}.pdf`, pdf.output('arraybuffer'));
            downloadBlob(await zip.generateAsync({ type: 'blob' }), `Overview_${jobTag}_${stamp}.zip`);
        } catch (err) {
            console.error('overview export', err);
            showToast('Export failed - see console', 'error');
        } finally {
            setExportBusy(false);
        }
    };

    // The CO log for the version on screen. jobUserData holds every version's
    // rows, so the fold has to be scoped — without the version filter this
    // returns the same CO number several times over, once per weekly copy.
    const handleCOLogExport = () => {
        const logs = buildCOLogRows(jobUserData, { job: selectedJob, version: selectedVersion });
        if (!logs.length) {
            showToast('No change orders on the CO log for this version', 'info');
            return;
        }
        downloadSheets(
            [['CO Log', [...coLogToSheetRows(logs), coLogTotalsRow(logs)]]],
            `CO_Log_${selectedJob}_${moment().format('YYYY-MM-DD')}.xlsx`,
        );
    };

    const [invoiceBusy, setInvoiceBusy] = useState(false);
    // Collapsed on load: the look ahead is a planning aid, not part of the
    // one-page report, and it only prints when someone opens it on purpose.
    const [lookAheadOpen, setLookAheadOpen] = useState(false);

    // Billing rows are job-level (BILLING_VERSION), loaded by the Billing tab
    // rather than with the rest of the job. Fetch them on the first download
    // and leave them on the shared atom so opening Billing afterwards doesn't
    // load them again.
    const handleInvoiceExport = async () => {
        if (invoiceBusy) return;
        setInvoiceBusy(true);
        try {
            let rows = billingData && billingData.job === selectedJob && Array.isArray(billingData.rows)
                ? billingData.rows
                : null;
            if (!rows) {
                const res = await loadUserData({ id: selectedJob, forecastGuid: BILLING_VERSION, live: true });
                if (!res || res.error) throw new Error(res?.error || 'no response');
                // Same shape the Billing tab puts on the atom — rows already
                // narrowed to the Billing tab — so whichever view loads first,
                // the other reads it instead of fetching again.
                rows = (Array.isArray(res.data) ? res.data : []).filter((x) => x?.Forecast_Tab === 'Billing');
                setBillingData({ job: selectedJob, rows, error: null });
            }
            const list = invoiceListFrom(rows);
            if (!list.length) {
                showToast('No billing summaries on this job yet', 'info');
                return;
            }
            downloadSheets(
                [['Invoices', invoiceListToSheetRows(list)]],
                `Invoices_${selectedJob}_${moment().format('YYYY-MM-DD')}.xlsx`,
            );
        } catch (e) {
            console.error('Invoice download failed', e);
            showToast('Could not load billing summaries — try the Billing tab', 'error');
        } finally {
            setInvoiceBusy(false);
        }
    };

    /* ─────────── 2 WEEK LOOK AHEAD ─────────── */
    // Crew count per phase for the report week and the one after it, read off
    // the same Burn Tool Labor Phased rows the Manpower Planning view writes
    // (Forecast_Row_ID is `<phase>=<ISO week Monday>`, Number_Units is the
    // crew). The anchor is the version's start date, not today: a report
    // printed later in the week still has to show the week it was cut for.
    const lookAhead = useMemo(() => {
        const anchor = versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime) : moment();
        const w0 = anchor.clone().startOf('isoWeek').format('YYYY-MM-DD');
        const w1 = moment(w0).add(1, 'week').format('YYYY-MM-DD');
        const byPhase = {};
        (jobUserData || []).forEach((d) => {
            if (d?.Forecast_Tab !== 'Burn Tool Labor Phased') return;
            if (String(d.Version_GUID) !== String(selectedVersion)) return;
            const id = String(d.Forecast_Row_ID || '');
            if (!id.includes('=')) return;
            const week = id.split('=')[1];
            if (week !== w0 && week !== w1) return;
            // Cost_Code on the row when it's real, otherwise the code baked
            // into the key — same fallback the burn-tool roll-up uses.
            const phase = (d.Cost_Code && d.Cost_Code !== 'undefined') ? String(d.Cost_Code) : id.split('=')[0];
            if (!phase) return;
            const crew = pNum(d.Number_Units);
            if (!byPhase[phase]) byPhase[phase] = { phase, c0: 0, c1: 0 };
            if (week === w0) byPhase[phase].c0 += crew;
            else byPhase[phase].c1 += crew;
        });
        const round2 = (n) => Math.round(n * 100) / 100;
        const rows = Object.values(byPhase)
            .map((r) => ({ ...r, c0: round2(r.c0), c1: round2(r.c1), description: jobPhaseCodes?.[r.phase] || '' }))
            .filter((r) => r.c0 || r.c1)
            .sort((a, b) => (b.c0 + b.c1) - (a.c0 + a.c1) || String(a.phase).localeCompare(String(b.phase)));
        return {
            weeks: [w0, w1],
            rows,
            tot0: round2(rows.reduce((s, r) => s + r.c0, 0)),
            tot1: round2(rows.reduce((s, r) => s + r.c1, 0)),
        };
    }, [jobUserData, selectedVersion, versionInfo, jobPhaseCodes]);

    const handleLookAheadExport = () => {
        if (!lookAhead.rows.length) {
            showToast('No staffed phases in the next two weeks', 'info');
            return;
        }
        const label = (w) => moment(w).format('M/D/YY');
        const aoa = [
            ['Phase', 'Description', `Week of ${label(lookAhead.weeks[0])}`, `Week of ${label(lookAhead.weeks[1])}`],
            ...lookAhead.rows.map((r) => [r.phase, r.description, r.c0 || '', r.c1 || '']),
            ['', 'Total crew', lookAhead.tot0, lookAhead.tot1],
        ];
        downloadSheets(
            [['2 Week Look Ahead', aoa]],
            `Look_Ahead_${selectedJob}_${moment().format('YYYY-MM-DD')}.xlsx`,
        );
    };

    /* ─────────── SAFETY METRIC HANDLERS ─────────── */
    const handleSafetyMetricSave = (label, value) => {
        const numVal = parseInt(value) || 0;

        // A prior (locked) version drops the write with a toast; keep the cell
        // showing the stored number rather than one that was never saved.
        if (lockedVersion) {
            showToast('Current version is locked for editing', 'warn');
            setEditingSafetyKey(null);
            return;
        }

        switch (label) {
            case 'First Aid':
                setSafetyFirstAid(numVal);
                save('SafetyFirstAid', numVal.toString());
                break;
            case 'Incidents':
                setSafetyIncidents(numVal);
                save('SafetyIncidents', numVal.toString());
                break;
            case 'Near Miss':
                setSafetyNearMiss(numVal);
                save('SafetyNearMiss', numVal.toString());
                break;
            case 'Stop Work':
                setSafetyStopWork(numVal);
                save('SafetyStopWork', numVal.toString());
                break;
            case 'Recordables':
                setSafetyQAQC(numVal);
                save('SafetyQAQC', numVal.toString());
                break;
        }

        setEditingSafetyKey(null);
    };

    /* ─────────── STATUS CALCULATIONS ─────────── */
    const newDate = new Date();
    // Compute pfScore from live budget data (same earned/actual hours used in the Budget Overview table)
    const pfScore = useMemo(() => {
        const pfDebug = budgetData?.['Total Costs']?._pfDebug;
        if (!pfDebug) return 0;
        // Match budget overview: if % complete < 25%, PF defaults to 1
        if (pfDebug.budgetHrs > 0 && pfDebug.pctComplete < 25) return 1;
        if (pfDebug.actualHrs > 0) {
            return parseFloat((pfDebug.earnedHrs / pfDebug.actualHrs).toFixed(4));
        }
        return 0;
    }, [budgetData]);

    // Compute billing totals from actual data
    const totalBilled = useMemo(() => {
        if (!jobBillings || !Array.isArray(jobBillings)) return 0;
        return jobBillings.reduce((sum, item) => sum + (pNum(item.Invoice_Amount) || 0), 0);
    }, [jobBillings]);
    const totalRevenue = useMemo(() => {
        // VoltaGrid: revenue EAC off the Revenue (Billable) row — billable to
        // date plus the cost ETC at the job's bill-to-cost ratio.
        if (isVoltaGrid) return pNum(budgetData?.['Contract Amount']?._revenue?.eac || 0);
        return (smBudgets.contractValuesRevised || 0) + (smBudgets.changeOrderRevenue || 0);
    }, [smBudgets, isVoltaGrid, budgetData]);
    // Est Earned Rev = Contract EAC × (JTD Actual Costs / Total Cost EAC)  (matches bidForecast.js)
    const earnedRevenue = useMemo(() => {
        // VoltaGrid: no contract to prorate — what is earned to date IS the
        // billable amount, so Over/(Under) Billed reads billable work not
        // yet invoiced.
        if (isVoltaGrid) return pNum(budgetData?.['Contract Amount']?._revenue?.jtd || 0);
        const costEAC = pNum(budgetData?.['Total Costs']?.eac || 0);
        const jtdActual = pNum(budgetData?.['Total Costs']?.actualCosts || 0);
        if (!costEAC || !totalRevenue) return 0;
        return totalRevenue * jtdActual / costEAC;
    }, [budgetData, totalRevenue, isVoltaGrid]);
    const overUnderBilled = totalBilled - earnedRevenue;
    const overUnder = totalRevenue > 0 ? (-overUnderBilled / totalRevenue) : 0.12;

    // Total actual labor hours from jobCostDetail (cost type 51000-51799)
    const totalActualLaborHours = useMemo(() => {
        if (!jobCostDetail || !Array.isArray(jobCostDetail)) return 0;
        return jobCostDetail.reduce((sum, item) => {
            if (item.Cost_Type_Code >= '51000' && item.Cost_Type_Code <= '51799') {
                return sum + (parseFloat(item.Total_Hours) || 0);
            }
            return sum;
        }, 0);
    }, [jobCostDetail]);

    // ─── LABOR CHART (inline, computed once from existing atoms) ───
    const laborChartConfig = useMemo(() => {
        if (!jobCostDetailRaw || !selectedJobDetails || !projectComplete) return null;

        // 1. Build project weeks
        let plannedStart = moment(selectedJobDetails?.Planned_Start_Date);
        jobCostDetailRaw.forEach(row => {
            if (row.Cost_Type_Code >= '51000' && row.Cost_Type_Code <= '51799' && row.Job_Cost > 0) {
                const d = moment(row.Consolidated_Date);
                if (d.isBefore(plannedStart)) plannedStart = d;
            }
        });
        const plannedFinish = moment(projectComplete);
        const days = plannedFinish.diff(plannedStart, 'days');
        if (days <= 0) return null;
        const uniqueWeeks = {};
        for (let i = 0; i < days; i++) {
            const wk = plannedStart.clone().add(i, 'days').startOf('isoWeek').format('YYYY-MM-DD');
            uniqueWeeks[wk] = 1;
        }
        const weeks = Object.keys(uniqueWeeks);
        if (weeks.length === 0) return null;

        // 2. Aggregate actual weekly hours from raw jobCostDetail (matches laborGraphs.js)
        const weeksHours = {};
        const weeksHoursBy = {};   // week -> { equipment, electrical, other } (VoltaGrid craft split)
        jobCostDetailRaw.forEach(row => {
            if (row.Cost_Type_Code >= '51000' && row.Cost_Type_Code <= '51799') {
                const wk = moment(row.Consolidated_Date).startOf('isoWeek').format('YYYY-MM-DD');
                const hrs = pNum(row.Total_Hours);
                weeksHours[wk] = (weeksHours[wk] || 0) + hrs;
                if (!weeksHoursBy[wk]) weeksHoursBy[wk] = emptyCrafts();
                weeksHoursBy[wk][vgCraftOf(row.Phase_Code || row.Cost_Code)] += hrs;
            }
        });

        // 3. Parse planned hours + forecasted hours from jobUserData
        const laborGraphData = {};
        const burnToolData = {};
        const dayZeroData = {};
        const coData = {};
        if (jobUserData) {
            jobUserData.forEach(d => {
                if (d?.Forecast_Tab === 'Labor Graphs') {
                    if (!laborGraphData[d.Forecast_Row_ID]) laborGraphData[d.Forecast_Row_ID] = {};
                    laborGraphData[d.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value;
                }
                if (d?.Forecast_Tab === 'Burn Tool Labor' || d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                    burnToolData[d.Forecast_Row_ID] = d;
                }
                if (d?.Forecast_Tab === 'Day Zero Budget') {
                    dayZeroData[d.Forecast_Row_ID] = d;
                }
                if (d?.Forecast_Tab === 'Change Order') {
                    coData[d.Forecast_User_Data_GUID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label };
                }
            });
        }

        // 4. Build forecasted weekly hours from burn tool
        const forecastedWeekHours = {};
        const forecastedWeekHoursBy = {};
        const verStart = versionInfo?.Version_Start_DateTime;
        Object.keys(burnToolData).forEach(row => {
            const isPhasedKey = row.includes('=');
            const week = isPhasedKey ? row.split('=')[1] : row.split('*')[0];
            const bt = burnToolData[row];
            const h = pNum(bt?.Number_Hours || 0);
            const cS = pNum(bt?.Number_Units || 0);
            const bR = pNum(bt?.Rate || 0);
            const withinProject = moment(week).isBefore(moment(projectComplete).endOf('week'));
            if (week && h && bR && cS && withinProject) {
                forecastedWeekHours[week] = (forecastedWeekHours[week] || 0) + h * cS;
                // Phase: the row's Cost_Code when real, else the code baked into the key.
                const phase = (bt?.Cost_Code && bt.Cost_Code !== 'undefined') ? String(bt.Cost_Code) : (isPhasedKey ? row.split('=')[0] : '');
                if (!forecastedWeekHoursBy[week]) forecastedWeekHoursBy[week] = emptyCrafts();
                forecastedWeekHoursBy[week][vgCraftOf(phase)] += h * cS;
            }
        });

        // 5. Compute cumulative values
        let cumActual = 0, cumPlanned = 0, cumCurrentPlanned = 0;
        const actualCum = [], plannedCum = [], currentPlannedCum = [], forecastCum = [];
        let lastActualCum = 0;
        let forecastStarted = false;
        let forecastStart = -1;   // index of the first forecast week
        weeks.forEach((week, wi) => {
            const ph = laborGraphData[week]?.plannedHours ? pNum(laborGraphData[week].plannedHours) : 0;
            const coH = laborGraphData[week]?.coHours ? pNum(laborGraphData[week].coHours) : 0;
            cumActual += (weeksHours[week] || 0);
            cumPlanned += ph;
            cumCurrentPlanned += ph + coH;

            // Forecast cumulative: actual up to version start, then actual + forecast after
            if (verStart && moment(week).isAfter(moment(verStart).startOf('week'))) {
                if (!forecastStarted) { forecastStarted = true; forecastStart = wi; }
                lastActualCum += (forecastedWeekHours[week] || 0);
            } else {
                lastActualCum = cumActual;
            }

            // Actual cumulative: show null after forecast line starts so orange line stops
            actualCum.push(forecastStarted ? null : cumActual);
            plannedCum.push(cumPlanned);
            currentPlannedCum.push(cumCurrentPlanned);
            forecastCum.push(lastActualCum);
        });
        // VoltaGrid: the forecast cumulative carries no history. The table
        // leaves the weeks before the version's week blank; the graph keeps
        // the last actual week so the dashed line starts where the orange
        // one ends.
        const historyEnd = forecastStart < 0 ? weeks.length : forecastStart;
        const forecastCumTable = isVoltaGrid ? forecastCum.map((v, i) => (i < historyEnd ? null : v)) : forecastCum;
        const forecastCumLine = isVoltaGrid ? forecastCum.map((v, i) => (i < historyEnd - 1 || forecastStart < 0 ? null : v)) : forecastCum;

        const labels = weeks.map((w) => moment(w).format('M/D/YY'));
        const maxWeekly = Math.max(...weeks.map(w => Math.max(weeksHours[w] || 0, laborGraphData[w]?.plannedHours ? pNum(laborGraphData[w].plannedHours) : 0, forecastedWeekHours[w] || 0)), 1);
        const maxCum = Math.max(...[...actualCum, ...plannedCum, ...currentPlannedCum, ...forecastCum], 1);

        // Build table rows for display below chart. FTE = weekly hours / 60
        // (a VoltaGrid man works a 60-hour week), shown on the VoltaGrid tab.
        const tableRows = weeks.map((w, i) => ({
            label: moment(w).format('M/D/YY'),
            actual: weeksHours[w] || 0,
            planned: laborGraphData[w]?.plannedHours ? pNum(laborGraphData[w].plannedHours) : 0,
            forecasted: forecastedWeekHours[w] || 0,
            actualBy: weeksHoursBy[w] || emptyCrafts(),
            forecastedBy: forecastedWeekHoursBy[w] || emptyCrafts(),
            actualFte: (weeksHours[w] || 0) / FTE_HOURS_PER_WEEK,
            plannedFte: (laborGraphData[w]?.plannedHours ? pNum(laborGraphData[w].plannedHours) : 0) / FTE_HOURS_PER_WEEK,
            forecastedFte: (forecastedWeekHours[w] || 0) / FTE_HOURS_PER_WEEK,
            cumActual: actualCum[i],
            cumPlanned: plannedCum[i],
            cumCurrentPlanned: currentPlannedCum[i],
            cumForecast: forecastCumTable[i],
        }));

        return {
            tableRows,
            weeks,
            data: {
                labels,
                datasets: [
                    { label: 'Planned Weekly', data: weeks.map(w => laborGraphData[w]?.plannedHours ? pNum(laborGraphData[w].plannedHours) : 0), backgroundColor: '#A5A5A5', yAxisID: 'y', order: 3, barPercentage: 0.85, categoryPercentage: 0.85, stack: 'planned' },
                    // VoltaGrid: the forecast and actual bars stack by craft
                    // (Equipment / Electrical / Other); planned hours are only
                    // entered as a weekly total, so that bar stays whole.
                    ...(isVoltaGrid
                        ? VG_CRAFTS.map(c => ({ label: `Forecasted ${c.label}`, data: weeks.map(w => forecastedWeekHoursBy[w]?.[c.key] || 0), backgroundColor: c.forecast, yAxisID: 'y', order: 3, barPercentage: 0.85, categoryPercentage: 0.85, stack: 'forecast' }))
                        : [{ label: 'Forecasted Weekly', data: weeks.map(w => forecastedWeekHours[w] || 0), backgroundColor: '#7CAFDD', yAxisID: 'y', order: 3, barPercentage: 0.85, categoryPercentage: 0.85, stack: 'forecast' }]),
                    ...(isVoltaGrid
                        ? VG_CRAFTS.map(c => ({ label: `Actual ${c.label}`, data: weeks.map(w => weeksHoursBy[w]?.[c.key] || 0), backgroundColor: c.actual, yAxisID: 'y', order: 3, barPercentage: 0.85, categoryPercentage: 0.85, stack: 'actual' }))
                        : [{ label: 'Actual Weekly', data: weeks.map(w => weeksHours[w] || 0), backgroundColor: '#F4B183', yAxisID: 'y', order: 3, barPercentage: 0.85, categoryPercentage: 0.85, stack: 'actual' }]),
                    { label: 'Planned Cumulative', data: currentPlannedCum, borderColor: '#597097', borderWidth: 2, fill: false, tension: 0, yAxisID: 'y1', type: 'line', order: 1, pointRadius: 0 },
                    { label: 'Original Cumulative', data: plannedCum, borderColor: '#b4b4b4', borderWidth: 2, fill: false, tension: 0, yAxisID: 'y1', type: 'line', order: 1, pointRadius: 0 },
                    { label: 'Actual Cumulative', data: actualCum, borderColor: '#f4b183', borderWidth: 2, fill: false, tension: 0, yAxisID: 'y1', type: 'line', order: 1, pointRadius: 0, spanGaps: false },
                    { label: 'Forecast Cumulative', data: forecastCumLine, borderColor: '#7CAFDD', borderDash: [8, 4], borderWidth: 2, fill: false, tension: 0, yAxisID: 'y1', type: 'line', order: 1, pointRadius: 0 },
                ]
            },
            options: {
                responsive: true, maintainAspectRatio: false,
                devicePixelRatio: 3,
                interaction: { mode: 'index', intersect: false },
                scales: {
                    x: { stacked: true, grid: { display: false }, ticks: { display: false } },
                    y: { type: 'linear', position: 'left', stacked: true, beginAtZero: true, max: maxWeekly + 200, afterFit: (scale) => { scale.width = 60; }, title: { display: true, text: 'Weekly Hrs', font: { size: 10, weight: 600 }, color: palette.muted }, ticks: { font: { size: 9 }, color: palette.muted }, grid: { color: '#f1f5f9' } },
                    y1: { type: 'linear', position: 'right', beginAtZero: true, suggestedMax: maxCum + 2000, afterFit: (scale) => { scale.width = 60; }, title: { display: true, text: 'Cumulative Hrs', font: { size: 10, weight: 600 }, color: palette.muted }, ticks: { font: { size: 9 }, color: palette.muted }, grid: { display: false } }
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        backgroundColor: '#fff', titleColor: palette.text, bodyColor: palette.text, borderColor: palette.border, borderWidth: 1, padding: 10, cornerRadius: 6,
                        filter: isVoltaGrid ? (item) => !(item.dataset.stack && item.dataset.stack !== 'planned' && !(item.parsed.y > 0)) : undefined,
                        callbacks: isVoltaGrid ? {
                            label: (item) => {
                                const base = `${item.dataset.label}: ${fNum(item.parsed.y || 0, 0)}`;
                                return item.dataset.yAxisID === 'y' ? `${base}  (${fNum((item.parsed.y || 0) / FTE_HOURS_PER_WEEK, 1)} FTE)` : base;
                            },
                            footer: (items) => {
                                const i = items[0]?.dataIndex;
                                if (i == null) return '';
                                const tr = tableRows[i];
                                const line = (lbl, v) => v ? `${lbl}: ${fNum(v, 0)}  (${fNum(v / FTE_HOURS_PER_WEEK, 1)} FTE)` : null;
                                return [line('Forecast total', tr.forecasted), line('Actual total', tr.actual)].filter(Boolean);
                            },
                        } : {},
                    }
                }
            }
        };
    }, [jobCostDetailRaw, selectedJobDetails, projectComplete, jobUserData, versionInfo, isVoltaGrid]);

    const statusColor = (level) =>
        level === 'red' ? palette.red : level === 'orange' ? palette.orange : palette.green;

    const statusBg = (level) =>
        level === 'red' ? palette.redLight : level === 'orange' ? palette.orangeLight : palette.greenLight;

    const calcBillingStatus = () => overUnder >= 0.15 ? 'red' : overUnder >= 0.05 ? 'orange' : 'green';
    const calcBudgetStatus = () => { const v = (jobProfitData?._jpPctEAC || 0) - (jobProfitData?._jpPctBud || 0); return v < -8 ? 'red' : v < -3 ? 'orange' : 'green'; };
    const calcProductivityStatus = () => pfScore < 0.85 ? 'red' : pfScore < 0.95 ? 'orange' : 'green';
    const calcScheduleStatus = (spiValue) => {
        const spi = typeof spiValue === 'number' ? spiValue : pNum(spiValue || 0);
        if (spi > 0 && spi < 0.85) return 'red';
        if (spi >= 0.85 && spi < 0.95) return 'orange';
        return 'green';
    };
    const calcSafetyStatus = () => {
        // Recordables: 0 = green, ≥1 = red
        if (safetyQAQC > 0) return 'red';
        // First Aid: 0 = green, ≥1 = yellow (orange)
        if (safetyFirstAid > 0) return 'orange';
        // Near Miss & Stop Work: both 0 = yellow (orange), either >0 = green
        if (safetyNearMiss === 0 && safetyStopWork === 0) return 'orange';
        return 'green';
    };
    // statuses computed after jobProfitData — see below

    /* ═══════════════ BUDGET COMPUTATION (reused from overview.js) ═══════════════ */
    const budgetRows = useMemo(() => {
        if (!jobUserData) return [{ label: "Contract Amount", level: 0 }];
        const rows = [{ label: "Contract Amount", level: 0 }];
        const { mapping: disciplineMapping, subgroup: vgSubgroup, order: vgSubgroupOrder } = trackerDisciplines(jobUserData, isVoltaGrid);
        const allCostCodes = new Set();
        jobUserData.filter(item => item.Forecast_Tab === 'Day Zero Budget' && item.Cost_Code && item.Version_GUID === selectedVersion).forEach(item => {
            if (item.Cost_Code && !item.Cost_Type?.toString().startsWith('4') && item.Cost_Code !== '1000' && item.Cost_Code !== '990000') allCostCodes.add(normCC(item.Cost_Code));
        });
        jobUserData.filter(item => item.Forecast_Tab === 'Change Order' && item.Version_GUID === selectedVersion).forEach(item => {
            if (!item.Cost_Type?.toString().startsWith('4')) {
                if (item.Cost_Code && item.Cost_Code !== '1000' && item.Cost_Code !== '990000') {
                    allCostCodes.add(normCC(item.Cost_Code));
                } else if (!item.Cost_Code || item.Cost_Code === '' || item.Cost_Code === '1000' || item.Cost_Code === '990000') {
                    // Approved CO rows with no phase code → Project Team
                    const status = (item.Status || item.status || '').toLowerCase();
                    if (status === 'approved' && item.Cost_Type?.toString()[0] === '5') allCostCodes.add('100101');
                }
            }
        });
        jobUserData.filter(item => ['Materials', 'Rentals', 'Subcontractors', 'Other', 'Materials-phased', 'Rentals-phased', 'Subcontractors-phased', 'Other-phased', 'Burn Tool Labor', 'Burn Tool Equipment', 'Burn Tool Labor Phased', 'Burn Tool Equipment Phased'].includes(item.Forecast_Tab) && item.Version_GUID === selectedVersion).forEach(item => {
            const cc = item.Cost_Code && item.Cost_Code !== '1000' && item.Cost_Code !== '990000' ? item.Cost_Code : '100101';
            if (!item.Cost_Type?.toString().startsWith('4')) allCostCodes.add(normCC(cc));
        });
        if (Array.isArray(jobCostDetail)) jobCostDetail.forEach(item => { if (item.Phase_Code && !item.Cost_Type?.toString().startsWith('4') && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') allCostCodes.add(normCC(item.Phase_Code)); });
        if (Array.isArray(jobOpenCommitments)) jobOpenCommitments.forEach(item => { if (item.Phase_Code && !item.Cost_Type?.toString().startsWith('4') && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') allCostCodes.add(normCC(item.Phase_Code)); });
        allCostCodes.forEach(cc => { if (!disciplineMapping[cc]) disciplineMapping[cc] = vgDiscipline(cc, isVoltaGrid) || (cc === '100101' ? 'Project Team' : 'Other'); });
        const disciplineGroups = {};
        Object.keys(disciplineMapping).forEach(cc => { if (cc === '101101') return; const d = disciplineMapping[cc]; if (d !== 'Contingency') { if (!disciplineGroups[d]) disciplineGroups[d] = []; disciplineGroups[d].push({ code: cc }); } });
        const ccLabel = cc => cc;
        // VoltaGrid: disciplines in the bid's section order (Indirects, Piping,
        // Coating, Equipment, Electrical …); a discipline the job named itself
        // right after the section its codes roll up to (AG Electrical Conduit
        // after Electrical); anything else after.
        const vgOrder = (d) => voltaGridDisciplineRank(d, disciplineGroups[d].map((c) => c.code));
        const byOrder = isVoltaGrid ? (a, b) => vgOrder(a) - vgOrder(b) || a.localeCompare(b) : (a, b) => a.localeCompare(b);
        // VoltaGrid children: only Equipment and Electrical open, one level, to
        // the bid's WBS groups (the QPAC installs, Cable Tray, Conduit …) rather
        // than cost codes; a discipline the job named itself and folded in
        // (AG Electrical Conduit) is one row under its own name. A group row
        // carries the codes it rolls up; its figures are built in
        // budgetDataForTab under `dataKey`.
        const vgChildren = (d) => {
            if (!isVoltaGrid) return null;
            if (!/^(equipment|electrical)$/i.test(String(d))) return [];
            const groups = {};
            disciplineGroups[d].forEach((cc) => {
                const own = vgSubgroup[cc.code];
                const { group: g, order } = own ? { group: own, order: vgSubgroupOrder[own] } : voltaGridGroupFor(cc.code, d);
                (groups[g] || (groups[g] = { group: g, order, codes: [] })).codes.push(cc.code);
            });
            return Object.values(groups).sort((a, b) => a.order - b.order || a.group.localeCompare(b.group)).map((g) => ({
                label: g.group.replace(/^installation\s+qpac\s+/i, 'QPAC '), level: 1, discipline: d, dataKey: `vgGroup_${d}|${g.group}`, groupCodes: g.codes,
            }));
        };
        const pushChildren = (d) => {
            const kids = vgChildren(d);
            if (kids) kids.forEach((r) => rows.push(r));
            else disciplineGroups[d].forEach(cc => rows.push({ label: ccLabel(cc.code), level: 1, costCode: cc.code, discipline: d }));
        };
        // The discipline row carries its codes: on VoltaGrid most disciplines
        // have no code rows under them, and the revenue basis still needs them.
        Object.keys(disciplineGroups).sort(byOrder).forEach(d => { if (d !== 'Other') { rows.push({ label: d, level: 0, discipline: d, codes: disciplineGroups[d].map((c) => c.code) }); pushChildren(d); } });
        // VoltaGrid: no Other row (codes outside the bid sections still land
        // in Total Costs) and no Fee at Risk row.
        if (disciplineGroups['Other'] && !isVoltaGrid) { rows.push({ label: 'Other', level: 0, discipline: 'Other' }); pushChildren('Other'); }
        rows.push({ label: "Contingency", level: 0 });
        rows.push({ label: "Total Costs", level: 0 });
        return rows;
    }, [jobUserData, jobCostDetail, jobOpenCommitments, selectedVersion, isVoltaGrid]);

    // Collapse all discipline sections on first load
    React.useEffect(() => {
        if (budgetRows.length > 1 && !disciplinesInitRef.current) {
            disciplinesInitRef.current = true;
            const init = {};
            budgetRows.filter(r => r.level === 0 && r.discipline).forEach(r => { init[r.discipline] = true; });
            setCollapsedDisciplines(init);
        }
    }, [budgetRows]);

    // LEMSO budget rows — alternative grouping by cost type category
    const budgetRowsLEMSO = useMemo(() => {
        if (!budgetData?._lemsoCodes) return [];
        const lemsoLabels = { L: 'Labor', E: 'Equipment', M: 'Materials', S: 'Subcontractors', O: 'Other' };
        const catOrder = ['L', 'E', 'M', 'S', 'O'];
        const filter = budgetPivot.length === 1 && catOrder.includes(budgetPivot) ? budgetPivot : null;
        const rows = [{ label: 'Contract Amount', level: 0 }];
        catOrder.forEach(key => {
            if (filter && filter !== key) return;
            const codes = budgetData._lemsoCodes[key] || [];
            rows.push({ label: lemsoLabels[key], level: 0, lemsoKey: key });
            codes.forEach(cc => rows.push({ label: cc, level: 1, costCode: cc, lemsoKey: key }));
        });
        rows.push({ label: 'Contingency', level: 0 });
        rows.push({ label: 'Total Costs', level: 0 });
        return rows;
    }, [budgetData, budgetPivot]);

    // Collapse all LEMSO categories by default on first load
    const lemsoInitRef = React.useRef(false);
    React.useEffect(() => {
        if (budgetRowsLEMSO.length > 1 && !lemsoInitRef.current) {
            lemsoInitRef.current = true;
            const init = {};
            budgetRowsLEMSO.filter(r => r.level === 0 && r.lemsoKey).forEach(r => { init[r.lemsoKey] = true; });
            setCollapsedLemso(init);
        }
    }, [budgetRowsLEMSO]);

    // Full budget calculation (same logic as overview.js — condensed)
    const calculatedBudgetData = useMemo(() => {
        if (!jobUserData || !jobCostDetail || !jobOpenCommitments) return {};
        const result = {};
        const disciplineMapping = {};
        const costCodeData = {};
        const costCodeQuantities = {};
        const previousVersionData = {};
        const previousActualCostsByCode = {};
        const previousCommitmentsByCode = {};
        const chOrValues = {};
        const dayZeroValues = {};
        let completionDates = {};
        const phasedEquipment = {};
        const phasedLabor = {};
        const laborValues = {};
        const equipmentValues = {};
        const contingData = {};
        // Hours tracking for PF = Earned Hours / Actual Hours
        const actualHoursByCode = {};
        const installedQtyByCode = {};
        const plugQtyByCode = {};
        const dayZeroHrsByCode = {};
        // Labor dollar tracking for CPI = earned labor $ / actual labor $
        const laborDzDollarsByCode = {};
        const laborCoDollarsByCode = {};
        const laborActualDollarsByCode = {};

        // LEMSO cost-type tracking: split actuals, commitments, DZ, COs, ETC by cost type × cost code
        const getCostTypeCategory = (ctc) => { if (!ctc) return 'O'; const s = ctc.toString(); if (s.startsWith('51')) return 'L'; if (s.startsWith('52')) return 'E'; if (s.startsWith('53')) return 'M'; if (s.startsWith('54')) return 'S'; return 'O'; };
        const actualByTypeAndCode = { L: {}, E: {}, M: {}, S: {}, O: {} };
        const commitByTypeAndCode = { L: {}, E: {}, M: {}, S: {}, O: {} };
        const dzByTypeAndCode = { L: {}, E: {}, M: {}, S: {}, O: {} };
        const coByTypeAndCode = { L: {}, E: {}, M: {}, S: {}, O: {} };
        const etcByTypeAndCode = { L: {}, E: {}, M: {}, S: {}, O: {} };
        // Previous-period LEMSO tracking for Δ EAC Previous per cost type
        const previousActualByTypeAndCode = { L: {}, E: {}, M: {}, S: {}, O: {} };
        const previousCommitByTypeAndCode = { L: {}, E: {}, M: {}, S: {}, O: {} };
        const previousEtcByTypeAndCode = { L: {}, E: {}, M: {}, S: {}, O: {} };

        // Load discipline mapping from Productivity Tracker (all versions, matching budgetRows)
        // so that discipline assignments are consistent between the row structure and the data aggregation
        Object.assign(disciplineMapping, trackerDisciplines(jobUserData, isVoltaGrid).mapping);

        const groupedData = jobUserData.reduce((acc, item) => {
            const key = `${item.Version_GUID}_${item.BU_ID}_${item.Forecast_Tab}_${item.Forecast_Field_Name}_${item.Forecast_Row_ID}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(item);
            return acc;
        }, {});
        const deduplicatedData = Object.values(groupedData).map(group => group.reduce((latest, current) => {
            const ld = new Date(Math.max(new Date(latest.DSS_Created_Date), new Date(latest.DSS_Updated_Datetime || 0)));
            const cd = new Date(Math.max(new Date(current.DSS_Created_Date), new Date(current.DSS_Updated_Datetime || 0)));
            return cd > ld ? current : latest;
        }));

        deduplicatedData.forEach(d => {
            if (d.Version_GUID == selectedVersion) {
                if (d.Forecast_Tab === 'Change Order') chOrValues[d.Forecast_User_Data_GUID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label };
                if (d.Forecast_Tab === 'Day Zero Budget') {
                    dayZeroValues[d.Forecast_Row_ID] = d;
                    if (d.Cost_Code && d.Cost_Code !== '1000' && d.Cost_Code !== '990000') {
                        const isRev = d.Cost_Type?.toString().startsWith('4');
                        const key = isRev ? 'CONTRACT_AMOUNT' : normCC(d.Cost_Code);
                        if (!isRev) dayZeroHrsByCode[key] = (dayZeroHrsByCode[key] || 0) + (parseFloat(d.Hours) || 0);
                        if (!costCodeQuantities[key]) costCodeQuantities[key] = 0;
                        costCodeQuantities[key] += pNum(d.Quantity || 0);
                        // Track labor DZ dollars (51xxx) for CPI
                        if (!isRev && d.Cost_Type?.toString().startsWith('51')) {
                            laborDzDollarsByCode[key] = (laborDzDollarsByCode[key] || 0) + pNum(d.Value_Amount || 0);
                        }
                    }
                }
                if (d.Forecast_Tab === 'All' || d.Forecast_Tab === 'Forecast') {
                    if (d.Forecast_Field_Name === 'completionDateForecast') completionDates = d.Forecast_Value ? moment(d.Forecast_Value).format('M/D/YYYY') : '';
                }
                if (d.Forecast_Tab === 'Burn Tool Labor') laborValues[d.Forecast_Row_ID] = d;
                if (d.Forecast_Tab === 'Burn Tool Equipment') equipmentValues[d.Forecast_Row_ID] = d;
                if (d.Forecast_Tab === 'Burn Tool Equipment Phased') phasedEquipment[d.Forecast_Row_ID] = { id: d.Forecast_Row_ID, ...d };
                if (d.Forecast_Tab === 'Burn Tool Labor Phased') phasedLabor[d.Forecast_Row_ID] = { id: d.Forecast_Row_ID, ...d };
                if (d.Forecast_Tab === 'Contingency') {
                    if (!contingData[d.Forecast_Row_ID]) contingData[d.Forecast_Row_ID] = { id: d.Forecast_Row_ID };
                    contingData[d.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value;
                }
                // Track Scorecard installed & plug for PF earned-hours calculation
                if (d.Forecast_Tab === 'Scorecard') {
                    if (d.Forecast_Field_Name === 'installed') installedQtyByCode[normCC(d.Forecast_Row_ID)] = pNum(d.Forecast_Value) || 0;
                    if (d.Forecast_Field_Name === 'plug') plugQtyByCode[normCC(d.Forecast_Row_ID)] = pNum(d.Forecast_Value) || 0;
                }
                if (['Materials', 'Rentals', 'Subcontractors', 'Other', 'Materials-phased', 'Rentals-phased', 'Subcontractors-phased', 'Other-phased'].includes(d.Forecast_Tab) && d.Cost_Code !== '101101' && !(hideFieldBonus && String(d.Cost_Type) === '51800')) {
                    const effectiveCC = d.Cost_Code && d.Cost_Code !== '1000' && d.Cost_Code !== '990000' ? d.Cost_Code : '100101';
                    {
                        const isRev = d.Cost_Type?.toString().startsWith('4');
                        const key = isRev ? 'CONTRACT_AMOUNT' : normCC(effectiveCC);
                        if (!costCodeData[key]) costCodeData[key] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                        const amt = pNum(d.Value_Amount);
                        costCodeData[key].etc += amt;
                        if (d.Forecast_Tab === 'Materials' || d.Forecast_Tab === 'Materials-phased') costCodeData[key].etcMaterials += amt;
                        else if (d.Forecast_Tab === 'Rentals' || d.Forecast_Tab === 'Rentals-phased') costCodeData[key].etcRentals += amt;
                        else if (d.Forecast_Tab === 'Subcontractors' || d.Forecast_Tab === 'Subcontractors-phased') costCodeData[key].etcSubs += amt;
                        else if (d.Forecast_Tab === 'Other' || d.Forecast_Tab === 'Other-phased') costCodeData[key].etcOther += amt;
                        // LEMSO: track ETC by actual cost type (not tab name) so 52xxx→E, 51800→L, etc.
                        if (!isRev) {
                            const _etcCat = getCostTypeCategory(d.Cost_Type);
                            if (!etcByTypeAndCode[_etcCat][key]) etcByTypeAndCode[_etcCat][key] = 0;
                            etcByTypeAndCode[_etcCat][key] += amt;
                        }
                    }
                }
                // Capture cost-type-level ETC (Forecast_Field_Name='etc') for Labor/Equipment sub-types
                // NOT covered by the tab-specific paths above. Mirrors bidForecast's etcValues capture
                // for 51600/51700/51800 (Truck Pay / Welding Rig Pay / Field Bonus) and 52300/52400 (Fuel / Repairs).
                else if (
                    d.Forecast_Field_Name === 'etc' &&
                    d.Cost_Type &&
                    d.Cost_Code !== '101101' &&
                    !['Burn Tool Labor', 'Burn Tool Equipment', 'Burn Tool Labor Phased', 'Burn Tool Equipment Phased'].includes(d.Forecast_Tab)
                ) {
                    const _ct = String(d.Cost_Type);
                    const _isMissingLabor = _ct.startsWith('51') && _ct !== '51100' && !(hideFieldBonus && _ct === '51800');
                    const _isMissingEquipment = _ct === '52300' || _ct === '52400';
                    if (_isMissingLabor || _isMissingEquipment) {
                        const _effCC = d.Cost_Code && d.Cost_Code !== '1000' && d.Cost_Code !== '990000' ? d.Cost_Code : '100101';
                        const _ck = normCC(_effCC);
                        if (!costCodeData[_ck]) costCodeData[_ck] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                        const _amt = pNum(d.Value_Amount);
                        costCodeData[_ck].etc += _amt;
                        if (_isMissingLabor) costCodeData[_ck].etcLabor += _amt; else costCodeData[_ck].etcEquipment += _amt;
                        const _cat = getCostTypeCategory(d.Cost_Type);
                        if (!etcByTypeAndCode[_cat][_ck]) etcByTypeAndCode[_cat][_ck] = 0;
                        etcByTypeAndCode[_cat][_ck] += _amt;
                    }
                }
            }
        });

        // Match bidForecast/productivityCombined: prefer projectComplete atom (set via job details);
        // fall back to per-version completionDateForecast. completionDates defaults to {} which makes
        // moment(pC).endOf('week') resolve to the current week and silently drops all future phased
        // labor/equipment rows — the source of the 51100/52100 ETC undercount.
        const pC = projectComplete || (typeof completionDates === 'string' && completionDates ? completionDates : null);
        Object.keys(equipmentValues).forEach(key => {
            if (key.length === 10 && moment(key).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(pC).endOf('week').isAfter(key)) {
                const row = equipmentValues[key];
                const cost = (isNaN(pNum(row.Rate)) ? 0 : pNum(row.Rate)) * (isNaN(pNum(row.Number_Units)) ? 0 : pNum(row.Number_Units)) * (isNaN(pNum(row.Number_Hours)) ? 0 : pNum(row.Number_Hours));
                if (row?.Cost_Code && cost > 0 && row.Cost_Code !== '1000' && row.Cost_Code !== '990000') {
                    const ck = row.Cost_Type?.toString().startsWith('4') ? 'CONTRACT_AMOUNT' : normCC(row.Cost_Code);
                    if (!costCodeData[ck]) costCodeData[ck] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                    costCodeData[ck].etc += cost;
                    costCodeData[ck].etcEquipment += cost;
                    // LEMSO: track burn tool equipment ETC by cost type
                    if (!ck.startsWith('CONTRACT')) { etcByTypeAndCode['E'][ck] = (etcByTypeAndCode['E'][ck] || 0) + cost; }
                }
            }
        });
        Object.values(laborValues).forEach(row => {
            if (row) {
                const week = row.Forecast_Row_ID ? row.Forecast_Row_ID.split('*')[0] : '';
                if (week && moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(pC).endOf('week').isAfter(week)) {
                    const cost = !isNaN(pNum(row.Rate)) && !isNaN(pNum(row.Number_Units)) && !isNaN(pNum(row.Number_Hours)) ? pNum(row.Rate) * pNum(row.Number_Units) * pNum(row.Number_Hours) : 0;
                    if (row?.Cost_Code && cost > 0 && row.Cost_Code !== '1000' && row.Cost_Code !== '990000') {
                        const ck = row.Cost_Type?.toString().startsWith('4') ? 'CONTRACT_AMOUNT' : normCC(row.Cost_Code);
                        if (!costCodeData[ck]) costCodeData[ck] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                        costCodeData[ck].etc += cost;
                        costCodeData[ck].etcLabor += cost;
                        // LEMSO: track burn tool labor ETC by cost type
                        if (!ck.startsWith('CONTRACT')) { etcByTypeAndCode['L'][ck] = (etcByTypeAndCode['L'][ck] || 0) + cost; }
                    }
                }
            }
        });
        Object.keys(phasedLabor).forEach(row => {
            const weekRow = row.includes('=');
            const week = row.split('=')[1];
            const keyCC = row.split('=')[0]; // cost code from key (e.g., '140201' from '140201=2026-03-30')
            if (weekRow && phasedLabor[row] && moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(pC).endOf('week'))) {
                const h = pNum(phasedLabor[row].Number_Hours || 0), bR = pNum(phasedLabor[row].Rate || 0), cS = pNum(phasedLabor[row].Number_Units || 0);
                if (h && bR && cS) {
                    const cost = h * bR * cS;
                    const lr = phasedLabor[row];
                    // Use Cost_Code from DB if valid, otherwise fall back to cost code extracted from the key
                    const rawCC = (lr?.Cost_Code && lr.Cost_Code !== 'undefined') ? lr.Cost_Code : keyCC;
                    if (rawCC && cost > 0 && rawCC !== '1000' && rawCC !== '990000') {
                        const ck = lr.Cost_Type?.toString().startsWith('4') ? 'CONTRACT_AMOUNT' : normCC(rawCC);
                        if (!costCodeData[ck]) costCodeData[ck] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                        costCodeData[ck].etc += cost;
                        costCodeData[ck].etcLabor += cost;
                        // LEMSO: track phased labor ETC by cost type
                        if (!ck.startsWith('CONTRACT')) { etcByTypeAndCode['L'][ck] = (etcByTypeAndCode['L'][ck] || 0) + cost; }
                    }
                }
            }
        });
        Object.keys(phasedEquipment).forEach(row => {
            // Row IDs use '=' (new) or '*' (legacy) between name and week
            const weekRow = row.includes('=') || row.includes('*');
            const _sep = row.includes('=') ? '=' : '*';
            const _idx = row.lastIndexOf(_sep);
            const week = _idx >= 0 ? row.substring(_idx + 1) : undefined;
            const phase = _idx >= 0 ? row.substring(0, _idx) : row;
            if (weekRow && phasedEquipment[row] && moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(pC).endOf('week'))) {
                const h = pNum(phasedEquipment[row].Number_Hours || 0), bR = pNum(phasedEquipment[row].Rate || 0), cS = pNum(phasedEquipment[row].Number_Units || 0);
                if (h && bR && cS) {
                    const cost = h * bR * cS;
                    if (cost > 0) {
                        const ck = phase !== 'undefined' ? normCC(phase) : '100101';
                        if (!costCodeData[ck]) costCodeData[ck] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                        costCodeData[ck].etc += cost;
                        costCodeData[ck].etcEquipment += cost;
                        // LEMSO: track phased equipment ETC by cost type
                        etcByTypeAndCode['E'][ck] = (etcByTypeAndCode['E'][ck] || 0) + cost;
                    }
                }
            }
        });

        let totalcLabor = 0, totalcEquipment = 0, totalcMaterials = 0, totalcSubcontractors = 0, totalcOtherExpenses = 0;
        Object.values(contingData).forEach(row => {
            totalcLabor += pNum(row.CurrentLabor || 0);
            totalcEquipment += pNum(row.CurrentEquipment || 0);
            totalcMaterials += pNum(row.CurrentMaterials || 0);
            totalcSubcontractors += pNum(row.CurrentSubs || 0);
            totalcOtherExpenses += pNum(row.CurrentOther || 0);
        });
        const totalcTotal = totalcLabor + totalcEquipment + totalcMaterials + totalcSubcontractors + totalcOtherExpenses;

        let contingencyDayZeroTotal = 0, contingencyCOTotal = 0, overheadDayZeroBudget = 0;
        Object.values(dayZeroValues).forEach(row => {
            if (row.Cost_Code === '101101') contingencyDayZeroTotal += pNum(row.Value_Amount || 0);
            // Track overhead day zero budget (matching sidemenu)
            if (row.Cost_Type?.toString() === '60900' && row.Cost_Code !== '101101') overheadDayZeroBudget += pNum(row.Value_Amount || 0);
        });
        Object.values(chOrValues).forEach(row => { if ((row.Status || row.status || '').toLowerCase() === 'approved' && row.Cost_Code == '101101' && row.Cost_Type?.toString()[0] === '5') contingencyCOTotal += pNum(row.Value_Amount || 0); });
        costCodeData['Contingency'] = { etcLabor: totalcLabor, etcEquipment: totalcEquipment, etcMaterials: totalcMaterials, etcSubs: totalcSubcontractors, etcOther: totalcOtherExpenses, etc: totalcTotal, dayZero: contingencyDayZeroTotal, dZ: [], cos: 0 };

        Object.values(dayZeroValues).forEach(row => {
            if (row.Cost_Code && row.Cost_Code !== '1000' && row.Cost_Code !== '990000') {
                const isRev = row.Cost_Type?.toString().startsWith('4');
                const key = isRev ? 'CONTRACT_AMOUNT' : normCC(row.Cost_Code);
                if (!costCodeData[key]) costCodeData[key] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                costCodeData[key].dZ.push(row);
                // LEMSO: track DZ by cost type category. Exclude contingency (101101) — it's
                // added separately via contingencyDayZeroTotal, mirroring how coByTypeAndCode
                // handles CO contingency. Including it here would double-count in tDZ.
                if (!isRev && row.Cost_Code !== '101101') {
                    const _dzCat = getCostTypeCategory(row.Cost_Type);
                    if (!dzByTypeAndCode[_dzCat][key]) dzByTypeAndCode[_dzCat][key] = 0;
                    dzByTypeAndCode[_dzCat][key] += pNum(row.Value_Amount || 0);
                }
            }
        });
        const _coDebugRows = [];
        Object.values(chOrValues).forEach(row => {
            const _rowStatus = (row.Status || row.status || '').toLowerCase();
            if (_rowStatus !== 'approved') return;
            const hasCode = row.Cost_Code != null && row.Cost_Code != '' && row.Cost_Code != '1000' && row.Cost_Code != '990000';
            // Rows with no phase code → route to '100101'
            const effectiveCode = hasCode ? row.Cost_Code : (row.Cost_Type?.toString()[0] === '5' ? '100101' : null);
            if (effectiveCode == null) return;

            // Always route revenue (41000) to CONTRACT_AMOUNT, matching sidemenu logic
            const rev41 = pNum(row['41000'] || 0);
            if (rev41 && effectiveCode != '101101') {
                if (!costCodeData['CONTRACT_AMOUNT']) costCodeData['CONTRACT_AMOUNT'] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                costCodeData['CONTRACT_AMOUNT'].cos += rev41;
            }
            // Route cost-type CO amounts to the cost code
            if (effectiveCode == '101101') {
                // Contingency COs: use Value_Amount directly for cost-type rows
                if (row.Cost_Type?.toString()[0] === '5') {
                    if (!costCodeData['Contingency']) costCodeData['Contingency'] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                    costCodeData['Contingency'].cos += pNum(row.Value_Amount || 0);
                }
            } else if (row.Cost_Type?.toString()[0] === '5') {
                const cc = normCC(effectiveCode);
                if (!costCodeData[cc]) costCodeData[cc] = { etc: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, etcLabor: 0, etcEquipment: 0, dayZero: 0, dZ: [], cos: 0 };
                const _amt = pNum(row.Value_Amount || 0);
                costCodeData[cc].cos += _amt;
                _coDebugRows.push({ CO: row.Row_Label || row.id, CostCode: effectiveCode, CostType: row.Cost_Type, FieldName: row.Forecast_Field_Name, Amount: _amt });
                // LEMSO: track COs by cost type category
                const _coCat = getCostTypeCategory(row.Cost_Type);
                if (!coByTypeAndCode[_coCat][cc]) coByTypeAndCode[_coCat][cc] = 0;
                coByTypeAndCode[_coCat][cc] += _amt;
            }
        });
        // Ensure '100101' (no-phase CO rows) map to 'Project Team' discipline — only if not already mapped via Productivity Tracker
        if (costCodeData['100101'] && !disciplineMapping['100101']) disciplineMapping['100101'] = vgDiscipline('100101', isVoltaGrid) || 'Project Team';

        Object.keys(costCodeData).forEach(cc => { if (cc !== 'CONTRACT_AMOUNT' && cc !== 'Contingency' && !disciplineMapping[cc]) disciplineMapping[cc] = vgDiscipline(cc, isVoltaGrid) || 'Other'; });

        const actualCostsByCode = {};
        let ohJTD = 0, revenueJTDPrevPeriod = 0, revenueJTD = 0;
        jobCostDetail.forEach(item => {
            if (item.Phase_Code && item.Cost_Type_Code?.[0] === '5' && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                const npc = normCC(item.Phase_Code);
                actualCostsByCode[npc] = (actualCostsByCode[npc] || 0) + pNum(item.Job_Cost || 0);
                if (!disciplineMapping[npc]) disciplineMapping[npc] = vgDiscipline(npc, isVoltaGrid) || 'Other';
                // Track actual labor hours for PF (51000-51799)
                if (item.Cost_Type_Code >= '51000' && item.Cost_Type_Code <= '51799') {
                    actualHoursByCode[npc] = (actualHoursByCode[npc] || 0) + (parseFloat(item.Total_Hours) || 0);
                }
                // Track actual labor dollars (51xxx) for CPI
                if (item.Cost_Type_Code?.startsWith('51')) {
                    laborActualDollarsByCode[npc] = (laborActualDollarsByCode[npc] || 0) + pNum(item.Job_Cost || 0);
                }
                // LEMSO: track actuals by cost type category
                const _actCat = getCostTypeCategory(item.Cost_Type_Code);
                if (!actualByTypeAndCode[_actCat][npc]) actualByTypeAndCode[_actCat][npc] = 0;
                actualByTypeAndCode[_actCat][npc] += pNum(item.Job_Cost || 0);
            }
            // Track overhead JTD (matching sidemenu)
            if (item.Cost_Type_Code === '60900') {
                ohJTD += pNum(item.Job_Cost || 0);
            }
            // Track revenue JTD for Contract Amount EAC
            if (item.Cost_Type_Code >= '40000' && item.Cost_Type_Code <= '49999') {
                revenueJTD += pNum(item.Job_Cost || 0);
                if (versionInfo?.Version_Start_DateTime && moment(item.GL_Date).isBefore(moment(versionInfo.Version_Start_DateTime).subtract(1, 'month').endOf('month'))) {
                    revenueJTDPrevPeriod += pNum(item.Job_Cost || 0);
                }
            }
        });
        const commitmentsByCode = {};
        let ohCommitted = 0;
        jobOpenCommitments.forEach(item => {
            if (item.Phase_Code && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                const npc = normCC(item.Phase_Code);
                commitmentsByCode[npc] = (commitmentsByCode[npc] || 0) + pNum(item.PO_True_Open_Amount || 0);
                if (!disciplineMapping[npc]) disciplineMapping[npc] = vgDiscipline(npc, isVoltaGrid) || 'Other';
                // LEMSO: track commitments by cost type category
                if (item.Cost_Type_Code?.[0] === '5') {
                    const _comCat = getCostTypeCategory(item.Cost_Type_Code);
                    if (!commitByTypeAndCode[_comCat][npc]) commitByTypeAndCode[_comCat][npc] = 0;
                    commitByTypeAndCode[_comCat][npc] += pNum(item.PO_True_Open_Amount || 0);
                }
            }
            // Track overhead committed (matching sidemenu)
            if (item.Cost_Type_Code >= '60900' && item.Cost_Type_Code <= '61000') {
                ohCommitted += pNum(item.PO_True_Open_Amount || 0);
            }
        });
        if (jobCostDetailPrev && Array.isArray(jobCostDetailPrev)) {
            jobCostDetailPrev.forEach(item => {
                if (item.Phase_Code && item.Cost_Type_Code?.[0] === '5' && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    const npc = normCC(item.Phase_Code);
                    previousActualCostsByCode[npc] = (previousActualCostsByCode[npc] || 0) + pNum(item.Job_Cost || 0);
                    const _pActCat = getCostTypeCategory(item.Cost_Type_Code);
                    previousActualByTypeAndCode[_pActCat][npc] = (previousActualByTypeAndCode[_pActCat][npc] || 0) + pNum(item.Job_Cost || 0);
                }
            });
        }
        if (jobOpenCommitmentsPrev && Array.isArray(jobOpenCommitmentsPrev)) {
            jobOpenCommitmentsPrev.forEach(item => {
                if (item.Phase_Code && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    const npc = normCC(item.Phase_Code);
                    previousCommitmentsByCode[npc] = (previousCommitmentsByCode[npc] || 0) + pNum(item.PO_True_Open_Amount || 0);
                    if (item.Cost_Type_Code?.[0] === '5') {
                        const _pComCat = getCostTypeCategory(item.Cost_Type_Code);
                        previousCommitByTypeAndCode[_pComCat][npc] = (previousCommitByTypeAndCode[_pComCat][npc] || 0) + pNum(item.PO_True_Open_Amount || 0);
                    }
                }
            });
        }
        if (jobUserDataPrev && Array.isArray(jobUserDataPrev)) {
            // Deduplicate previous version data the same way as the current version
            const prevGrouped = jobUserDataPrev.reduce((acc, item) => {
                const key = `${item.Version_GUID}_${item.BU_ID}_${item.Forecast_Tab}_${item.Forecast_Field_Name}_${item.Forecast_Row_ID}`;
                if (!acc[key]) acc[key] = [];
                acc[key].push(item);
                return acc;
            }, {});
            const prevDeduped = Object.values(prevGrouped).map(group => group.reduce((latest, current) => {
                const ld = new Date(Math.max(new Date(latest.DSS_Created_Date), new Date(latest.DSS_Updated_Datetime || 0)));
                const cd = new Date(Math.max(new Date(current.DSS_Created_Date), new Date(current.DSS_Updated_Datetime || 0)));
                return cd > ld ? current : latest;
            }));
            // Mirror the current version's ETC computation: use Rate*Hours*Units for burn tool entries with date filtering
            const prevVersionStart = prevVersion?.Version_Start_DateTime;
            const prevVersionGUID = prevVersion?.Version_GUID;
            let prevCompletionDate = '';
            const prevEquipmentValues = {};
            const prevLaborValues = {};
            const prevPhasedLabor = {};
            const prevPhasedEquipment = {};
            prevDeduped.forEach(d => {
                if (prevVersionGUID && d.Version_GUID != prevVersionGUID) return;
                if (d.Forecast_Tab === 'All' || d.Forecast_Tab === 'Forecast') {
                    if (d.Forecast_Field_Name === 'completionDateForecast' && d.Forecast_Value) prevCompletionDate = moment(d.Forecast_Value).format('M/D/YYYY');
                }
                if (d.Forecast_Tab === 'Burn Tool Labor') prevLaborValues[d.Forecast_Row_ID] = d;
                if (d.Forecast_Tab === 'Burn Tool Equipment') prevEquipmentValues[d.Forecast_Row_ID] = d;
                if (d.Forecast_Tab === 'Burn Tool Equipment Phased') prevPhasedEquipment[d.Forecast_Row_ID] = { id: d.Forecast_Row_ID, ...d };
                if (d.Forecast_Tab === 'Burn Tool Labor Phased') prevPhasedLabor[d.Forecast_Row_ID] = { id: d.Forecast_Row_ID, ...d };
                if (['Materials', 'Rentals', 'Subcontractors', 'Other', 'Materials-phased', 'Rentals-phased', 'Subcontractors-phased', 'Other-phased'].includes(d.Forecast_Tab)) {
                    if (d.Cost_Code && d.Cost_Code !== '1000' && d.Cost_Code !== '990000') {
                        const key = d.Cost_Type?.toString().startsWith('4') ? 'CONTRACT_AMOUNT' : normCC(d.Cost_Code);
                        previousVersionData[key] = (previousVersionData[key] || 0) + pNum(d.Value_Amount || 0);
                        if (!d.Cost_Type?.toString().startsWith('4')) {
                            const _pEtcCat = getCostTypeCategory(d.Cost_Type);
                            previousEtcByTypeAndCode[_pEtcCat][key] = (previousEtcByTypeAndCode[_pEtcCat][key] || 0) + pNum(d.Value_Amount || 0);
                        }
                    }
                }
            });
            // Same fallback as the current period (see pC above): without it, a prev version
            // missing completionDateForecast makes moment('') invalid and every prev burn-tool
            // row is dropped, zeroing prev labor/equipment ETC.
            const prevPC = projectComplete || prevCompletionDate || null;
            // Burn Tool Equipment (non-phased)
            Object.keys(prevEquipmentValues).forEach(key => {
                if (key.length === 10 && moment(key).isAfter(moment(prevVersionStart).startOf('week')) && moment(prevPC).endOf('week').isAfter(key)) {
                    const row = prevEquipmentValues[key];
                    const cost = (isNaN(pNum(row.Rate)) ? 0 : pNum(row.Rate)) * (isNaN(pNum(row.Number_Units)) ? 0 : pNum(row.Number_Units)) * (isNaN(pNum(row.Number_Hours)) ? 0 : pNum(row.Number_Hours));
                    if (row?.Cost_Code && cost > 0 && row.Cost_Code !== '1000' && row.Cost_Code !== '990000') {
                        const ck = row.Cost_Type?.toString().startsWith('4') ? 'CONTRACT_AMOUNT' : normCC(row.Cost_Code);
                        previousVersionData[ck] = (previousVersionData[ck] || 0) + cost;
                        if (!ck.startsWith('CONTRACT')) { previousEtcByTypeAndCode['E'][ck] = (previousEtcByTypeAndCode['E'][ck] || 0) + cost; }
                    }
                }
            });
            // Burn Tool Labor (non-phased)
            Object.values(prevLaborValues).forEach(row => {
                if (row) {
                    const week = row.Forecast_Row_ID ? row.Forecast_Row_ID.split('*')[0] : '';
                    if (week && moment(week).isAfter(moment(prevVersionStart).startOf('week')) && moment(prevPC).isAfter(week)) {
                        const cost = !isNaN(pNum(row.Rate)) && !isNaN(pNum(row.Number_Units)) && !isNaN(pNum(row.Number_Hours)) ? pNum(row.Rate) * pNum(row.Number_Units) * pNum(row.Number_Hours) : 0;
                        if (row?.Cost_Code && cost > 0 && row.Cost_Code !== '1000' && row.Cost_Code !== '990000') {
                            const ck = row.Cost_Type?.toString().startsWith('4') ? 'CONTRACT_AMOUNT' : normCC(row.Cost_Code);
                            previousVersionData[ck] = (previousVersionData[ck] || 0) + cost;
                            if (!ck.startsWith('CONTRACT')) { previousEtcByTypeAndCode['L'][ck] = (previousEtcByTypeAndCode['L'][ck] || 0) + cost; }
                        }
                    }
                }
            });
            // Burn Tool Labor Phased
            Object.keys(prevPhasedLabor).forEach(row => {
                const weekRow = row.includes('=');
                const week = row.split('=')[1];
                if (weekRow && prevPhasedLabor[row] && moment(week).isAfter(moment(prevVersionStart).startOf('week')) && moment(week).isBefore(moment(prevPC).endOf('week'))) {
                    const h = pNum(prevPhasedLabor[row].Number_Hours || 0), bR = pNum(prevPhasedLabor[row].Rate || 0), cS = pNum(prevPhasedLabor[row].Number_Units || 0);
                    if (h && bR && cS) {
                        const cost = h * bR * cS;
                        const lr = prevPhasedLabor[row];
                        if (lr?.Cost_Code && cost > 0 && lr.Cost_Code !== '1000' && lr.Cost_Code !== '990000') {
                            const ck = lr.Cost_Type?.toString().startsWith('4') ? 'CONTRACT_AMOUNT' : normCC(lr.Cost_Code);
                            previousVersionData[ck] = (previousVersionData[ck] || 0) + cost;
                            if (!ck.startsWith('CONTRACT')) { previousEtcByTypeAndCode['L'][ck] = (previousEtcByTypeAndCode['L'][ck] || 0) + cost; }
                        }
                    }
                }
            });
            // Burn Tool Equipment Phased
            Object.keys(prevPhasedEquipment).forEach(row => {
                // Row IDs use '=' (new) or '*' (legacy) between name and week
                const weekRow = row.includes('=') || row.includes('*');
                const _sep = row.includes('=') ? '=' : '*';
                const _idx = row.lastIndexOf(_sep);
                const week = _idx >= 0 ? row.substring(_idx + 1) : undefined;
                const phase = _idx >= 0 ? row.substring(0, _idx) : row;
                if (weekRow && prevPhasedEquipment[row] && moment(week).isAfter(moment(prevVersionStart).startOf('week')) && moment(week).isBefore(moment(prevPC).endOf('week'))) {
                    const h = pNum(prevPhasedEquipment[row].Number_Hours || 0), bR = pNum(prevPhasedEquipment[row].Rate || 0), cS = pNum(prevPhasedEquipment[row].Number_Units || 0);
                    if (h && bR && cS) {
                        const cost = h * bR * cS;
                        if (cost > 0) {
                            const ck = phase !== 'undefined' ? normCC(phase) : '100101';
                            previousVersionData[ck] = (previousVersionData[ck] || 0) + cost;
                            previousEtcByTypeAndCode['E'][ck] = (previousEtcByTypeAndCode['E'][ck] || 0) + cost;
                        }
                    }
                }
            });
        }

        const allDisciplines = [...new Set(Object.values(disciplineMapping))];
        if (!allDisciplines.includes('Other')) allDisciplines.push('Other');
        const disciplineCostCodes = {};
        Object.keys(disciplineMapping).forEach(cc => { if (cc === '101101') return; const d = disciplineMapping[cc]; if (!disciplineCostCodes[d]) disciplineCostCodes[d] = []; disciplineCostCodes[d].push(cc); });

        // Cumulative planned hours per code up to current week (for SPI = earned hrs / planned hrs)
        const plannedHrsByCode = {};
        const _spiCurrentWeek = versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime).startOf('isoWeek').format('YYYY-MM-DD') : null;
        jobUserData.forEach(item => {
            if (item.Forecast_Tab === 'Phase Hours' && item.Version_GUID === selectedVersion && item.Forecast_Field_Name?.startsWith('week_')) {
                const week = item.Forecast_Field_Name.replace('week_', '');
                if (!_spiCurrentWeek || week <= _spiCurrentWeek) {
                    const npc = normCC(item.Forecast_Row_ID);
                    plannedHrsByCode[npc] = (plannedHrsByCode[npc] || 0) + (parseFloat(item.Forecast_Value) || 0);
                }
            }
        });

        // ── PF helpers: precompute CO hours/qty per code, heavy-job installed, and earned-hours helper ──
        const coHrsByCode = {}, coQtyByCode = {};
        Object.values(chOrValues).forEach(row => {
            if ((row.Status || row.status || '').toLowerCase() !== 'approved') return;
            if (row.Cost_Code === '101101') return;
            // Use same routing logic as budget: no code / 1000 / 990000 → route to 100101
            const hasCode = row.Cost_Code != null && row.Cost_Code !== '' && row.Cost_Code !== '1000' && row.Cost_Code !== '990000';
            const effectiveCode = hasCode ? row.Cost_Code : (row.Cost_Type?.toString()[0] === '5' ? '100101' : null);
            if (!effectiveCode) return;
            const npc = normCC(effectiveCode);
            coHrsByCode[npc] = (coHrsByCode[npc] || 0) + (parseFloat(row.Hours) || 0);
            coQtyByCode[npc] = (coQtyByCode[npc] || 0) + (parseFloat(row.Quantity) || 0);
            // Track labor CO dollars (51xxx) for CPI
            if (row.Cost_Type?.toString().startsWith('51')) {
                laborCoDollarsByCode[npc] = (laborCoDollarsByCode[npc] || 0) + pNum(row.Value_Amount || 0);
            }
        });
        const heavyJobInstalledByCode = {};
        if (Array.isArray(jobCostDetailRaw)) {
            jobCostDetailRaw.forEach(cd => {
                if (cd.Document_Type === 'FP' && cd.Phase_Code) {
                    const npc = normCC(cd.Phase_Code);
                    heavyJobInstalledByCode[npc] = (heavyJobInstalledByCode[npc] || 0) + (pNum(cd.Total_Hours) || 0);
                }
            });
        }
        /** Earned hours for a cost code: pctComplete × budgetHours */
        const getCodeHoursData = (cc) => {
            const budgetHrs = (dayZeroHrsByCode[cc] || 0) + (coHrsByCode[cc] || 0);
            const dzQty = costCodeQuantities[cc] || 0;
            const totalQty = dzQty + (coQtyByCode[cc] || 0) + (plugQtyByCode[cc] || 0);
            const installed = (installedQtyByCode[cc] || 0) + (heavyJobInstalledByCode[cc] || 0);
            const pctComplete = totalQty > 0 ? Math.min(installed / totalQty, 1) : 0;
            return { budgetHrs, earnedHrs: pctComplete * budgetHrs, actualHrs: actualHoursByCode[cc] || 0 };
        };

        // Per-cost-code results (for sub-rows)
        Object.keys(disciplineMapping).forEach(costCode => {
            const cd = costCodeData[costCode] || { etc: 0, etcLabor: 0, etcEquipment: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, dayZero: 0, dZ: [], cos: 0 };
            const ac = actualCostsByCode[costCode] || 0;
            const oc = commitmentsByCode[costCode] || 0;
            const dz = cd.dZ.reduce((s, i) => s + pNum(i.Value_Amount || 0), 0);
            const co = cd.cos;
            const cb = dz + co, eac = ac + oc + cd.etc;
            const pAc = previousActualCostsByCode[costCode] || 0;
            const pOc = previousCommitmentsByCode[costCode] || 0;
            const pEtc = previousVersionData[costCode] || 0;
            const pEAC = pAc + pOc + pEtc;
            const _h = getCodeHoursData(costCode);
            const _pctComplete = _h.budgetHrs > 0 ? (_h.earnedHrs / _h.budgetHrs * 100) : 0;
            const pf = _pctComplete < 25 ? 1 : (_h.actualHrs > 0 ? _h.earnedHrs / _h.actualHrs : 0);
            const _physPct = _h.budgetHrs > 0 ? _h.earnedHrs / _h.budgetHrs : 0;
            const _laborBudgetCC = (laborDzDollarsByCode[costCode] || 0) + (laborCoDollarsByCode[costCode] || 0);
            const _laborActualCC = laborActualDollarsByCode[costCode] || 0;
            // Like PF: under 25% complete a labor code's CPI and SPI hold at 1 (too little earned to read the ratio)
            const _early = _h.budgetHrs > 0 && _pctComplete < 25;
            const cpi = _early ? 1 : (_laborActualCC > 0 ? (_physPct * _laborBudgetCC) / _laborActualCC : 0);
            const _plannedHrsCC = plannedHrsByCode[costCode] || 0;
            const spi = _early ? 1 : (_plannedHrsCC > 0 ? _h.earnedHrs / _plannedHrsCC : 0);
            result[`costCode_${costCode}`] = {
                _pfDebug: { budgetHrs: _h.budgetHrs, earnedHrs: _h.earnedHrs, actualHrs: _h.actualHrs, pctComplete: _h.budgetHrs > 0 ? (_h.earnedHrs / _h.budgetHrs * 100) : 0, codes: [{ cc: costCode, budgetHrs: _h.budgetHrs, earnedHrs: _h.earnedHrs, actualHrs: _h.actualHrs, pct: _h.budgetHrs > 0 ? (_h.earnedHrs / _h.budgetHrs * 100) : 0 }] },
                _cpiDebug: { budgetHrs: _h.budgetHrs, earnedHrs: _h.earnedHrs, physPct: _physPct, laborBudget: _laborBudgetCC, laborBudgetDZ: laborDzDollarsByCode[costCode] || 0, laborBudgetCO: laborCoDollarsByCode[costCode] || 0, laborActual: _laborActualCC, earnedLabor: _physPct * _laborBudgetCC, codes: [{ cc: costCode, budgetHrs: _h.budgetHrs, earnedHrs: _h.earnedHrs, physPct: _physPct, laborBudget: _laborBudgetCC, laborBudgetDZ: laborDzDollarsByCode[costCode] || 0, laborBudgetCO: laborCoDollarsByCode[costCode] || 0, laborActual: _laborActualCC, earnedLabor: _physPct * _laborBudgetCC }] },
                _spiDebug: { budgetHrs: _h.budgetHrs, earnedHrs: _h.earnedHrs, plannedHrs: _plannedHrsCC, pctComplete: _h.budgetHrs > 0 ? (_h.earnedHrs / _h.budgetHrs * 100) : 0, codes: [{ cc: costCode, budgetHrs: _h.budgetHrs, earnedHrs: _h.earnedHrs, plannedHrs: _plannedHrsCC, spi: _plannedHrsCC > 0 ? _h.earnedHrs / _plannedHrsCC : 0 }] },
                k2Budget: '-', bidBudget: fNum(dz, 1), approvedCOs: fNum(co, 1), currentBudget: fNum(cb, 1),
                actualCosts: fNum(ac, 1), openCommit: fNum(oc, 1), totalCommit: fNum(ac + oc, 1),
                etcLabor: fNum(cd.etcLabor || 0, 1), etcEquipment: fNum(cd.etcEquipment || 0, 1),
                etcMaterials: fNum(cd.etcMaterials || 0, 1), etcRentals: fNum(cd.etcRentals || 0, 1),
                etcSubs: fNum(cd.etcSubs || 0, 1), etcOther: fNum(cd.etcOther || 0, 1),
                etc: fNum(cd.etc, 1), eac: fNum(eac, 1), deltaEACBudget: fNum(eac - cb, 1),
                prevActualCosts: fNum(pAc, 1), prevOpenCommit: fNum(pOc, 1), prevEtcTotal: fNum(pEtc, 1), previousEAC: fNum(pEAC, 1),
                deltaEACPrevious: fNum(eac - pEAC, 1), pf: pf > 0 ? fNum(pf) : '-', cpi: cpi > 0 ? fNum(cpi) : '-', spi: spi > 0 ? fNum(spi) : '-'
            };
        });

        // Discipline-level aggregation
        allDisciplines.forEach(discipline => {
            const codes = disciplineCostCodes[discipline] || [];
            let ac = 0, oc = 0, etc = 0, dz = 0, co = 0, etcL = 0, etcEq = 0, etcM = 0, etcR = 0, etcS = 0, etcO = 0;
            codes.forEach(cc => {
                const cd = costCodeData[cc] || { etc: 0, etcLabor: 0, etcEquipment: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, dZ: [], cos: 0 };
                ac += actualCostsByCode[cc] || 0; oc += commitmentsByCode[cc] || 0; etc += cd.etc;
                dz += cd.dZ.reduce((s, i) => s + pNum(i.Value_Amount || 0), 0); co += cd.cos;
                etcL += cd.etcLabor || 0; etcEq += cd.etcEquipment || 0; etcM += cd.etcMaterials || 0;
                etcR += cd.etcRentals || 0; etcS += cd.etcSubs || 0; etcO += cd.etcOther || 0;
            });
            const cb = dz + co, eac = ac + oc + etc;
            let pAc = 0, pOc = 0, pEtc = 0;
            codes.forEach(cc => { pAc += previousActualCostsByCode[cc] || 0; pOc += previousCommitmentsByCode[cc] || 0; pEtc += previousVersionData[cc] || 0; });
            const pEAC = pAc + pOc + pEtc;
            // PF = Earned Hours / Actual Hours; CPI = Earned Labor $ / Actual Labor $; SPI = Earned Hrs / Planned Hrs
            let discActualHrs = 0, discEarnedHrs = 0, discBudgetHrs = 0, discLaborBudget = 0, discLaborBudgetDZ = 0, discLaborBudgetCO = 0, discLaborActual = 0, discPlannedHrs = 0;
            codes.forEach(cc => {
                const h = getCodeHoursData(cc);
                discActualHrs += h.actualHrs; discEarnedHrs += h.earnedHrs; discBudgetHrs += h.budgetHrs;
                discLaborBudgetDZ += laborDzDollarsByCode[cc] || 0;
                discLaborBudgetCO += laborCoDollarsByCode[cc] || 0;
                discLaborBudget += (laborDzDollarsByCode[cc] || 0) + (laborCoDollarsByCode[cc] || 0);
                discLaborActual += laborActualDollarsByCode[cc] || 0;
                discPlannedHrs += plannedHrsByCode[cc] || 0;
            });
            const pf = (discBudgetHrs > 0 && (discEarnedHrs / discBudgetHrs * 100) < 25) ? 1 : (discActualHrs > 0 ? discEarnedHrs / discActualHrs : 0);
            const discPhysPct = discBudgetHrs > 0 ? discEarnedHrs / discBudgetHrs : 0;
            const _discEarly = discBudgetHrs > 0 && discPhysPct * 100 < 25;   // same <25% rule as PF
            const cpi = _discEarly ? 1 : (discLaborActual > 0 ? (discPhysPct * discLaborBudget) / discLaborActual : 0);
            const spi = _discEarly ? 1 : (discPlannedHrs > 0 ? discEarnedHrs / discPlannedHrs : 0);
            const _discPfCodes = codes.map(cc => { const h = getCodeHoursData(cc); return { cc, budgetHrs: h.budgetHrs, earnedHrs: h.earnedHrs, actualHrs: h.actualHrs, pct: h.budgetHrs > 0 ? (h.earnedHrs / h.budgetHrs * 100) : 0 }; });
            const _discCpiCodes = codes.map(cc => { const h = getCodeHoursData(cc); const pp = h.budgetHrs > 0 ? h.earnedHrs / h.budgetHrs : 0; const lbDZ = laborDzDollarsByCode[cc] || 0; const lbCO = laborCoDollarsByCode[cc] || 0; const lb = lbDZ + lbCO; const la = laborActualDollarsByCode[cc] || 0; return { cc, budgetHrs: h.budgetHrs, earnedHrs: h.earnedHrs, physPct: pp, laborBudget: lb, laborBudgetDZ: lbDZ, laborBudgetCO: lbCO, laborActual: la, earnedLabor: pp * lb }; });
            const _discSpiCodes = codes.map(cc => { const h = getCodeHoursData(cc); const ph = plannedHrsByCode[cc] || 0; return { cc, budgetHrs: h.budgetHrs, earnedHrs: h.earnedHrs, plannedHrs: ph, spi: ph > 0 ? h.earnedHrs / ph : 0 }; });
            result[discipline] = {
                _pfDebug: { budgetHrs: discBudgetHrs, earnedHrs: discEarnedHrs, actualHrs: discActualHrs, pctComplete: discBudgetHrs > 0 ? (discEarnedHrs / discBudgetHrs * 100) : 0, codes: _discPfCodes },
                _cpiDebug: { budgetHrs: discBudgetHrs, earnedHrs: discEarnedHrs, physPct: discPhysPct, laborBudget: discLaborBudget, laborBudgetDZ: discLaborBudgetDZ, laborBudgetCO: discLaborBudgetCO, laborActual: discLaborActual, earnedLabor: discPhysPct * discLaborBudget, codes: _discCpiCodes },
                _spiDebug: { budgetHrs: discBudgetHrs, earnedHrs: discEarnedHrs, plannedHrs: discPlannedHrs, pctComplete: discBudgetHrs > 0 ? (discEarnedHrs / discBudgetHrs * 100) : 0, codes: _discSpiCodes },
                k2Budget: '-', bidBudget: fNum(dz, 1), approvedCOs: fNum(co, 1), currentBudget: fNum(cb, 1),
                actualCosts: fNum(ac, 1), openCommit: fNum(oc, 1), totalCommit: fNum(ac + oc, 1),
                etcLabor: fNum(etcL, 1), etcEquipment: fNum(etcEq, 1), etcMaterials: fNum(etcM, 1),
                etcRentals: fNum(etcR, 1), etcSubs: fNum(etcS, 1), etcOther: fNum(etcO, 1),
                etc: fNum(etc, 1), eac: fNum(eac, 1), deltaEACBudget: fNum(eac - cb, 1),
                prevActualCosts: fNum(pAc, 1), prevOpenCommit: fNum(pOc, 1), prevEtcTotal: fNum(pEtc, 1), previousEAC: fNum(pEAC, 1),
                deltaEACPrevious: fNum(eac - pEAC, 1), pf: pf > 0 ? fNum(pf) : '-', cpi: cpi > 0 ? fNum(cpi) : '-', spi: spi > 0 ? fNum(spi) : '-'
            };
        });

        // Contract Amount — use smBudgets (same source as bidForecast/sideMenu) so values always match
        const caDZ = smBudgets.contractValuesRevised || 0;
        const caCO = smBudgets.changeOrderRevenue || 0;
        const caAc = revenueJTD;  // actual revenue billed JTD
        const caCB = caDZ + caCO;
        const caEAC = caCB; // revised contract = EAC for revenue row (matching bidForecast)
        result['Contract Amount'] = {
            k2Budget: '-', bidBudget: fNum(caDZ, 1), approvedCOs: fNum(caCO, 1), currentBudget: fNum(caCB, 1),
            actualCosts: '-', openCommit: '-', totalCommit: '-',
            etcLabor: '-', etcEquipment: '-',
            etcMaterials: '-', etcRentals: '-',
            etcSubs: '-', etcOther: '-',
            etc: '-', eac: fNum(caEAC, 1), deltaEACBudget: '-',
            prevActualCosts: '-', prevOpenCommit: '-', prevEtcTotal: '-', previousEAC: '-',
            deltaEACPrevious: '-', pf: '-', cpi: '-', spi: '-'
        };

        // Contingency
        const contCB = contingencyDayZeroTotal + contingencyCOTotal;
        const contPF = contCB > 0 ? totalcTotal / contCB : 0;
        result['Contingency'] = {
            k2Budget: '-', bidBudget: fNum(contingencyDayZeroTotal, 1), approvedCOs: fNum(contingencyCOTotal, 1), currentBudget: fNum(contCB, 1),
            actualCosts: '$0', openCommit: '$0', totalCommit: '$0',
            etcLabor: fNum(totalcLabor, 1), etcEquipment: fNum(totalcEquipment, 1), etcMaterials: fNum(totalcMaterials, 1),
            etcRentals: '$0', etcSubs: fNum(totalcSubcontractors, 1), etcOther: fNum(totalcOtherExpenses, 1),
            etc: fNum(totalcTotal, 1), eac: fNum(totalcTotal, 1), deltaEACBudget: fNum(totalcTotal - contCB, 1),
            prevActualCosts: '-', prevOpenCommit: '-', prevEtcTotal: '-', previousEAC: '-',
            deltaEACPrevious: '-', pf: contPF > 0 ? fNum(contPF) : '-', cpi: '-', spi: '-'
        };

        // Total Costs — sum across LEMSO cost-type buckets (L/E/M/S/O) plus Contingency.
        // Sourcing from cost-type buckets (rather than per-discipline rollup) captures all
        // 5* activity even when a cost code is missing a Productivity Tracker discipline.
        const dKeys = allDisciplines.filter(d => result[d] && d !== 'Contingency');
        const dKeysWithCont = [...dKeys, ...(result['Contingency'] ? ['Contingency'] : [])];
        const displayedCostCodes = dKeys.flatMap(d => disciplineCostCodes[d] || []);
        const _lemsoKeys = ['L', 'E', 'M', 'S', 'O'];
        const _sumBucket = (byTypeAndCode) => _lemsoKeys.reduce((sum, k) => sum + Object.values(byTypeAndCode[k] || {}).reduce((s, v) => s + (pNum(v) || 0), 0), 0);
        const _sumBucketKey = (byTypeAndCode, k) => Object.values(byTypeAndCode[k] || {}).reduce((s, v) => s + (pNum(v) || 0), 0);
        const _contEtc = result['Contingency'] ? pNum(result['Contingency'].etc || 0) : 0;
        const tAc = _sumBucket(actualByTypeAndCode);
        const tOc = _sumBucket(commitByTypeAndCode);
        const tEtc = _sumBucket(etcByTypeAndCode) + _contEtc;
        const tEtcL = _sumBucketKey(etcByTypeAndCode, 'L') + (result['Contingency'] ? pNum(result['Contingency'].etcLabor || 0) : 0);
        const tEtcEq = _sumBucketKey(etcByTypeAndCode, 'E') + (result['Contingency'] ? pNum(result['Contingency'].etcEquipment || 0) : 0);
        const tEtcM = _sumBucketKey(etcByTypeAndCode, 'M') + (result['Contingency'] ? pNum(result['Contingency'].etcMaterials || 0) : 0);
        const tEtcR = 0;
        const tEtcS = _sumBucketKey(etcByTypeAndCode, 'S') + (result['Contingency'] ? pNum(result['Contingency'].etcSubs || 0) : 0);
        const tEtcO = _sumBucketKey(etcByTypeAndCode, 'O') + (result['Contingency'] ? pNum(result['Contingency'].etcOther || 0) : 0);
        const tDZ = _sumBucket(dzByTypeAndCode) + contingencyDayZeroTotal;
        const tCO = _sumBucket(coByTypeAndCode) + contingencyCOTotal;
        const tCB = tDZ + tCO;
        const tEAC = tAc + tOc + tEtc;
        let tPrevAc = 0, tPrevOc = 0, tPrevEtc = 0;
        displayedCostCodes.forEach(cc => {
            tPrevAc += previousActualCostsByCode[cc] || 0; tPrevOc += previousCommitmentsByCode[cc] || 0; tPrevEtc += previousVersionData[cc] || 0;
        });
        const tPrevEAC = tPrevAc + tPrevOc + tPrevEtc;
        const tCPI = tAc > 0 ? tCB / tAc : 0;
        // Total PF = total earned hours / total actual hours (no summation of per-discipline PFs).
        // VoltaGrid: the quantifiable work only — Indirects' hours earn nothing (vgIndirect).
        const pfCostCodes = isVoltaGrid ? displayedCostCodes.filter(cc => !vgIndirect(disciplineMapping[cc], disciplineCostCodes[disciplineMapping[cc]] || [])) : displayedCostCodes;
        let tActHrs = 0, tEarnedHrs = 0, tBudgetHrs = 0, tLaborBudget = 0, tLaborBudgetDZ = 0, tLaborBudgetCO = 0, tLaborActual = 0, tPlannedHrs = 0;
        pfCostCodes.forEach(cc => {
            const h = getCodeHoursData(cc);
            tActHrs += h.actualHrs; tEarnedHrs += h.earnedHrs; tBudgetHrs += h.budgetHrs;
            tLaborBudgetDZ += laborDzDollarsByCode[cc] || 0;
            tLaborBudgetCO += laborCoDollarsByCode[cc] || 0;
            tLaborBudget += (laborDzDollarsByCode[cc] || 0) + (laborCoDollarsByCode[cc] || 0);
            tLaborActual += laborActualDollarsByCode[cc] || 0;
            tPlannedHrs += plannedHrsByCode[cc] || 0;
        });
        const tPF = (tBudgetHrs > 0 && (tEarnedHrs / tBudgetHrs * 100) < 25) ? 1 : (tActHrs > 0 ? tEarnedHrs / tActHrs : 0);
        const tPhysPct = tBudgetHrs > 0 ? tEarnedHrs / tBudgetHrs : 0;
        const _tEarly = tBudgetHrs > 0 && tPhysPct * 100 < 25;   // same <25% rule as PF
        const tCPICalc = _tEarly ? 1 : (tLaborActual > 0 ? (tPhysPct * tLaborBudget) / tLaborActual : 0);
        const tSPICalc = _tEarly ? 1 : (tPlannedHrs > 0 ? tEarnedHrs / tPlannedHrs : 0);
        const _totalPfCodes = [];
        const _totalCpiCodes = [];
        const _totalSpiCodes = [];
        pfCostCodes.forEach(cc => { const h = getCodeHoursData(cc); _totalPfCodes.push({ cc, disc: disciplineMapping[cc], budgetHrs: h.budgetHrs, earnedHrs: h.earnedHrs, actualHrs: h.actualHrs, pct: h.budgetHrs > 0 ? (h.earnedHrs / h.budgetHrs * 100) : 0 }); const pp = h.budgetHrs > 0 ? h.earnedHrs / h.budgetHrs : 0; const lbDZ = laborDzDollarsByCode[cc] || 0; const lbCO = laborCoDollarsByCode[cc] || 0; const lb = lbDZ + lbCO; const la = laborActualDollarsByCode[cc] || 0; _totalCpiCodes.push({ cc, disc: disciplineMapping[cc], budgetHrs: h.budgetHrs, earnedHrs: h.earnedHrs, physPct: pp, laborBudget: lb, laborBudgetDZ: lbDZ, laborBudgetCO: lbCO, laborActual: la, earnedLabor: pp * lb }); const ph = plannedHrsByCode[cc] || 0; _totalSpiCodes.push({ cc, disc: disciplineMapping[cc], budgetHrs: h.budgetHrs, earnedHrs: h.earnedHrs, plannedHrs: ph, spi: ph > 0 ? h.earnedHrs / ph : 0 }); });
        result['Total Costs'] = {
            _pfDebug: { budgetHrs: tBudgetHrs, earnedHrs: tEarnedHrs, actualHrs: tActHrs, pctComplete: tBudgetHrs > 0 ? (tEarnedHrs / tBudgetHrs * 100) : 0, codes: _totalPfCodes },
            _cpiDebug: { budgetHrs: tBudgetHrs, earnedHrs: tEarnedHrs, physPct: tPhysPct, laborBudget: tLaborBudget, laborBudgetDZ: tLaborBudgetDZ, laborBudgetCO: tLaborBudgetCO, laborActual: tLaborActual, earnedLabor: tPhysPct * tLaborBudget, codes: _totalCpiCodes },
            _spiDebug: { budgetHrs: tBudgetHrs, earnedHrs: tEarnedHrs, plannedHrs: tPlannedHrs, pctComplete: tBudgetHrs > 0 ? (tEarnedHrs / tBudgetHrs * 100) : 0, codes: _totalSpiCodes },
            k2Budget: '-', bidBudget: fNum(tDZ, 1), approvedCOs: fNum(tCO, 1), currentBudget: fNum(tCB, 1),
            actualCosts: fNum(tAc, 1), openCommit: fNum(tOc, 1), totalCommit: fNum(tAc + tOc, 1),
            etcLabor: fNum(tEtcL, 1), etcEquipment: fNum(tEtcEq, 1), etcMaterials: fNum(tEtcM, 1),
            etcRentals: fNum(tEtcR, 1), etcSubs: fNum(tEtcS, 1), etcOther: fNum(tEtcO, 1),
            etc: fNum(tEtc, 1), eac: fNum(tEAC, 1), deltaEACBudget: fNum(tEAC - tCB, 1),
            prevActualCosts: fNum(tPrevAc, 1), prevOpenCommit: fNum(tPrevOc, 1), prevEtcTotal: fNum(tPrevEtc, 1), previousEAC: fNum(tPrevEAC, 1),
            deltaEACPrevious: fNum(tEAC - tPrevEAC, 1), pf: tPF > 0 ? fNum(tPF) : '-', cpi: tCPICalc > 0 ? fNum(tCPICalc) : '-', spi: tSPICalc > 0 ? fNum(tSPICalc) : '-'
        };

        // ═══════════ LEMSO AGGREGATION ═══════════
        // Build per-code-per-type and category-level aggregation for LEMSO pivot
        const lemsoCategories = [
            { key: 'L', label: 'Labor', etcField: 'etcLabor' },
            { key: 'E', label: 'Equipment', etcField: 'etcEquipment', etcField2: 'etcRentals' },
            { key: 'M', label: 'Materials', etcField: 'etcMaterials' },
            { key: 'S', label: 'Subcontractors', etcField: 'etcSubs' },
            { key: 'O', label: 'Other', etcField: 'etcOther' },
        ];
        const lemsoCodes = {}; // { L: Set, E: Set, ... }
        lemsoCategories.forEach(({ key }) => { lemsoCodes[key] = new Set(); });
        // Collect all cost codes that have activity in each LEMSO category
        const allTrackingCodes = new Set([
            ...Object.keys(disciplineMapping).filter(cc => cc !== '101101'),
        ]);
        allTrackingCodes.forEach(cc => {
            lemsoCategories.forEach(({ key }) => {
                const hasActual = (actualByTypeAndCode[key][cc] || 0) !== 0;
                const hasCommit = (commitByTypeAndCode[key][cc] || 0) !== 0;
                const hasDZ = (dzByTypeAndCode[key][cc] || 0) !== 0;
                const hasCO = (coByTypeAndCode[key][cc] || 0) !== 0;
                // Use cost-type-based ETC tracking (etcByTypeAndCode) instead of tab-based fields
                // so that 52300/52400 on Other tab → E, 51800 on Other tab → L, etc.
                const hasEtc = (etcByTypeAndCode[key][cc] || 0) !== 0;
                if (hasActual || hasCommit || hasDZ || hasCO || hasEtc) lemsoCodes[key].add(cc);
            });
        });
        // Per-code-per-type result entries
        lemsoCategories.forEach(({ key }) => {
            lemsoCodes[key].forEach(cc => {
                const cd = costCodeData[cc] || { etc: 0, etcLabor: 0, etcEquipment: 0, etcMaterials: 0, etcRentals: 0, etcSubs: 0, etcOther: 0, dZ: [], cos: 0 };
                const ac = actualByTypeAndCode[key][cc] || 0;
                const oc = commitByTypeAndCode[key][cc] || 0;
                const dz = dzByTypeAndCode[key][cc] || 0;
                const co = coByTypeAndCode[key][cc] || 0;
                const etcAmt = etcByTypeAndCode[key][cc] || 0;
                const cb = dz + co, eac = ac + oc + etcAmt;
                const _pAc = previousActualByTypeAndCode[key][cc] || 0;
                const _pOc = previousCommitByTypeAndCode[key][cc] || 0;
                const _pEtc = previousEtcByTypeAndCode[key][cc] || 0;
                const _pEAC = _pAc + _pOc + _pEtc;
                result[`lemsoCode_${key}_${cc}`] = {
                    k2Budget: '-', bidBudget: fNum(dz, 1), approvedCOs: fNum(co, 1), currentBudget: fNum(cb, 1),
                    actualCosts: fNum(ac, 1), openCommit: fNum(oc, 1), totalCommit: fNum(ac + oc, 1),
                    etcLabor: key === 'L' ? fNum(etcAmt, 1) : '-',
                    etcEquipment: key === 'E' ? fNum(etcAmt, 1) : '-',
                    etcMaterials: key === 'M' ? fNum(etcAmt, 1) : '-',
                    etcRentals: '-', etcSubs: key === 'S' ? fNum(etcAmt, 1) : '-',
                    etcOther: key === 'O' ? fNum(etcAmt, 1) : '-',
                    etc: fNum(etcAmt, 1), eac: fNum(eac, 1), deltaEACBudget: fNum(eac - cb, 1),
                    prevActualCosts: fNum(_pAc, 1), prevOpenCommit: fNum(_pOc, 1), prevEtcTotal: fNum(_pEtc, 1), previousEAC: fNum(_pEAC, 1),
                    deltaEACPrevious: fNum(eac - _pEAC, 1), pf: '-', cpi: '-', spi: '-'
                };
            });
        });
        // Category-level aggregation (summary rows)
        lemsoCategories.forEach(({ key, label }) => {
            let ac = 0, oc = 0, dz = 0, co = 0, etcAmt = 0;
            let _catPAc = 0, _catPOc = 0, _catPEtc = 0;
            lemsoCodes[key].forEach(cc => {
                ac += actualByTypeAndCode[key][cc] || 0;
                oc += commitByTypeAndCode[key][cc] || 0;
                dz += dzByTypeAndCode[key][cc] || 0;
                co += coByTypeAndCode[key][cc] || 0;
                etcAmt += etcByTypeAndCode[key][cc] || 0;
                _catPAc += previousActualByTypeAndCode[key][cc] || 0;
                _catPOc += previousCommitByTypeAndCode[key][cc] || 0;
                _catPEtc += previousEtcByTypeAndCode[key][cc] || 0;
            });
            const cb = dz + co, eac = ac + oc + etcAmt;
            const _catPEAC = _catPAc + _catPOc + _catPEtc;
            result[`lemso_${key}`] = {
                k2Budget: '-', bidBudget: fNum(dz, 1), approvedCOs: fNum(co, 1), currentBudget: fNum(cb, 1),
                actualCosts: fNum(ac, 1), openCommit: fNum(oc, 1), totalCommit: fNum(ac + oc, 1),
                etcLabor: key === 'L' ? fNum(etcAmt, 1) : '-', etcEquipment: key === 'E' ? fNum(etcAmt, 1) : '-',
                etcMaterials: key === 'M' ? fNum(etcAmt, 1) : '-', etcRentals: '-',
                etcSubs: key === 'S' ? fNum(etcAmt, 1) : '-', etcOther: key === 'O' ? fNum(etcAmt, 1) : '-',
                etc: fNum(etcAmt, 1), eac: fNum(eac, 1), deltaEACBudget: fNum(eac - cb, 1),
                prevActualCosts: fNum(_catPAc, 1), prevOpenCommit: fNum(_catPOc, 1), prevEtcTotal: fNum(_catPEtc, 1), previousEAC: fNum(_catPEAC, 1),
                deltaEACPrevious: fNum(eac - _catPEAC, 1), pf: '-', cpi: '-', spi: '-'
            };
        });
        // Store LEMSO code lists for row generation
        result._lemsoCodes = {};
        lemsoCategories.forEach(({ key }) => { result._lemsoCodes[key] = [...lemsoCodes[key]].sort(); });

        if (k2Data) { result['Contract Amount'].k2Budget = fNum(k2Data.Estimated_Project_Amount || 0, 1); result['Total Costs'].k2Budget = fNum(k2Data.Expected_Cost || 0, 1); }
        // Expose overhead & revenue meta for GM calculation (matching sidemenu)
        const hasPlannedHours = Object.values(plannedHrsByCode).some(v => v > 0);
        result._meta = { ohJTD, ohCommitted, revenueJTD, revenueJTDPrevPeriod, overheadDayZeroBudget, hasPlannedHours };
        // Tag with job ID so prompt builder can verify data belongs to current job
        result._jobId = selectedJobDetails?.BU_ID || null;
        return result;
    }, [jobUserData, jobUserDataPrev, jobCostDetail, jobOpenCommitments, dayZero, selectedJobDetails, k2Data, jobCostDetailPrev, jobOpenCommitmentsPrev, versionInfo, smBudgets, jobCostDetailRaw, prevVersion, projectComplete, isVoltaGrid]);

    // Override Δ EAC Previous on the LEMSO category rows and Contingency with the WIP variance
    // API values (same source as the Forecast tab's "Delta from Prev WIP" and the Total Costs
    // wipCostVar override) — the client-side prev-version recompute can't reproduce the WIP
    // snapshot, so without this the category rows disagree with the Forecast tab.
    const budgetDataWithWipDeltas = useMemo(() => {
        // A picked version has no WIP snapshot behind it: the recompute stands.
        if (!calculatedBudgetData || prevCustom) return calculatedBudgetData;
        const out = { ...calculatedBudgetData };
        const applyWip = (rowKey, prevAmt) => {
            const row = out[rowKey];
            if (!row || row.eac == null || row.eac === '-') return;
            out[rowKey] = { ...row, previousEAC: fNum(pNum(prevAmt), 1), deltaEACPrevious: fNum(pNum(row.eac) - pNum(prevAmt), 1) };
        };
        if (laborDeltaWip) applyWip('lemso_L', laborDeltaWip);
        if (equipmentDeltaWip) applyWip('lemso_E', equipmentDeltaWip);
        if (materialsDeltaWip) applyWip('lemso_M', materialsDeltaWip);
        if (subcontractorsDeltaWip) applyWip('lemso_S', subcontractorsDeltaWip);
        if (otherDeltaWip) applyWip('lemso_O', otherDeltaWip);
        if (contingencyDeltaWip != null) applyWip('Contingency', contingencyDeltaWip);
        return out;
    }, [calculatedBudgetData, prevCustom, laborDeltaWip, equipmentDeltaWip, materialsDeltaWip, subcontractorsDeltaWip, otherDeltaWip, contingencyDeltaWip]);

    // VoltaGrid: the Contract Amount row reads as Revenue (Billable). To date
    // is the Billing tab's billable amount — summaries at their frozen
    // amounts, unbilled lines at the rate sheet / markup, fuel surcharge
    // included (billingPricing's billableRevenue). The Bid column is the
    // version's Revenue Budget (helpers/revenueBudget: bid revenue by cost
    // code x cost type) where it has one; otherwise the cost budget priced at
    // the bid's bill-to-cost ratio. EAC projects the cost ETC at each
    // section's bill-to-cost ratio on the billable lines.
    const revenueBudget = useMemo(
        () => (isVoltaGrid ? revenueBudgetByCode(jobUserData, selectedVersion) : null),
        [isVoltaGrid, jobUserData, selectedVersion],
    );
    const budgetDataForTab = useMemo(() => {
        const base = budgetDataWithWipDeltas;
        if (!isVoltaGrid || !base || !base['Contract Amount']) return base;
        const ready = !!billRev.ready;
        const jtd = ready ? billRev.bill : 0;
        // The Revenue Budget by cost code, on the grid's own code keys.
        const revByCc = {};
        const hasRevBudget = !!(revenueBudget && revenueBudget.has);
        if (hasRevBudget) Object.entries(revenueBudget.byCode).forEach(([cc, amt]) => { const k = normCC(cc); revByCc[k] = (revByCc[k] || 0) + amt; });
        const caCOs = pNum(base['Contract Amount']?.approvedCOs || 0);
        // Bill-to-cost ratio for the ETC: the bid's own ratio (revenue / cost
        // budget) once a budget is loaded — early in the job the billed-to-date ratio
        // swings with whatever happened to be billed first — else billed / cost so far.
        const bidRev = hasRevBudget ? revenueBudget.total + caCOs : pNum(base['Contract Amount']?.currentBudget || 0);
        const bidCost = pNum(base['Total Costs']?.currentBudget || 0);
        const ratio = bidRev > 0 && bidCost > 0 ? bidRev / bidCost : (ready && billRev.cost > 0 ? billRev.bill / billRev.cost : 0);
        // Cost ETC and open commitments both bill at the ratio: the cost EAC is
        // actual + commit + ETC, so the revenue EAC is billable to date + the
        // rest of that at the bid's bill-to-cost ratio.
        const dash = '-';

        // Billable to date by cost code, off the same priced lines the total
        // comes from, so the discipline rows foot to the Revenue row.
        const billByCc = {};
        if (ready) {
            (billRev.txns || []).forEach((t) => {
                if (t.nb) return;
                const cc = normCC(String(t.cc || ''));
                billByCc[cc] = (billByCc[cc] || 0) + pNum(t.bill) + fuelLineAmt(t, billRev.fuelCtx);
            });
        }
        // Cost codes by discipline, off the discipline row itself: on VoltaGrid
        // only Equipment / Electrical have rows under them (WBS groups).
        const codesOf = (discipline) => {
            const row = budgetRows.find((r) => r.level === 0 && r.discipline === discipline);
            return row && row.codes ? row.codes : [];
        };
        // The bid's price for a discipline and its own bill-to-cost ratio —
        // bid price / cost budget — so every section scales on its own
        // margin rather than the job average. The price is the Revenue
        // Budget on the discipline's codes; without one, Block 4 reads its
        // bid table (voltaGridBidSummary: labor + material + subcontract).
        // Falls back to the job ratio for a discipline with no price.
        const bidPriceOf = (discipline) => {
            if (hasRevBudget) {
                const codes = codesOf(discipline);
                return codes.length ? codes.reduce((a, cc) => a + (revByCc[normCC(cc)] || 0), 0) : null;
            }
            // Block 4's bid table only prices Block 4; any other VoltaGrid job
            // falls through to its own Day Zero revenue / cost ratio.
            if (String(selectedJob || '').trim() !== BID_JOB) return null;
            const sec = BID_SECTIONS.find((x) => x.name.toLowerCase() === String(discipline || '').toLowerCase());
            return sec ? sec.labor + sec.material + sec.sub : null;
        };
        const ratioOf = {};
        Object.keys(base).forEach((key) => {
            if (key.startsWith('_') || key.startsWith('costCode_') || key === 'Contract Amount' || key === 'Total Costs' || key === 'Contingency') return;
            const price = bidPriceOf(key);
            // The Revenue Budget is the Day Zero's counterpart, so it prices
            // against the Day Zero cost; approved COs then ride the ratio.
            const costCb = pNum(base[key]?.[hasRevBudget ? 'bidBudget' : 'currentBudget'] || 0);
            ratioOf[key] = price != null && costCb > 0 ? price / costCb : ratio;
        });
        const disciplineOfCc = {};
        budgetRows.forEach((r) => {
            if (r.level === 0 && r.discipline) (r.codes || []).forEach((cc) => { disciplineOfCc[cc] = r.discipline; });
        });
        // A cost row re-expressed as revenue: bid, COs and commitments at the
        // ratio; actual = what the cost to date bills for. The billable EAC is
        // the current budget — the same rule the Revenue row uses — so the ETC
        // is what is left of the budget after billed-to-date and open
        // commitments, and Δ EAC Budget is zero (Aeron, 10/8). The ETC
        // breakdown is the cost ETC scaled to that remainder. The indexes
        // (PF / CPI / SPI) read on hours and labor cost, not billable
        // dollars, so the cost row's values stay.
        const asRevenue = (row, billJtd, r, bidOverride) => {
            const x = (k) => pNum(row?.[k] || 0) * r;
            const bid = bidOverride != null ? bidOverride : x('bidBudget'), co = x('approvedCOs'), cb = bid + co;
            const oc = x('openCommit');
            const rEac = cb;
            const rEtc = ready ? Math.max(0, rEac - billJtd - oc) : x('etc');
            const costEtc = x('etc');
            const scale = costEtc > 0 ? rEtc / costEtc : 0;
            const part = (k) => fNum(x(k) * scale, 1);
            // Unrounded, so a group row foots to its discipline: summing the
            // rounded code figures drifted a few dollars per group.
            const _sum = { bidBudget: bid, approvedCOs: co, currentBudget: cb, actualCosts: billJtd, openCommit: oc, totalCommit: billJtd + oc, etc: rEtc, eac: rEac, deltaEACBudget: 0 };
            ['etcLabor', 'etcEquipment', 'etcMaterials', 'etcRentals', 'etcSubs', 'etcOther'].forEach((k) => { _sum[k] = x(k) * scale; });
            return {
                ...row,
                _sum,
                bidBudget: fNum(bid, 1), approvedCOs: fNum(co, 1), currentBudget: fNum(cb, 1),
                actualCosts: ready ? fNum(billJtd, 1) : dash, openCommit: fNum(oc, 1), totalCommit: ready ? fNum(billJtd + oc, 1) : dash,
                etcLabor: part('etcLabor'), etcEquipment: part('etcEquipment'), etcMaterials: part('etcMaterials'),
                etcRentals: part('etcRentals'), etcSubs: part('etcSubs'), etcOther: part('etcOther'),
                etc: fNum(rEtc, 1), eac: ready ? fNum(rEac, 1) : dash, deltaEACBudget: ready ? fNum(0, 1) : dash,
                prevActualCosts: dash, prevOpenCommit: dash, prevEtcTotal: dash, previousEAC: dash, deltaEACPrevious: dash,
                pf: row?.pf || dash, cpi: row?.cpi || dash, spi: row?.spi || dash,
            };
        };
        const revenueRows = {};
        const disciplineKeys = new Set(budgetRows.filter((r) => r.level === 0 && r.discipline).map((r) => r.discipline));
        Object.keys(base).forEach((key) => {
            if (key.startsWith('_') || key === 'Contract Amount' || key === 'Total Costs') return;
            if (key.startsWith('lemso_') || key.startsWith('lemsoCode_')) return;
            if (!key.startsWith('costCode_') && key !== 'Contingency' && !disciplineKeys.has(key)) return;
            const row = base[key];
            if (!row || typeof row !== 'object') return;
            let billJtd = 0;
            let r = ratio, bidOverride = null;
            if (key.startsWith('costCode_')) {
                const cc = key.slice('costCode_'.length);
                billJtd = billByCc[normCC(cc)] || 0;
                r = ratioOf[disciplineOfCc[cc]] ?? ratio;
                if (hasRevBudget) bidOverride = revByCc[normCC(cc)] || 0;
            } else if (key !== 'Contingency') {
                codesOf(key).forEach((cc) => { billJtd += billByCc[normCC(cc)] || 0; });
                r = ratioOf[key] ?? ratio;
                bidOverride = bidPriceOf(key);
            }
            revenueRows[key] = asRevenue(row, billJtd, r, bidOverride);
        });
        // Group rows (Equipment / Electrical children): the sum of their cost
        // codes on the revenue basis.
        const SUM_FIELDS = ['bidBudget', 'approvedCOs', 'currentBudget', 'actualCosts', 'openCommit', 'totalCommit', 'etcLabor', 'etcEquipment', 'etcMaterials', 'etcRentals', 'etcSubs', 'etcOther', 'etc', 'eac', 'deltaEACBudget'];
        budgetRows.filter((r) => r.dataKey && r.groupCodes).forEach((r) => {
            const tot = {};
            SUM_FIELDS.forEach((f) => { tot[f] = 0; });
            r.groupCodes.forEach((cc) => {
                const row = revenueRows[`costCode_${cc}`];
                if (!row) return;
                SUM_FIELDS.forEach((f) => { tot[f] += row._sum ? row._sum[f] : pNum(row[f] || 0); });
            });
            // The group's indexes, off the hours and labor dollars of its codes
            // (the same arithmetic the discipline rows use).
            const h = { budgetHrs: 0, earnedHrs: 0, actualHrs: 0, laborBudget: 0, laborActual: 0, plannedHrs: 0 };
            r.groupCodes.forEach((cc) => {
                const row = base[`costCode_${cc}`];
                if (!row) return;
                h.budgetHrs += pNum(row._pfDebug?.budgetHrs || 0); h.earnedHrs += pNum(row._pfDebug?.earnedHrs || 0); h.actualHrs += pNum(row._pfDebug?.actualHrs || 0);
                h.laborBudget += pNum(row._cpiDebug?.laborBudget || 0); h.laborActual += pNum(row._cpiDebug?.laborActual || 0);
                h.plannedHrs += pNum(row._spiDebug?.plannedHrs || 0);
            });
            const gPct = h.budgetHrs > 0 ? h.earnedHrs / h.budgetHrs : 0;
            const gPf = (h.budgetHrs > 0 && gPct * 100 < 25) ? 1 : (h.actualHrs > 0 ? h.earnedHrs / h.actualHrs : 0);
            const gEarly = h.budgetHrs > 0 && gPct * 100 < 25;
            const gCpi = gEarly ? 1 : (h.laborActual > 0 ? (gPct * h.laborBudget) / h.laborActual : 0);
            const gSpi = gEarly ? 1 : (h.plannedHrs > 0 ? h.earnedHrs / h.plannedHrs : 0);
            const out = { k2Budget: dash, prevActualCosts: dash, prevOpenCommit: dash, prevEtcTotal: dash, previousEAC: dash, deltaEACPrevious: dash,
                pf: gPf > 0 ? fNum(gPf) : dash, cpi: gCpi > 0 ? fNum(gCpi) : dash, spi: gSpi > 0 ? fNum(gSpi) : dash };
            SUM_FIELDS.forEach((f) => { out[f] = ready || !/^(actualCosts|totalCommit|eac|deltaEACBudget)$/.test(f) ? fNum(tot[f], 1) : dash; });
            // Nothing in it: no dollars and no hours. The table leaves such a
            // group out, so a code the job retired, still holding its tracker
            // discipline row, doesn't keep an empty row open (Conduit – UG on
            // 54026071 once 120616 read as 220616, Seth 10/23).
            out._empty = ready && SUM_FIELDS.every((f) => Math.abs(tot[f]) < 0.5) && !h.budgetHrs && !h.actualHrs;
            revenueRows[r.dataKey] = out;
        });
        // The revenue row foots to what is under it: every discipline's EAC
        // on its own ratio.
        // For now the revenue EAC is the bid (contract) amount: billable to
        // date plus what's left of the bid. The per-discipline EACs stay on
        // their own ratios for the rows beneath.
        const eac = bidRev;
        const etc = Math.max(0, eac - jtd);
        // Revenue Budget dollars on codes the grid has no row for (no cost
        // budget, no cost yet) are in the Revenue total but in no section.
        const offGrid = hasRevBudget ? Object.keys(revByCc).filter((cc) => revByCc[cc] && !disciplineOfCc[cc]).map((cc) => ({ cc, amount: revByCc[cc] })) : [];
        return {
            ...base,
            ...revenueRows,
            'Contract Amount': {
                ...base['Contract Amount'],
                // Bid revenue stays visible — the Revenue Budget, else the Day Zero
                // 41000 row; the rest is the billing EAC.
                ...(hasRevBudget
                    ? { bidBudget: fNum(revenueBudget.total, 1), approvedCOs: fNum(caCOs, 1), currentBudget: fNum(revenueBudget.total + caCOs, 1) }
                    : { bidBudget: base['Contract Amount'].bidBudget, approvedCOs: base['Contract Amount'].approvedCOs, currentBudget: base['Contract Amount'].currentBudget }),
                actualCosts: ready ? fNum(jtd, 1) : dash,
                openCommit: dash, totalCommit: dash,
                etc: ready ? fNum(etc, 1) : dash,
                eac: ready ? fNum(eac, 1) : dash,
                deltaEACBudget: dash,
                // The job's indexes ride on the top row: the Total Costs row
                // that carries them is hidden on the client view.
                pf: base['Total Costs']?.pf || dash, cpi: base['Total Costs']?.cpi || dash, spi: base['Total Costs']?.spi || dash,
                _pfDebug: base['Total Costs']?._pfDebug, _cpiDebug: base['Total Costs']?._cpiDebug, _spiDebug: base['Total Costs']?._spiDebug,
                _revenue: ready ? { jtd, etc, eac, ratio, billed: billRev.billed, unbilled: billRev.unbilled, fuel: billRev.fuel, cost: billRev.cost, nbCost: billRev.nbCost, noRate: billRev.noRate } : null,
                _revenueBudget: hasRevBudget ? { total: revenueBudget.total, lines: revenueBudget.lines.length, offGrid } : null,
            },
        };
    }, [isVoltaGrid, budgetDataWithWipDeltas, billRev, budgetRows, selectedJob, revenueBudget]);

    useEffect(() => { setBudgetData(budgetDataForTab); }, [budgetDataForTab]);

    // Classify cost codes into LEMSO categories based on dominant cost type
    const costCodeCostTypeMap = useMemo(() => {
        const costsByType = {};
        if (jobUserData) {
            jobUserData.forEach(item => {
                if (item.Version_GUID === selectedVersion && item.Forecast_Tab === 'Day Zero Budget' && item.Cost_Code && item.Cost_Type) {
                    const ct = item.Cost_Type.toString().substring(0, 2);
                    if (ct.startsWith('5')) {
                        const cc = item.Cost_Code;
                        if (!costsByType[cc]) costsByType[cc] = {};
                        costsByType[cc][ct] = (costsByType[cc][ct] || 0) + Math.abs(pNum(item.Value_Amount || 0));
                    }
                }
            });
        }
        if (Array.isArray(jobCostDetail)) {
            jobCostDetail.forEach(item => {
                if (item.Phase_Code && item.Cost_Type_Code && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    const ct = item.Cost_Type_Code.toString().substring(0, 2);
                    if (ct.startsWith('5')) {
                        const cc = item.Phase_Code;
                        if (!costsByType[cc]) costsByType[cc] = {};
                        costsByType[cc][ct] = (costsByType[cc][ct] || 0) + Math.abs(pNum(item.Job_Cost || 0));
                    }
                }
            });
        }
        const map = {};
        Object.keys(costsByType).forEach(costCode => {
            const types = costsByType[costCode];
            let maxType = null, maxAmount = 0;
            Object.keys(types).forEach(ct => {
                if (types[ct] > maxAmount) { maxAmount = types[ct]; maxType = ct; }
            });
            if (maxType) {
                if (maxType === '51') map[costCode] = 'L';
                else if (maxType === '52') map[costCode] = 'E';
                else if (maxType === '53') map[costCode] = 'M';
                else if (maxType === '54') map[costCode] = 'S';
                else map[costCode] = 'O';
            }
        });
        return map;
    }, [jobUserData, jobCostDetail, selectedVersion]);

    // Filtered budget rows and data based on active LEMSO tab
    const filteredBudgetView = useMemo(() => {
        if (budgetPivot === 'phase') {
            return { rows: budgetRows, data: budgetData };
        }

        const aggregateCostCodes = (matchingCostCodes) => {
            const disciplineGroups = {};
            budgetRows.forEach(row => {
                if (row.level === 1 && row.costCode && matchingCostCodes.has(row.costCode)) {
                    if (!disciplineGroups[row.discipline]) disciplineGroups[row.discipline] = [];
                    disciplineGroups[row.discipline].push(row);
                }
            });

            const computeSubtotal = (codeList) => {
                let bid = 0, cos = 0, actual = 0, commit = 0, etc = 0;
                let etcL = 0, etcE = 0, etcM = 0, etcR = 0, etcS = 0, etcO = 0;
                let pAc = 0, pOc = 0, pEtc = 0;
                codeList.forEach(cc => {
                    const d = budgetData[`costCode_${cc}`];
                    if (d) {
                        bid += pNum(d.bidBudget); cos += pNum(d.approvedCOs);
                        actual += pNum(d.actualCosts); commit += pNum(d.openCommit);
                        etc += pNum(d.etc);
                        etcL += pNum(d.etcLabor); etcE += pNum(d.etcEquipment);
                        etcM += pNum(d.etcMaterials); etcR += pNum(d.etcRentals);
                        etcS += pNum(d.etcSubs); etcO += pNum(d.etcOther);
                        pAc += pNum(d.prevActualCosts); pOc += pNum(d.prevOpenCommit); pEtc += pNum(d.prevEtcTotal);
                    }
                });
                const curBudget = bid + cos;
                const eac = actual + commit + etc;
                const pEAC = pAc + pOc + pEtc;
                return {
                    k2Budget: '-', bidBudget: fNum(bid, 1), approvedCOs: fNum(cos, 1),
                    currentBudget: fNum(curBudget, 1), actualCosts: fNum(actual, 1),
                    openCommit: fNum(commit, 1), totalCommit: fNum(actual + commit, 1),
                    etcLabor: fNum(etcL, 1), etcEquipment: fNum(etcE, 1),
                    etcMaterials: fNum(etcM, 1), etcRentals: fNum(etcR, 1),
                    etcSubs: fNum(etcS, 1), etcOther: fNum(etcO, 1),
                    etc: fNum(etc, 1), eac: fNum(eac, 1),
                    deltaEACBudget: fNum(eac - curBudget, 1),
                    prevActualCosts: fNum(pAc, 1), prevOpenCommit: fNum(pOc, 1), prevEtcTotal: fNum(pEtc, 1), previousEAC: fNum(pEAC, 1),
                    deltaEACPrevious: fNum(eac - pEAC, 1),
                    pf: '-', cpi: '-', spi: '-'
                };
            };

            return { disciplineGroups, computeSubtotal };
        };

        if (['L', 'E', 'M', 'S', 'O'].includes(budgetPivot)) {
            // Use _lemsoCodes which correctly tracks cost codes by cost type (51*=L, 52*=E, 53*=M, 54*=S, other=O)
            const matchingCostCodes = new Set(budgetData?._lemsoCodes?.[budgetPivot] || []);
            const { disciplineGroups } = aggregateCostCodes(matchingCostCodes);
            // Ensure all disciplines from budgetRows appear, even if they have no cost codes in this category
            const allDisciplines = budgetRows.filter(r => r.level === 0 && r.discipline).map(r => r.discipline);
            allDisciplines.forEach(d => { if (!disciplineGroups[d]) disciplineGroups[d] = []; });
            const data = { ...budgetData };
            const rows = [{ label: "Contract Amount", level: 0 }];

            // Use per-cost-type data (lemsoCode_${key}_${cc}) instead of aggregate costCode_ entries
            const computeSubtotalByType = (codeList) => {
                let bid = 0, cos = 0, actual = 0, commit = 0, etc = 0;
                let etcL = 0, etcE = 0, etcM = 0, etcR = 0, etcS = 0, etcO = 0;
                let pAc = 0, pOc = 0, pEtc = 0;
                codeList.forEach(cc => {
                    const d = budgetData[`lemsoCode_${budgetPivot}_${cc}`];
                    if (d) {
                        bid += pNum(d.bidBudget); cos += pNum(d.approvedCOs);
                        actual += pNum(d.actualCosts); commit += pNum(d.openCommit);
                        etc += pNum(d.etc);
                        etcL += pNum(d.etcLabor); etcE += pNum(d.etcEquipment);
                        etcM += pNum(d.etcMaterials); etcR += pNum(d.etcRentals);
                        etcS += pNum(d.etcSubs); etcO += pNum(d.etcOther);
                        pAc += pNum(d.prevActualCosts); pOc += pNum(d.prevOpenCommit); pEtc += pNum(d.prevEtcTotal);
                    }
                });
                const curBudget = bid + cos;
                const eac = actual + commit + etc;
                const pEAC = pAc + pOc + pEtc;
                return {
                    k2Budget: '-', bidBudget: fNum(bid, 1), approvedCOs: fNum(cos, 1),
                    currentBudget: fNum(curBudget, 1), actualCosts: fNum(actual, 1),
                    openCommit: fNum(commit, 1), totalCommit: fNum(actual + commit, 1),
                    etcLabor: fNum(etcL, 1), etcEquipment: fNum(etcE, 1),
                    etcMaterials: fNum(etcM, 1), etcRentals: fNum(etcR, 1),
                    etcSubs: fNum(etcS, 1), etcOther: fNum(etcO, 1),
                    etc: fNum(etc, 1), eac: fNum(eac, 1),
                    deltaEACBudget: fNum(eac - curBudget, 1),
                    prevActualCosts: fNum(pAc, 1), prevOpenCommit: fNum(pOc, 1), prevEtcTotal: fNum(pEtc, 1), previousEAC: fNum(pEAC, 1),
                    deltaEACPrevious: fNum(eac - pEAC, 1),
                    pf: '-', cpi: '-', spi: '-'
                };
            };

            Object.keys(disciplineGroups).sort().forEach(disc => {
                if (disc !== 'Other') {
                    const codes = disciplineGroups[disc].map(r => r.costCode);
                    data[disc] = computeSubtotalByType(codes);
                    rows.push({ label: disc, level: 0, discipline: disc });
                    disciplineGroups[disc].sort((a, b) => a.costCode.localeCompare(b.costCode)).forEach(r => {
                        // Point cost code rows to per-type data
                        rows.push({ ...r, dataKey: `lemsoCode_${budgetPivot}_${r.costCode}` });
                    });
                }
            });
            {
                if (!disciplineGroups['Other']) disciplineGroups['Other'] = [];
                const codes = disciplineGroups['Other'].map(r => r.costCode);
                data['Other'] = computeSubtotalByType(codes);
                rows.push({ label: 'Other', level: 0, discipline: 'Other' });
                disciplineGroups['Other'].sort((a, b) => a.costCode.localeCompare(b.costCode)).forEach(r => {
                    rows.push({ ...r, dataKey: `lemsoCode_${budgetPivot}_${r.costCode}` });
                });
            }
            data['Total Costs'] = computeSubtotalByType([...matchingCostCodes]);
            rows.push({ label: "Contingency", level: 0 });
            rows.push({ label: "Total Costs", level: 0 });
            return { rows, data };
        }

        if (budgetPivot === 'lemso') {
            const data = { ...budgetData };
            const rows = [{ label: "Contract Amount", level: 0 }];
            const categories = [
                { key: 'L', label: 'Labor' }, { key: 'E', label: 'Equipment' },
                { key: 'M', label: 'Materials' }, { key: 'S', label: 'Subcontractors' },
                { key: 'O', label: 'Other Expenses' }
            ];
            let grandBid = 0, grandCOs = 0, grandActual = 0, grandCommit = 0, grandEtc = 0;
            let grandPrevAc = 0, grandPrevOc = 0, grandPrevEtc = 0;

            // Per-type subtotal using lemsoCode_ entries instead of aggregate costCode_ entries
            const computeSubtotalByTypeFor = (catKey, codeList) => {
                let bid = 0, cos = 0, actual = 0, commit = 0, etc = 0;
                let etcL = 0, etcE = 0, etcM = 0, etcR = 0, etcS = 0, etcO = 0;
                let pAc = 0, pOc = 0, pEtc = 0;
                codeList.forEach(cc => {
                    const d = budgetData[`lemsoCode_${catKey}_${cc}`];
                    if (d) {
                        bid += pNum(d.bidBudget); cos += pNum(d.approvedCOs);
                        actual += pNum(d.actualCosts); commit += pNum(d.openCommit);
                        etc += pNum(d.etc);
                        etcL += pNum(d.etcLabor); etcE += pNum(d.etcEquipment);
                        etcM += pNum(d.etcMaterials); etcR += pNum(d.etcRentals);
                        etcS += pNum(d.etcSubs); etcO += pNum(d.etcOther);
                        pAc += pNum(d.prevActualCosts); pOc += pNum(d.prevOpenCommit); pEtc += pNum(d.prevEtcTotal);
                    }
                });
                const curBudget = bid + cos;
                const eac = actual + commit + etc;
                const pEAC = pAc + pOc + pEtc;
                return {
                    k2Budget: '-', bidBudget: fNum(bid, 1), approvedCOs: fNum(cos, 1),
                    currentBudget: fNum(curBudget, 1), actualCosts: fNum(actual, 1),
                    openCommit: fNum(commit, 1), totalCommit: fNum(actual + commit, 1),
                    etcLabor: fNum(etcL, 1), etcEquipment: fNum(etcE, 1),
                    etcMaterials: fNum(etcM, 1), etcRentals: fNum(etcR, 1),
                    etcSubs: fNum(etcS, 1), etcOther: fNum(etcO, 1),
                    etc: fNum(etc, 1), eac: fNum(eac, 1),
                    deltaEACBudget: fNum(eac - curBudget, 1),
                    prevActualCosts: fNum(pAc, 1), prevOpenCommit: fNum(pOc, 1), prevEtcTotal: fNum(pEtc, 1), previousEAC: fNum(pEAC, 1),
                    deltaEACPrevious: fNum(eac - pEAC, 1),
                    pf: '-', cpi: '-', spi: '-'
                };
            };

            categories.forEach(cat => {
                // Use _lemsoCodes which correctly tracks cost codes by cost type (51*=L, 52*=E, etc.)
                const matchingCostCodes = new Set(budgetData?._lemsoCodes?.[cat.key] || []);
                if (matchingCostCodes.size === 0) {
                    // Still show the category header even if empty
                    const catKey = `lemso_${cat.key}`;
                    data[catKey] = computeSubtotalByTypeFor(cat.key, []);
                    rows.push({ label: cat.label, level: 0, lemsoCategory: cat.key, dataKey: catKey });
                    // Show all disciplines as empty
                    const allDisciplines = budgetRows.filter(r => r.level === 0 && r.discipline).map(r => r.discipline);
                    allDisciplines.forEach(disc => {
                        const discKey = `lemso_${cat.key}_${disc}`;
                        data[discKey] = computeSubtotalByTypeFor(cat.key, []);
                        rows.push({ label: disc, level: 1, discipline: disc, lemsoCategory: cat.key, dataKey: discKey });
                    });
                    return;
                }
                const { disciplineGroups } = aggregateCostCodes(matchingCostCodes);
                // Ensure all disciplines from budgetRows appear in each LEMSO category
                const allDisciplines = budgetRows.filter(r => r.level === 0 && r.discipline).map(r => r.discipline);
                allDisciplines.forEach(d => { if (!disciplineGroups[d]) disciplineGroups[d] = []; });

                const catTotal = computeSubtotalByTypeFor(cat.key, [...matchingCostCodes]);
                const catKey = `lemso_${cat.key}`;
                data[catKey] = catTotal;
                grandBid += pNum(catTotal.bidBudget); grandCOs += pNum(catTotal.approvedCOs);
                grandActual += pNum(catTotal.actualCosts); grandCommit += pNum(catTotal.openCommit);
                grandEtc += pNum(catTotal.etc);
                grandPrevAc += pNum(catTotal.prevActualCosts); grandPrevOc += pNum(catTotal.prevOpenCommit); grandPrevEtc += pNum(catTotal.prevEtcTotal);
                rows.push({ label: cat.label, level: 0, lemsoCategory: cat.key, dataKey: catKey });

                Object.keys(disciplineGroups).sort().forEach(disc => {
                    const codes = disciplineGroups[disc].map(r => r.costCode);
                    const discKey = `lemso_${cat.key}_${disc}`;
                    data[discKey] = computeSubtotalByTypeFor(cat.key, codes);
                    rows.push({ label: disc, level: 1, discipline: disc, lemsoCategory: cat.key, dataKey: discKey });
                    disciplineGroups[disc].sort((a, b) => a.costCode.localeCompare(b.costCode)).forEach(r => {
                        rows.push({ ...r, level: 2, lemsoCategory: cat.key, dataKey: `lemsoCode_${cat.key}_${r.costCode}` });
                    });
                });
            });

            const grandCurBudget = grandBid + grandCOs;
            const grandEAC = grandActual + grandCommit + grandEtc;
            const grandPrevEAC = grandPrevAc + grandPrevOc + grandPrevEtc;
            // Use discipline view's Total Costs for the grand-total row so both views are consistent
            // (the LEMSO categories only track 5* cost-type commitments, but the discipline view
            //  includes all cost types in commitmentsByCode / previousCommitmentsByCode)
            const discTotal = budgetData['Total Costs'] || {};
            data['Total Costs'] = {
                k2Budget: '-', bidBudget: discTotal.bidBudget || fNum(grandBid, 1), approvedCOs: discTotal.approvedCOs || fNum(grandCOs, 1),
                currentBudget: discTotal.currentBudget || fNum(grandCurBudget, 1), actualCosts: discTotal.actualCosts || fNum(grandActual, 1),
                openCommit: discTotal.openCommit || fNum(grandCommit, 1), totalCommit: discTotal.totalCommit || fNum(grandActual + grandCommit, 1),
                etcLabor: '-', etcEquipment: '-', etcMaterials: '-', etcRentals: '-', etcSubs: '-', etcOther: '-',
                etc: discTotal.etc || fNum(grandEtc, 1), eac: discTotal.eac || fNum(grandEAC, 1),
                deltaEACBudget: discTotal.deltaEACBudget || fNum(grandEAC - grandCurBudget, 1),
                prevActualCosts: discTotal.prevActualCosts || fNum(grandPrevAc, 1), prevOpenCommit: discTotal.prevOpenCommit || fNum(grandPrevOc, 1), prevEtcTotal: discTotal.prevEtcTotal || fNum(grandPrevEtc, 1), previousEAC: discTotal.previousEAC || fNum(grandPrevEAC, 1),
                deltaEACPrevious: discTotal.deltaEACPrevious || fNum(grandEAC - grandPrevEAC, 1),
                pf: discTotal.pf || '-', cpi: discTotal.cpi || '-', spi: discTotal.spi || '-'
            };
            rows.push({ label: "Contingency", level: 0 });
            rows.push({ label: "Total Costs", level: 0 });
            return { rows, data };
        }

        return { rows: budgetRows, data: budgetData };
    }, [budgetPivot, budgetRows, budgetData]);

    const jobProfitData = useMemo(() => {
        // VoltaGrid: cost-plus, so there is no contract value and no budget
        // profit — only the EAC, off the Revenue (Billable) row.
        if (isVoltaGrid) {
            const rv = budgetData?.['Contract Amount']?._revenue;
            if (!rv) return null;
            const costEAC = pNum(budgetData?.['Total Costs']?.eac || 0);
            const jpEAC = rv.eac - costEAC;
            const jpPctEAC = rv.eac ? (jpEAC / rv.eac * 100) : 0;
            const gmEAC = jpEAC - (smCosts.overhead || 0);
            const gmPctEAC = rv.eac ? (gmEAC / rv.eac * 100) : 0;
            const dash = '-';
            return {
                revenue: fNum(rv.eac, 1),
                costBudget: dash, costEAC: fNum(costEAC, 1),
                jpBudget: dash, jpEAC: fNum(jpEAC, 1),
                jpBid: dash, jpCO: dash,
                jpPctBudget: dash, jpPctEAC: jpPctEAC.toFixed(2) + '%',
                jpPctBid: dash, jpPctCO: dash,
                gmPctBudget: dash, gmPctEAC: gmPctEAC.toFixed(2) + '%',
                // No budget to vary against: the budget status reads off the
                // EAC alone (variance 0).
                _gmPctBud: gmPctEAC, _gmPctEAC: gmPctEAC,
                _jpPctBud: jpPctEAC, _jpPctEAC: jpPctEAC,
            };
        }
        // Use shared atoms from sideMenu for exact value match
        const revenue = (smBudgets.contractValuesRevised || 0) + (smBudgets.changeOrderRevenue || 0);
        if (!revenue) return null;
        // Cost Budget = current budget from budget overview (day-zero + approved COs)
        // Fall back to day-zero-only sum if budgetData not yet available
        const costBudgetFromTable = budgetData?.['Total Costs']?.currentBudget ? pNum(budgetData['Total Costs'].currentBudget) : null;
        const costBudget = costBudgetFromTable != null ? costBudgetFromTable :
            (smBudgets.laborDayZeroBudget || 0) + (smBudgets.equipmentDayZeroBudget || 0) +
            (smBudgets.materialsDayZeroBudget || 0) + (smBudgets.subcontractorsDayZeroBudget || 0) +
            (smBudgets.otherExpensesDayZeroBudget || 0) + (smBudgets.contingencyDayZeroBudget || 0);
        // Cost EAC — pull from the Budget Overview table so the Budget & Rework
        // card always matches the table on the same page. Fall back to the
        // sidemenu sum only if the table hasn't loaded yet.
        const costEACFromTable = budgetData?.['Total Costs']?.eac ? pNum(budgetData['Total Costs'].eac) : null;
        const costEAC = costEACFromTable != null ? costEACFromTable :
            ((smCosts.labor || 0) + (smCosts.equipment || 0) + (smCosts.materials || 0) +
             (smCosts.subs || 0) + (smCosts.other || 0) + (smCosts.contingency || 0));
        // Bid-only and CO-only values from budget table (consistent with displayed rows)
        const bidRevenue = budgetData?.['Contract Amount']?.bidBudget ? pNum(budgetData['Contract Amount'].bidBudget) : (smBudgets.contractValuesRevised || 0);
        const coRevenue = budgetData?.['Contract Amount']?.approvedCOs ? pNum(budgetData['Contract Amount'].approvedCOs) : (smBudgets.changeOrderRevenue || 0);
        const bidCost = budgetData?.['Total Costs']?.bidBudget ? pNum(budgetData['Total Costs'].bidBudget) : costBudget;
        const coCost = budgetData?.['Total Costs']?.approvedCOs ? pNum(budgetData['Total Costs'].approvedCOs) : 0;
        // JP = Revenue - Cost (matching sidemenu)
        const jpBud = revenue - costBudget;
        const jpBid = bidRevenue - bidCost;
        const jpCO = coRevenue - coCost;
        const jpEAC = revenue - costEAC;
        const jpPctBud = revenue ? (jpBud / revenue * 100) : 0;
        const jpPctBid = bidRevenue ? (jpBid / bidRevenue * 100) : 0;
        const jpPctCO = coRevenue ? (jpCO / coRevenue * 100) : 0;
        const jpPctEAC = revenue ? (jpEAC / revenue * 100) : 0;
        // Overhead
        const overheadBudget = smBudgets.overheadDayZeroBudget || 0;
        const overheadEAC = smCosts.overhead || 0;
        // GM = JP - Overhead (matching sidemenu)
        const gmBud = jpBud - overheadBudget;
        const gmEAC = jpEAC - overheadEAC;
        const gmPctBud = revenue ? (gmBud / revenue * 100) : 0;
        const gmPctEAC = revenue ? (gmEAC / revenue * 100) : 0;
        return {
            revenue: fNum(revenue, 1),
            costBudget: fNum(costBudget, 1), costEAC: fNum(costEAC, 1),
            jpBudget: fNum(jpBud, 1), jpEAC: fNum(jpEAC, 1),
            jpBid: fNum(jpBid, 1), jpCO: fNum(jpCO, 1),
            jpPctBudget: jpPctBud.toFixed(2) + '%', jpPctEAC: jpPctEAC.toFixed(2) + '%',
            jpPctBid: jpPctBid.toFixed(2) + '%', jpPctCO: jpPctCO.toFixed(2) + '%',
            gmPctBudget: gmPctBud.toFixed(2) + '%', gmPctEAC: gmPctEAC.toFixed(2) + '%',
            // Raw numeric values for status calculations
            _gmPctBud: gmPctBud, _gmPctEAC: gmPctEAC,
            _jpPctBud: jpPctBud, _jpPctEAC: jpPctEAC,
        };
    }, [smBudgets, smCosts, budgetData, isVoltaGrid]);

    const gmVariance = Math.abs((jobProfitData?._jpPctBud || 0) - (jobProfitData?._jpPctEAC || 0));

    const statuses = {
        productivity: calcProductivityStatus(),
        budget: calcBudgetStatus(),
        schedule: spiExpanded ? calcScheduleStatus(budgetData?.['Total Costs']?.spi) : 'grey',
        // VoltaGrid is cost-plus: billed vs earned is not a health signal there yet, so the
        // pill sits grey (N/A) and drops out of the overall score until billing is tied out.
        billing: isVoltaGrid ? 'grey' : calcBillingStatus(),
        safety: calcSafetyStatus(),
    };
    const overallStatus = (() => {
        const toScore = s => s === 'green' ? 1.0 : s === 'orange' ? 0.5 : 0.0;
        const weights = spiExpanded
            ? [['safety', 0.25], ['budget', 0.20], ['productivity', 0.25], ['schedule', 0.20], ['billing', 0.10]]
            : [['safety', 0.3125], ['budget', 0.25], ['productivity', 0.3125], ['billing', 0.125]];
        // Grey (N/A) categories are left out and the remaining weights renormalised.
        const live = weights.filter(([k]) => statuses[k] !== 'grey');
        const wsum = live.reduce((t, [, w]) => t + w, 0) || 1;
        const score = live.reduce((t, [k, w]) => t + toScore(statuses[k]) * (w / wsum), 0);
        const raw = score >= 0.85 ? 'green' : score >= 0.70 ? 'orange' : 'red';
        // Governance rule: Safety Red → Overall cannot be Green
        if (statuses.safety === 'red' && raw === 'green') return 'orange';
        return raw;
    })();

    const changeOrderSummary = useMemo(() => {
        if (!jobUserData) return { approved: 0, pendingPerformed: 0, pendingNotPerformed: 0, notSubmitted: 0, denied: 0, approvedCount: 0, pendingPerformedCount: 0, pendingNotPerformedCount: 0, notSubmittedCount: 0, deniedCount: 0 };
        const summary = { approved: 0, pendingPerformed: 0, pendingNotPerformed: 0, notSubmitted: 0, denied: 0, approvedCount: 0, pendingPerformedCount: 0, pendingNotPerformedCount: 0, notSubmittedCount: 0, deniedCount: 0 };
        const dedup = {};
        jobUserData.forEach(item => {
            if (item.Version_GUID == selectedVersion && item.Forecast_Tab === 'Change Order') {
                const key = item.Forecast_User_Data_GUID;
                if (!dedup[key] || new Date(item.DSS_Updated_Datetime || item.DSS_Created_Date) > new Date(dedup[key].DSS_Updated_Datetime || dedup[key].DSS_Created_Date)) dedup[key] = item;
            }
        });
        // Track unique CO numbers per status (e.g. 1.1 and 1.2 count as CO #1)
        const uniqueCOs = { Approved: new Set(), 'Pending - Performed': new Set(), 'Pending - Not Performed': new Set(), 'Not Submitted': new Set(), Denied: new Set() };
        Object.values(dedup).forEach(row => {
            if (row.Forecast_Tab !== 'Change Order') return;
            // Only count revenue rows (Cost_Type '41000') to match sideMenu changeOrderRevenue calc
            if (row.Cost_Type?.toString() !== '41000') return;
            const amt = parseFloat(row.Value_Amount) || 0;
            const rawLabel = row.Row_Label;
            const parsed = rawLabel != null && rawLabel !== '' ? parseFloat(rawLabel) : NaN;
            const coId = !isNaN(parsed) ? Math.floor(parsed) : null;
            switch (row.Status) {
                case 'Approved': summary.approved += amt; if (coId !== null) uniqueCOs['Approved'].add(coId); break;
                case 'Pending - Performed': summary.pendingPerformed += amt; if (coId !== null) uniqueCOs['Pending - Performed'].add(coId); break;
                case 'Pending - Not Performed': summary.pendingNotPerformed += amt; if (coId !== null) uniqueCOs['Pending - Not Performed'].add(coId); break;
                case 'Not Submitted': summary.notSubmitted += amt; if (coId !== null) uniqueCOs['Not Submitted'].add(coId); break;
                case 'Denied': summary.denied += amt; if (coId !== null) uniqueCOs['Denied'].add(coId); break;
                default: break;
            }
        });
        summary.approvedCount = uniqueCOs['Approved'].size;
        summary.pendingPerformedCount = uniqueCOs['Pending - Performed'].size;
        summary.pendingNotPerformedCount = uniqueCOs['Pending - Not Performed'].size;
        summary.notSubmittedCount = uniqueCOs['Not Submitted'].size;
        summary.deniedCount = uniqueCOs['Denied'].size;
        return summary;
    }, [jobUserData, selectedVersion]);

    const quantityTrackingData = useMemo(() => {
        if (!jobUserData || !selectedVersion || !budgetRows) return {};
        const result = {};

        // Read math operation settings per discipline — same source as productivityCombined
        // Forecast_Tab: 'Productivity Tracker', Forecast_Field_Name: 'math' | 'mathRow', Forecast_Row_ID: discipline
        const mathByDisc = {};
        const mathRowByDisc = {};
        jobUserData.forEach(i => {
            if (i.Forecast_Tab === 'Productivity Tracker') {
                if (i.Forecast_Field_Name === 'math') mathByDisc[i.Forecast_Row_ID] = i.Forecast_Value;
                if (i.Forecast_Field_Name === 'mathRow') mathRowByDisc[i.Forecast_Row_ID] = i.Forecast_Value;
            }
        });

        const disciplines = budgetRows.filter(r => r.level === 0 && r.discipline && r.discipline !== 'Contingency' && r.label !== 'Contract Amount' && r.label !== 'Total Costs');
        // Build actual-hours-by-code lookup from cost detail (labor: 51000-51799), same as budgetRows logic
        const qtyActualHoursByCode = {};
        if (Array.isArray(jobCostDetail)) {
            jobCostDetail.forEach(item => {
                if (item.Phase_Code && item.Cost_Type_Code >= '51000' && item.Cost_Type_Code <= '51799' && item.Phase_Code !== '1000' && item.Phase_Code !== '990000') {
                    const npc = normCC(item.Phase_Code);
                    qtyActualHoursByCode[npc] = (qtyActualHoursByCode[npc] || 0) + (parseFloat(item.Total_Hours) || 0);
                }
            });
        }

        let oP = 0, totalBudgetHours = 0, totalEarnedHours = 0, totalActualHours = 0;
        disciplines.forEach(discRow => {
            const disc = discRow.discipline;
            // The discipline row carries its codes; on VoltaGrid most disciplines have no
            // code rows under them (the children are WBS groups), so filtering for
            // costCode rows found nothing and the header % complete sat at 0.
            const codes = discRow.codes?.length ? discRow.codes : budgetRows.filter(r => r.discipline === disc && r.costCode).map(r => r.costCode);
            let discBudgetHours = 0, discEarnedHours = 0, discActualHours = 0, codeDetails = [];

            codes.forEach(cc => {
                const dzItems = jobUserData.filter(i => i.Forecast_Tab === 'Day Zero Budget' && i.Cost_Code === cc && i.Version_GUID === selectedVersion);
                const dzQty = dzItems.reduce((s, i) => s + (parseFloat(i?.Quantity) || 0), 0);
                const dzHrs = dzItems.reduce((s, i) => s + (parseFloat(i?.Hours) || 0), 0);
                const dzUom = dzItems.find(i => i?.UoM)?.UoM || '';
                const scorecardUomItem = jobUserData.find(i => i.Forecast_Tab === 'Scorecard' && i.Forecast_Field_Name === 'uom' && i.Forecast_Row_ID === cc && i.Version_GUID === selectedVersion);
                const scorecardUom = scorecardUomItem?.Forecast_Value || '';
                const coItems = jobUserData.filter(i => i.Forecast_Tab === 'Change Order' && i.Cost_Code === cc && i.Version_GUID === selectedVersion && i.Status === 'Approved');
                const coQty = coItems.reduce((s, i) => s + (parseFloat(i?.Quantity) || 0), 0);
                const coHrs = coItems.reduce((s, i) => s + (parseFloat(i?.Hours) || 0), 0);
                const plugItem = jobUserData.find(i => i.Forecast_Tab === 'Scorecard' && i.Forecast_Field_Name === 'plug' && i.Forecast_Row_ID === cc && i.Version_GUID === selectedVersion);
                const plugQty = pNum(plugItem?.Forecast_Value) || 0;
                const instItem = jobUserData.find(i => i.Forecast_Tab === 'Scorecard' && i.Forecast_Field_Name === 'installed' && i.Forecast_Row_ID === cc && i.Version_GUID === selectedVersion);
                const scorecardInstalled = pNum(instItem?.Forecast_Value) || 0;
                // Heavy Job installed quantities from FP document type — must use raw (not condensed) data, same as productivityCombined/scorecard
                let heavyJobInstalled = 0;
                if (Array.isArray(jobCostDetailRaw)) {
                    jobCostDetailRaw.forEach(cd => { if (cd.Document_Type === 'FP' && cd.Phase_Code === cc) heavyJobInstalled += pNum(cd.Total_Hours) || 0; });
                }
                // Always add both sources (matches productivityCombined: totalInstalledQuantity = heavyJobInstalled + installed)
                const installedQ = (scorecardInstalled || 0) + (heavyJobInstalled || 0);
                const totalQty = dzQty + coQty + plugQty;
                const budgetQtyOnly = dzQty + coQty; // for Max comparison (no plug), matching productivityCombined
                const budgetHrs = dzHrs + coHrs;
                // Per-phase earned hours (productivityCombined approach) — always summed regardless of math mode
                const phasePct = totalQty > 0 ? Math.min((installedQ / totalQty) * 100, 100) : 0;
                const phaseEarnedHrs = phasePct > 0 ? Math.min(phasePct * budgetHrs / 100, budgetHrs) : 0;
                const actualHrs = qtyActualHoursByCode[cc] || 0;
                discBudgetHours += budgetHrs;
                discEarnedHours += phaseEarnedHrs;
                discActualHours += actualHrs;
                codeDetails.push({ cc, uom: scorecardUom || dzUom, plannedQty: totalQty, budgetQtyOnly, installedQty: installedQ, scorecardInstalled, heavyJobInstalled, phasePct: phasePct.toFixed(1), budgetHrs, earnedHrs: phaseEarnedHrs, actualHrs });
            });

            // Apply math operation to determine discipline-level planned qty & UOM
            // matches productivityCombined DisciplineRow: default=Max, Sum, Row, Qty
            const mathMode = mathByDisc[disc] || 'Max';
            const mathRowIdx = parseInt(mathRowByDisc[disc] || 1) - 1; // 1-based → 0-based
            let dP = 0, dU = '', mathDrivingCode = null;

            if (mathMode === 'Sum') {
                // Sum all codes' planned qty
                codeDetails.forEach(cd => { dP += cd.plannedQty; });
                // The unit the codes carrying a quantity share, blank when they differ (see productivityCombined)
                dU = sharedUnit(codeDetails.filter(cd => cd.plannedQty > 0).map(cd => cd.uom));
                mathDrivingCode = null;
            } else if (mathMode === 'Qty') {
                // Typed discipline quantity ("11 EA"), see productivityCombined
                const q = parseQtyMath(mathRowByDisc[disc]);
                dP = q.qty; dU = q.uom || (codeDetails.find(cd => cd.uom)?.uom || ''); mathDrivingCode = null;
            } else if (mathMode === 'Row') {
                // Use specific row by index (1-based)
                const rowCd = codeDetails[Math.min(mathRowIdx, codeDetails.length - 1)];
                if (rowCd) { dP = rowCd.plannedQty; dU = rowCd.uom; mathDrivingCode = rowCd.cc; }
            } else {
                // Max (default): use the code with the largest budgetQty (matching productivityCombined)
                let maxQty = -1, maxIdx = 0;
                codeDetails.forEach((cd, i) => { if (cd.budgetQtyOnly > maxQty) { maxQty = cd.budgetQtyOnly; maxIdx = i; } });
                const maxCd = codeDetails[maxIdx];
                if (maxCd) { dP = maxCd.plannedQty; dU = maxCd.uom; mathDrivingCode = maxCd.cc; }
            }

            // Discipline installed = back-calculated from earned-hour ratio × planned qty
            // (pctComplete = earnedHours/budgetHours regardless of math — the qty cancels out)
            const dA = discBudgetHours > 0 ? (discEarnedHours / discBudgetHours) * dP : 0;
            oP += dP;
            const discPct = discBudgetHours > 0 ? discEarnedHours / discBudgetHours : 0;
            totalBudgetHours += discBudgetHours;
            totalEarnedHours += discEarnedHours;
            totalActualHours += discActualHours;
            result[disc] = { uom: dU, planned: dP, actual: dA, variance: dP - dA, pctComplete: (discPct * 100).toFixed(1), budgetHours: discBudgetHours, earnedHours: discEarnedHours, actualHours: discActualHours, codeDetails, mathMode, mathDrivingCode };
        });
        const overallPctComplete = totalBudgetHours > 0 ? (totalEarnedHours / totalBudgetHours) * 100 : 0;
        const oA = totalBudgetHours > 0 ? (totalEarnedHours / totalBudgetHours) * oP : 0;
        result['__overall'] = { planned: oP, actual: oA, variance: oP - oA, pctComplete: overallPctComplete.toFixed(1), budgetHours: totalBudgetHours, earnedHours: totalEarnedHours, actualHours: totalActualHours };
        return result;
    }, [jobUserData, selectedVersion, budgetRows, jobCostDetail, jobCostDetailRaw]);

    /* ═══════════ PROGRESS SCHEDULE DATA (matches overview.js Section 4) ═══════════ */
    const progressScheduleData = useMemo(() => {
        if (!jobUserData || !selectedVersion || !budgetRows) return {};
        const result = {};
        const disciplines = budgetRows.filter(r => r.level === 0 && r.discipline && r.discipline !== 'Contingency' && r.label !== 'Contract Amount' && r.label !== 'Total Costs');

        // Build Phase Hours lookup: { costCode: { week: hours } }
        const phaseHoursData = {};
        jobUserData.forEach(item => {
            if (item.Forecast_Tab === 'Phase Hours' && item.Version_GUID === selectedVersion && item.BU_ID === selectedJob && item.Forecast_Field_Name?.startsWith('week_')) {
                const week = item.Forecast_Field_Name.replace('week_', '');
                const cc = item.Forecast_Row_ID;
                if (!phaseHoursData[cc]) phaseHoursData[cc] = {};
                phaseHoursData[cc][week] = (phaseHoursData[cc][week] || 0) + (parseFloat(item.Forecast_Value) || 0);
            }
        });
        // All weeks sorted
        const allWeeks = new Set();
        Object.values(phaseHoursData).forEach(weeks => Object.keys(weeks).forEach(w => allWeeks.add(w)));
        const sortedWeeks = Array.from(allWeeks).sort();
        const currentWeek = versionInfo?.Version_Start_DateTime ? moment(versionInfo.Version_Start_DateTime).startOf('isoWeek').format('YYYY-MM-DD') : null;

        // Build Burn Tool Labor Phased ETC lookup (used when regular !== 'regular')
        const btpData = {};
        jobUserData.forEach(item => {
            if (item.Forecast_Tab === 'Burn Tool Labor Phased') {
                btpData[item.Forecast_Row_ID] = item;
            }
        });
        const burnToolEtcByCode = {};
        Object.keys(btpData).forEach(key => {
            if (key.includes('=')) {
                const phase = key.split('=')[0];
                const week = key.split('=')[1];
                const phaseDetails = btpData[phase] || {};
                const withinProject = moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(projectComplete).endOf('week'));
                const withinStartStop = phaseDetails.Phase_Start && phaseDetails.Phase_End
                    ? moment(week).isAfter(moment(phaseDetails.Phase_Start).startOf('week')) && moment(week).isBefore(moment(phaseDetails.Phase_End).endOf('week'))
                    : withinProject;
                const h = parseFloat(btpData[key]?.Number_Hours) || 0;
                const bR = parseFloat(btpData[key]?.Rate) || 0;
                const cS = parseFloat(btpData[key]?.Number_Units) || 0;
                if (h && bR && cS && withinProject && withinStartStop) {
                    burnToolEtcByCode[phase] = (burnToolEtcByCode[phase] || 0) + h * cS;
                }
            }
        });

        let overallBudgetHrs = 0, overallActualHrs = 0, overallForecastHrs = 0, overallTrendingHrs = 0, overallCumulativePlan = 0;

        disciplines.forEach(discRow => {
            const codes = budgetRows.filter(r => r.discipline === discRow.discipline && r.costCode).map(r => r.costCode);
            let discBudgetHrs = 0, discActualHrs = 0, discForecastHrs = 0, discTrendingHrs = 0;
            let discInstalledQty = 0, discTotalQty = 0;
            let periodPlan = 0, cumulativePlan = 0, totalPhaseHrs = 0;
            const codeDebugRows = [];

            codes.forEach(cc => {
                // Budget hours from Day Zero + COs
                const dzItems = jobUserData.filter(i => i.Forecast_Tab === 'Day Zero Budget' && i.Cost_Code === cc && i.Version_GUID === selectedVersion);
                const dzHrs = dzItems.reduce((s, i) => s + (parseFloat(i?.Hours) || 0), 0);
                const dzQty = dzItems.reduce((s, i) => s + (parseFloat(i?.Quantity) || 0), 0);
                const coItems = jobUserData.filter(i => i.Forecast_Tab === 'Change Order' && i.Cost_Code === cc && i.Version_GUID === selectedVersion && i.Status === 'Approved');
                const coHrs = coItems.reduce((s, i) => s + (parseFloat(i?.Hours) || 0), 0);
                const coQty = coItems.reduce((s, i) => s + (parseFloat(i?.Quantity) || 0), 0);
                const plugItem = jobUserData.find(i => i.Forecast_Tab === 'Scorecard' && i.Forecast_Field_Name === 'plug' && i.Forecast_Row_ID === cc && i.Version_GUID === selectedVersion);
                const plugQty = pNum(plugItem?.Forecast_Value) || 0;
                const totalQty = dzQty + coQty + plugQty;
                const budgetHrs = dzHrs + coHrs;

                // Installed from Scorecard
                const instItem = jobUserData.find(i => i.Forecast_Tab === 'Scorecard' && i.Forecast_Field_Name === 'installed' && i.Forecast_Row_ID === cc && i.Version_GUID === selectedVersion);
                const installed = pNum(instItem?.Forecast_Value) || 0;

                // Actual hours from cost detail
                let phaseActualHrs = 0;
                if (Array.isArray(jobCostDetail)) {
                    jobCostDetail.forEach(cd => {
                        if (cd.Phase_Code === cc && cd.Cost_Type_Code && cd.Cost_Type_Code[0] === '5' && cd.Cost_Type_Code[1] === '1' && cd.Cost_Type_Code[2] !== '8') {
                            phaseActualHrs += parseFloat(cd.Total_Hours) || 0;
                        }
                    });
                }

                // Forecasted hours (ETC) — source depends on phasedForecastAtom mode
                let fcastHrs, versionMatch;
                if (regular === 'regular') {
                    // Scorecard mode: use manually-entered forecastedHrs
                    const fcastItemVersioned = jobUserData.find(i => i.Forecast_Tab === 'Scorecard' && i.Forecast_Field_Name === 'forecastedHrs' && i.Forecast_Row_ID === cc && i.Version_GUID === selectedVersion);
                    const fcastItemAny = !fcastItemVersioned ? jobUserData.filter(i => i.Forecast_Tab === 'Scorecard' && i.Forecast_Field_Name === 'forecastedHrs' && i.Forecast_Row_ID === cc).reduce((last, i) => i, null) : null;
                    const fcastItem = fcastItemVersioned || fcastItemAny;
                    fcastHrs = parseFloat(fcastItem?.Forecast_Value) || 0;
                    versionMatch = !!fcastItemVersioned;
                } else {
                    // Phased/Heavy Job mode: use Burn Tool Labor Phased hours (Number_Hours × Number_Units)
                    fcastHrs = burnToolEtcByCode[cc] || 0;
                    versionMatch = true; // Not applicable in BTP mode
                }
                const pmEAC = fcastHrs + phaseActualHrs;

                // Trending EAC
                const pctComplete = totalQty > 0 ? (installed / totalQty) * 100 : 0;
                const actualHrsQty = installed > 0 ? (pctComplete < 25 ? (budgetHrs / totalQty || 0) : (phaseActualHrs / installed)) : 0;
                const trendingEAC = pctComplete > 25 ? actualHrsQty * totalQty : (phaseActualHrs > budgetHrs ? phaseActualHrs : budgetHrs);

                discBudgetHrs += budgetHrs;
                discActualHrs += phaseActualHrs;
                discForecastHrs += pmEAC;
                discTrendingHrs += trendingEAC;
                discInstalledQty += installed;
                discTotalQty += totalQty;

                // Phase Hours planned data
                const codeWeeks = phaseHoursData[cc] || {};
                if (currentWeek && codeWeeks[currentWeek]) periodPlan += codeWeeks[currentWeek];
                let codeCumulativeHrs = 0;
                sortedWeeks.forEach(w => { if (!currentWeek || w <= currentWeek) { codeCumulativeHrs += (codeWeeks[w] || 0); cumulativePlan += (codeWeeks[w] || 0); } });
                let codeTotalPhaseHrs = 0;
                Object.values(codeWeeks).forEach(h => { totalPhaseHrs += h; codeTotalPhaseHrs += h; });
                codeDebugRows.push({ cc, budgetHrs, phaseActualHrs, fcastHrs, pmEAC, codeCumulativeHrs, codeTotalPhaseHrs, versionMatch });
            });

            overallBudgetHrs += discBudgetHrs;
            overallActualHrs += discActualHrs;
            overallForecastHrs += discForecastHrs;
            overallTrendingHrs += discTrendingHrs;
            overallCumulativePlan += cumulativePlan;

            result[discRow.discipline] = {
                cumulativePlan: discBudgetHrs > 0 ? ((cumulativePlan / discBudgetHrs) * 100).toFixed(1) + '%' : '0.0%',
                cumulativePlanHrs: cumulativePlan,
                budgetHrs: discBudgetHrs,
                currentWeek,
                codeDebugRows,
                etcMode: regular,
                cumulativeActual: discBudgetHrs > 0 ? ((discActualHrs / discBudgetHrs) * 100).toFixed(1) + '%' : '0.0%',
                cumulativeFcast: discForecastHrs > 0 ? ((discActualHrs / discForecastHrs) * 100).toFixed(1) + '%' : '0.0%',
                totalFcastHrs: discForecastHrs,
                totalActualHrs: discActualHrs,
                cumulativeTrending: discBudgetHrs > 0 ? ((discTrendingHrs / discBudgetHrs) * 100).toFixed(1) + '%' : '0.0%',
            };
        });

        result['__overall'] = {
            cumulativePlan: overallBudgetHrs > 0 ? ((overallCumulativePlan / overallBudgetHrs) * 100).toFixed(1) + '%' : '0.0%',
            cumulativePlanHrs: overallCumulativePlan,
            budgetHrs: overallBudgetHrs,
            currentWeek,
            cumulativeActual: overallBudgetHrs > 0 ? ((overallActualHrs / overallBudgetHrs) * 100).toFixed(1) + '%' : '0.0%',
            cumulativeFcast: overallForecastHrs > 0 ? ((overallActualHrs / overallForecastHrs) * 100).toFixed(1) + '%' : '0.0%',
            totalFcastHrs: overallForecastHrs,
            totalActualHrs: overallActualHrs,
            totalTrendingHrs: overallTrendingHrs,
            cumulativeTrending: overallBudgetHrs > 0 ? ((overallTrendingHrs / overallBudgetHrs) * 100).toFixed(1) + '%' : '0.0%',
        };
        return result;
    }, [jobUserData, jobCostDetail, selectedVersion, selectedJob, budgetRows, versionInfo, regular, projectComplete]);

    // Milestone handlers
    const handleSaveMilestone = () => {
        const em = editingMilestoneRef.current;
        if (!em) return;
        const weeklyDelta = em.actualForecast && em.prevForecast ? moment(em.actualForecast).diff(moment(em.prevForecast), 'days') : '';
        const baselineDelta = em.currentBaseline && em.actualForecast ? moment(em.actualForecast).diff(moment(em.currentBaseline), 'days') : '';
        const ms = { ...em, weeklyDelta, baselineDelta };
        setMilestones(milestones.map(m => m.id === ms.id ? ms : m));
        save('Milestone', JSON.stringify(ms), ms.id);
        setEditingMilestoneId(null);
        editingMilestoneRef.current = null;
    };
    const handleAddMilestone = () => {
        const nm = { id: Date.now().toString(), milestone: '', owner: '', currentBaseline: '', actualForecast: '', prevForecast: '', remarks: '', weeklyDelta: '', baselineDelta: '' };
        setMilestones([...milestones, nm]);
        setEditingMilestoneId(nm.id);
        editingMilestoneRef.current = nm;
    };
    const handleDeleteMilestone = (id) => {
        if (window.confirm('Delete this milestone?')) {
            setMilestones(milestones.filter(m => m.id !== id));
            save('Milestone', '', id);
        }
    };

    /* ═══════════════  TOTAL REWORK HOURS ═══════════════ */
    const totalReworkHours = useMemo(() => {
        // Use automated HCSS data if available, otherwise fall back to manual
        if (reworkApiData.length > 0) {
            return reworkApiData.reduce((sum, r) => sum + (parseFloat(r.Hours) || 0), 0);
        }
        let total = 0;
        Object.values(reworkHoursData).forEach(v => {
            if (typeof v === 'string') {
                try { const parsed = JSON.parse(v); total += parseFloat(parsed.cumulative || 0); } catch { total += parseFloat(v) || 0; }
            } else { total += parseFloat(v) || 0; }
        });
        return total;
    }, [reworkHoursData, reworkApiData]);

    const reworkByForeman = useMemo(() => {
        const grouped = {};
        reworkApiData.forEach(r => {
            const key = r.Foreman_Name || r.Foreman_Code || 'Unknown';
            if (!grouped[key]) grouped[key] = { hours: 0, count: 0 };
            grouped[key].hours += parseFloat(r.Hours) || 0;
            grouped[key].count += 1;
        });
        return Object.entries(grouped).sort((a, b) => b[1].hours - a[1].hours);
    }, [reworkApiData]);

    /* ═══════════════ COMPUTED DISPLAY VALUES ═══════════════ */
    const contractAmt = jobProfitData?.revenue || '$0';
    const totalCosts = jobProfitData?.costBudget || '$0';
    const totalEAC = jobProfitData?.costEAC || '$0';

    // Variance WIP computations for Budget & Rework card
    // A boolean on purpose: this gates JSX, and a numeric 0 would print as "0".
    const hasWipData = !!(laborDeltaWip || equipmentDeltaWip || materialsDeltaWip || subcontractorsDeltaWip || otherDeltaWip || contingencyDeltaWip);
    const wipRevenueVar = budgetDeltaWip != null ? fNum(pNum(jobProfitData?.revenue) - pNum(budgetDeltaWip), 1) : '';
    const wipTotalCost = pNum(laborDeltaWip) + pNum(equipmentDeltaWip) + pNum(materialsDeltaWip) + pNum(subcontractorsDeltaWip) + pNum(otherDeltaWip) + pNum(contingencyDeltaWip);
    const wipCostVar = hasWipData ? fNum(pNum(jobProfitData?.costEAC) - wipTotalCost, 1) : '';
    const wipJpBudget = budgetDeltaWip != null ? pNum(budgetDeltaWip) - wipTotalCost : 0;
    const wipJpEAC = pNum(jobProfitData?.revenue) - pNum(jobProfitData?.costEAC);
    const wipMarginVar = hasWipData ? fNum(wipJpEAC - wipJpBudget, 1) : '';
    const wipJpPctWip = pNum(budgetDeltaWip) ? (wipJpBudget / pNum(budgetDeltaWip) * 100) : 0;
    const wipJpPctEAC = pNum(jobProfitData?.revenue) ? (wipJpEAC / pNum(jobProfitData?.revenue) * 100) : 0;
    const wipPctVar = hasWipData ? (wipJpPctEAC - wipJpPctWip).toFixed(2) + '%' : '';
    const daysRemaining = projectComplete ? moment(projectComplete).diff(newDate, 'days') : null;
    const pctComplete = quantityTrackingData?.['__overall']?.pctComplete || '0.0';


    /* ═══════════════════════════════════════════════════════════════════ */
    /*                          R E N D E R                              */
    /* ═══════════════════════════════════════════════════════════════════ */

    const StatusPill = ({ level, label, onClick }) => (
        <div onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, background: statusBg(level), fontSize: 10, fontWeight: 600, color: statusColor(level), cursor: onClick ? 'pointer' : 'default' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor(level), display: 'inline-block', boxShadow: `0 0 5px ${statusColor(level)}44` }} />
            {label}
        </div>
    );

    const ProgressBar = ({ pct, height = 8 }) => {
        const p = Math.min(parseFloat(pct) || 0, 100);
        return (
            <div style={{ width: '100%', height, borderRadius: height, background: palette.blue50, overflow: 'hidden' }}>
                <div style={{ width: `${p}%`, height: '100%', borderRadius: height, background: `linear-gradient(90deg, ${palette.blue400}, ${palette.blue600})`, transition: 'width 0.8s cubic-bezier(.4,0,.2,1)' }} />
            </div>
        );
    };

    // Title-bar action button. Every one of these is screen-only — the print
    // sheet is the report, and a row of greyed buttons on it is noise.
    const ToolbarBtn = ({ onClick, disabled, title, children }) => (
        <button className="no-print" onClick={onClick} disabled={disabled} title={title}
            style={{ padding: '6px 12px', background: palette.card, border: `1px solid ${palette.border}`, borderRadius: 8, cursor: disabled ? 'default' : 'pointer', fontSize: 11, fontWeight: 600, color: disabled ? palette.border : palette.muted, display: 'flex', alignItems: 'center', gap: 5, transition: 'all .15s', flexShrink: 0, whiteSpace: 'nowrap' }}
            onMouseEnter={e => { if (disabled) return; e.currentTarget.style.borderColor = palette.accent; e.currentTarget.style.color = palette.accent; }}
            onMouseLeave={e => { if (disabled) return; e.currentTarget.style.borderColor = palette.border; e.currentTarget.style.color = palette.muted; }}>
            {children}
        </button>
    );

    const handleBannerUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || !selectedJob) return;
        setBannerBusy(true);
        try {
            const blob = await downscaleImage(file);
            const res = await uploadJobBanner({ buId: selectedJob, uploadedBy: userEmail || '', blob });
            if (res?.sasUrl) setBannerImage(res.sasUrl);
            else window.alert('Banner upload failed' + (res?.error ? ': ' + res.error : ''));
        } catch (err) {
            window.alert('Banner upload failed: ' + (err?.message || err));
        } finally {
            setBannerBusy(false);
        }
    };

    // Adjust mode: drag the photo up or down, zoom with the slider, then save.
    const BANNER_HEIGHT = 120;
    const bannerOverflowPx = (zoom) => {
        // Height the photo renders at (cover + scale) minus the strip; the
        // y% walks across this. Unknown size: a reasonable guess.
        const w = bannerBoxRef.current?.clientWidth || 1200;
        const h = bannerNatural ? (w * bannerNatural.h / bannerNatural.w) : w * 0.6;
        return Math.max(1, h * (zoom / 100) - BANNER_HEIGHT);
    };
    const clampView = (v) => ({
        y: Math.min(100, Math.max(0, Math.round(v.y))),
        zoom: Math.min(300, Math.max(100, Math.round(v.zoom))),
    });
    const handleBannerDragStart = (e) => {
        if (!bannerAdjust) return;
        e.preventDefault();
        bannerDragRef.current = { startY: e.clientY, startView: bannerAdjust };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const handleBannerDragMove = (e) => {
        const d = bannerDragRef.current;
        if (!d || !bannerAdjust) return;
        const dy = e.clientY - d.startY;
        // Dragging the picture down shows more of its top: y goes down.
        const y = d.startView.y - (dy / bannerOverflowPx(d.startView.zoom)) * 100;
        setBannerAdjust(clampView({ y, zoom: d.startView.zoom }));
    };
    const handleBannerDragEnd = () => { bannerDragRef.current = null; };
    const handleBannerAdjustSave = async () => {
        if (!selectedJob || !bannerAdjust) return;
        const view = clampView(bannerAdjust);
        setBannerBusy(true);
        try {
            const res = await updateUserDataArray({
                userId: '154045', userName: 'Seth Bailey', userEmail: userEmail || '', tab: 'Overview', fieldName: 'BannerView',
                value: JSON.stringify(view), version: BILLING_VERSION + '', id: selectedJob, rowId: '1', lockedVersion: false,
            });
            if (res?.error) throw new Error(res.error);
            setBannerView(view);
            setBannerAdjust(null);
        } catch (err) {
            window.alert('Could not save the banner position: ' + (err?.message || err));
        } finally {
            setBannerBusy(false);
        }
    };

    const handleBannerRemove = async () => {
        if (!selectedJob || !bannerImage) return;
        if (!window.confirm('Remove the custom banner and go back to the default photo?')) return;
        setBannerBusy(true);
        try {
            await deleteJobBanner({ buId: selectedJob });
            setBannerImage(null);
        } finally {
            setBannerBusy(false);
        }
    };

    // Revenue / Cost / Margin vs EAC table. Non-VoltaGrid: top of the Budget &
    // Rework card. VoltaGrid: folded into the Change Orders card (one less box).
    const revenueTable = (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed', fontVariantNumeric: 'tabular-nums' }}>
                            <colgroup>
                                {(!isVoltaGrid ? ['25%', '25%', '25%', '25%'] : ['34%', '33%', '33%']).map((w, i) => <col key={i} style={{ width: w }} />)}
                            </colgroup>
                            <thead>
                                <tr>
                                    <th />
                                    <th style={{ textAlign: 'right', fontSize: 8, fontWeight: 700, color: palette.blue600, textTransform: 'uppercase', letterSpacing: '.06em', padding: '3px 8px' }}>{isVoltaGrid ? 'Bid' : 'C.Budget'}</th>
                                    <th style={{ textAlign: 'right', fontSize: 8, fontWeight: 700, color: palette.blue600, textTransform: 'uppercase', letterSpacing: '.06em', padding: '3px 8px' }}>EAC</th>
                                    {!isVoltaGrid && <th style={{ textAlign: 'right', fontSize: 8, fontWeight: 700, color: palette.blue600, textTransform: 'uppercase', letterSpacing: '.06em', padding: '3px 8px' }}>Var. WIP</th>}
                                </tr>
                            </thead>
                            <tbody>
                            {[
                                // VoltaGrid: budget is the bid revenue (Day Zero 41000 row); EAC is the billable amount.
                                { label: 'Revenue', budget: isVoltaGrid ? (budgetData?.['Contract Amount']?.currentBudget || '-') : contractAmt, eac: isVoltaGrid && !jobProfitData ? '-' : contractAmt, varWip: wipRevenueVar },
                                { label: 'Cost', budget: totalCosts, eac: totalEAC, varWip: wipCostVar },
                                { label: 'Job Margin', budget: jobProfitData?.jpBudget || '$0', eac: jobProfitData?.jpEAC || '$0', bold: true, varWip: wipMarginVar },
                                { label: 'Job %', budget: jobProfitData?.jpPctBudget || '0.00%', eac: jobProfitData?.jpPctEAC || '0.00%', bold: true, eacPct: parseFloat(jobProfitData?.jpPctEAC) || 0, varWip: wipPctVar },
                            ].filter(({ label }) => !isVoltaGrid || label === 'Revenue').map(({ label, budget, eac, bold, budgetOnly, eacPct, varWip }, idx) => {
                                const eacColor = eacPct !== undefined && eacPct < 0 ? palette.red : eacPct !== undefined && eacPct < 11 ? '#d4a017' : (bold ? palette.blue700 : palette.text);
                                return (
                                    <tr key={label} style={{ borderTop: idx === 0 ? `1px solid ${palette.blue100}` : 'none', borderBottom: `1px solid ${palette.blue100}`, background: bold ? palette.blue50 : 'transparent' }}>
                                        <td style={{ padding: '5px 8px', fontWeight: bold ? 800 : 600, color: palette.text }}>{label}</td>
                                        <td style={{ textAlign: 'right', padding: '5px 8px', fontWeight: bold ? 800 : 700, color: bold ? palette.blue700 : palette.text }}>{budget}</td>
                                        <td style={{ textAlign: 'right', padding: '5px 8px', fontWeight: bold ? 800 : 700, color: eacPct !== undefined ? eacColor : (bold ? palette.blue700 : palette.text) }}>{budgetOnly ? '' : eac}</td>
                                        {!isVoltaGrid && <td style={{ textAlign: 'right', padding: '5px 8px', fontWeight: bold ? 800 : 700, color: palette.muted }}>{varWip || '-'}</td>}
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
    );

    return (
        <div ref={overviewRootRef} className="overview-root" style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif", background: palette.bg, minHeight: '100vh', color: palette.text, maxWidth: 1100, margin: '0 auto' }}>
            <style>{`
                @media print {
                    @page { margin: 0.25in; }
                    body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: white !important; }
                    .labor-chart-section { max-width: 100% !important; overflow: hidden !important; }
                    .labor-table-scroll { display: none !important; }
                    .no-print { display: none !important; }
                    .overview-root {
                        max-width: 100% !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: white !important;
                        font-size: 80% !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .overview-root * {
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .overview-root td, .overview-root th {
                        padding: 2px 4px !important;
                        font-size: 8px !important;
                        line-height: 1.4 !important;
                    }
                    .overview-root h3 {
                        font-size: 9px !important;
                        margin-bottom: 4px !important;
                    }
                    .overview-root div[style*="padding: 16"] {
                        padding: 10px !important;
                    }
                    .overview-root div[style*="marginBottom: 14"] {
                        margin-bottom: 8px !important;
                    }
                    .overview-root div[style*="gap:"],
                    .overview-root div[style*="gap :"] {
                        gap: 4px !important;
                    }
                    .overview-content { padding: 0 4px 10px !important; }
                    .top-cards-grid { grid-template-columns: repeat(4, minmax(0, 1fr)) !important; gap: 5px !important; width: 100% !important; margin: 0 !important; margin-top: 30px !important; margin-bottom: 20px !important; }
                    .top-cards-grid.vg-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
                    .top-cards-grid > * { box-sizing: border-box !important; padding: 7px !important; border-radius: 6px !important; }
                    .top-cards-grid * { font-size: 8px !important; }
                    .top-cards-grid h3 { font-size: 8px !important; margin-bottom: 2px !important; }
                    .top-cards-grid div[style*="fontSize: 17"],
                    .top-cards-grid div[style*="fontSize: 18"] { font-size: 14px !important; }
                    .top-cards-grid div[style*="padding:"] { padding: 4px 6px !important; }
                    .top-cards-grid div[style*="gap:"] { gap: 4px !important; }
                    .top-cards-grid .safety-card *, .top-cards-grid .co-card *, .top-cards-grid .status-card * { font-size: 11px !important; }
                    .top-cards-grid .safety-card h3, .top-cards-grid .co-card h3, .top-cards-grid .status-card h3 { font-size: 10px !important; }
                    .top-cards-grid .safety-card div[style*="fontSize: 17"] { font-size: 17px !important; }
                    .top-cards-grid .status-card .status-row { padding: 7px 12px !important; }
                    .top-cards-grid .status-card .status-label { font-size: 9px !important; font-weight: 700 !important; }
                    .top-cards-grid .status-card .status-indicator { font-size: 8px !important; font-weight: 800 !important; }
                    .top-cards-grid .status-card .status-dot { width: 12px !important; height: 12px !important; }
                    .date-long { display: none !important; }
                    .date-short { display: inline !important; }
                    .notes-section-empty { display: none !important; }
                    .billing-notes-empty { display: none !important; }
                    .labor-chart-section .chart-legend-print { display: flex !important; }
                    .labor-chart-section .chart-legend-screen { display: none !important; }
                    .scope-section { margin-top: 4px !important; margin-bottom: 0 !important; padding-bottom: 0 !important; }
                    .quantity-tracking-section table { font-size: 65% !important; }
                    .quantity-tracking-section td, .quantity-tracking-section th { padding: 2px 3px !important; line-height: 1.2 !important; }
                    .quantity-tracking-section h3 { margin-bottom: 4px !important; }
                    .budget-overview-section { overflow: visible !important; }
                    .budget-overview-section > div { overflow: visible !important; }
                    .budget-overview-section td, .budget-overview-section th {
                        padding: 1px 3px !important;
                        font-size: 6.5px !important;
                        min-width: 0 !important;
                        white-space: normal !important;
                        word-break: break-word !important;
                    }
                    .budget-overview-section th { font-size: 6px !important; letter-spacing: 0 !important; }
                    .budget-overview-section table { width: 100% !important; table-layout: auto !important; }
                    /* The look ahead prints only when it is open on screen —
                       when collapsed it isn't rendered at all, so there is
                       nothing here to hide. Open, it prints as a compact
                       table like the rest of the sheet, and its own Export
                       button is .no-print. */
                    .look-ahead-card { page-break-inside: avoid !important; }
                    .look-ahead-card table { width: 100% !important; table-layout: auto !important; }
                    .look-ahead-card td, .look-ahead-card th { padding: 1px 3px !important; font-size: 7px !important; line-height: 1.3 !important; }
                    .look-ahead-card h3 { font-size: 8px !important; }
                    .look-ahead-section { margin-bottom: 6px !important; }
                    .manpower-curve-section { page-break-inside: avoid !important; box-shadow: none !important; }
                    .title-bar { padding-left: 0 !important; padding-right: 0 !important; }
                    .top-cards-grid { padding-top: 20px !important; }
                    .quantity-tracking-section { padding-top: 20px !important; }
                }
            `}</style>

            {/* ═══════════ HERO BANNER (image only) ═══════════ */}
            {/* Off the print sheet, but the VoltaGrid Export keeps the photo (export-banner) and hides its buttons */}
            <div className="no-print export-banner" ref={bannerBoxRef} style={{
                position: 'relative',
                width: '100%',
                height: 120,
                //borderRadius: '0 0 18px 18px',
                overflow: 'hidden',
            }}>
                {/* Background image */}
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: `url(${bannerImage || defaultBannerImg})`,
                    backgroundSize: 'cover',
                    backgroundPositionX: 'center',
                    backgroundPositionY: `${(bannerAdjust || bannerView).y}%`,
                    transform: `scale(${(bannerAdjust || bannerView).zoom / 100})`,
                    transformOrigin: 'center center',
                    filter: bannerAdjust ? 'none' : 'brightness(0.85)',
                    cursor: bannerAdjust ? 'grab' : 'default',
                    touchAction: bannerAdjust ? 'none' : 'auto',
                }}
                    onPointerDown={handleBannerDragStart}
                    onPointerMove={handleBannerDragMove}
                    onPointerUp={handleBannerDragEnd}
                    onPointerCancel={handleBannerDragEnd}
                />
                {bannerAdjust && (
                    <div className="no-print" style={{
                        position: 'absolute', left: 18, bottom: 12, zIndex: 3, display: 'flex', alignItems: 'center', gap: 10,
                        padding: '6px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(6px)',
                        border: `1px solid ${palette.border}`, fontSize: 12, color: palette.muted,
                    }}>
                        <span>Drag the photo up or down</span>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            Zoom
                            <input type="range" min={100} max={300} step={5} value={bannerAdjust.zoom}
                                aria-label="Banner zoom"
                                onChange={(e) => setBannerAdjust(clampView({ ...bannerAdjust, zoom: Number(e.target.value) }))}
                                style={{ width: 120 }} />
                            <span style={{ fontVariantNumeric: 'tabular-nums', minWidth: 36 }}>{bannerAdjust.zoom}%</span>
                        </label>
                        <button type="button" onClick={() => setBannerAdjust({ y: 40, zoom: 100 })} disabled={bannerBusy}
                            style={{ padding: '4px 8px', borderRadius: 8, border: `1px solid ${palette.border}`, background: '#fff', cursor: 'pointer', fontSize: 12, color: palette.muted }}>
                            Reset
                        </button>
                    </div>
                )}
                {/* Gradient overlay — white fade at bottom */}
                {/* <div style={{
                    position: 'absolute', inset: 0,
                    background: `linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.55) 80%, ${palette.bg} 100%)`,
                }} /> */}

                {/* Change / remove banner — uploads go to blob storage (jobBanner.js) */}
                <input ref={bannerFileRef} type="file" accept="image/*" onChange={handleBannerUpload} style={{ display: 'none' }} />
                <div className="no-print" style={{ position: 'absolute', top: 14, right: 18, zIndex: 3, display: 'flex', gap: 6, alignItems: 'center' }}>
                    {!bannerAdjust && (
                        <button type="button"
                            onClick={() => setBannerMenuOpen((o) => !o)}
                            aria-expanded={bannerMenuOpen}
                            title={bannerMenuOpen ? 'Hide banner options' : 'Banner options'}
                            style={{
                                padding: 5, borderRadius: '50%', lineHeight: 0, order: 9,
                                background: bannerMenuOpen ? '#fff' : 'rgba(255,255,255,0.45)', backdropFilter: 'blur(6px)',
                                border: `1px solid ${bannerMenuOpen ? palette.border : 'rgba(255,255,255,0.5)'}`, cursor: 'pointer',
                                color: bannerMenuOpen ? palette.accent : 'rgba(255,255,255,0.9)', transition: 'all .15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = palette.accent; e.currentTarget.style.borderColor = palette.border; }}
                            onMouseLeave={e => { if (bannerMenuOpen) return; e.currentTarget.style.background = 'rgba(255,255,255,0.45)'; e.currentTarget.style.color = 'rgba(255,255,255,0.9)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><circle cx="5" cy="12" r="2.2" /><circle cx="12" cy="12" r="2.2" /><circle cx="19" cy="12" r="2.2" /></svg>
                        </button>
                    )}
                    {bannerImage && bannerAdjust && (<>
                        <button type="button" onClick={() => setBannerAdjust(null)} disabled={bannerBusy}
                            title="Cancel banner adjustment"
                            style={{ padding: '6px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(6px)', border: `1px solid ${palette.border}`, cursor: 'pointer', fontSize: 12, fontWeight: 500, color: palette.muted }}>
                            Cancel
                        </button>
                        <button type="button" onClick={handleBannerAdjustSave} disabled={bannerBusy}
                            title="Save banner position"
                            style={{ padding: '6px 12px', borderRadius: 10, background: palette.accent, border: `1px solid ${palette.accent}`, cursor: bannerBusy ? 'wait' : 'pointer', fontSize: 12, fontWeight: 500, color: '#fff' }}>
                            {bannerBusy ? 'Saving...' : 'Save position'}
                        </button>
                    </>)}
                    {bannerImage && bannerMenuOpen && !bannerAdjust && (
                        <button
                            onClick={() => { setBannerAdjust({ ...bannerView }); setBannerMenuOpen(false); }}
                            disabled={bannerBusy}
                            style={{
                                padding: 8, borderRadius: '50%', lineHeight: 0,
                                background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(6px)',
                                border: `1px solid ${palette.border}`, cursor: bannerBusy ? 'wait' : 'pointer',
                                color: palette.muted, transition: 'all .15s',
                            }}
                            title="Adjust banner position and zoom"
                            onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = palette.accent; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.75)'; e.currentTarget.style.color = palette.muted; }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 9l-3 3 3 3" /><path d="M9 5l3-3 3 3" /><path d="M15 19l-3 3-3-3" /><path d="M19 9l3 3-3 3" /><path d="M2 12h20" /><path d="M12 2v20" /></svg>
                        </button>
                    )}
                    {bannerImage && bannerMenuOpen && !bannerAdjust && (
                        <button
                            onClick={handleBannerRemove}
                            disabled={bannerBusy}
                            style={{
                                padding: 8, borderRadius: '50%', lineHeight: 0,
                                background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(6px)',
                                border: `1px solid ${palette.border}`, cursor: bannerBusy ? 'wait' : 'pointer',
                                color: palette.muted, transition: 'all .15s',
                            }}
                            title="Remove banner image (back to default)"
                            onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#b91c1c'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.75)'; e.currentTarget.style.color = palette.muted; }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /></svg>
                        </button>
                    )}
                    {bannerMenuOpen && !bannerAdjust && <button
                        onClick={() => bannerFileRef.current?.click()}
                        disabled={bannerBusy}
                        style={{
                            padding: 8, borderRadius: '50%', lineHeight: 0,
                            background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(6px)',
                            border: `1px solid ${palette.border}`, cursor: bannerBusy ? 'wait' : 'pointer',
                            color: palette.muted, transition: 'all .15s',
                        }}
                        title={bannerBusy ? 'Uploading...' : 'Change banner image'}
                        onMouseEnter={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = palette.accent; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.75)'; e.currentTarget.style.color = palette.muted; }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>}
                </div>
            </div>

            {/* ═══════════ TITLE BAR (below image) ═══════════ */}
            <div className="title-bar" style={{ padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', color: palette.text }}>
                            {selectedJobDetails?.BU_Description || 'Project Dashboard'}
                        </h1>
                        {isVoltaGrid && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 999, background: palette.blue700, fontSize: 10, fontWeight: 700, color: '#fff', letterSpacing: '.04em', textTransform: 'uppercase' }}>
                                VoltaGrid
                            </div>
                        )}
                        <StatusPill level={overallStatus} label={overallStatus === 'green' ? 'On Track' : overallStatus === 'orange' ? 'At Risk' : 'Critical'}
                            onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setOverallStatusPopup({ top: r.bottom + window.scrollY, left: r.left + window.scrollX }); }}
                        />
                        <div
                            onClick={e => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setPctCompletePopup(prev => prev ? null : { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
                            }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, background: palette.blue50, fontSize: 10, fontWeight: 700, color: palette.blue700, border: `1px solid ${palette.blue200}`, cursor: 'pointer', userSelect: 'none' }}
                            title="Click to see how this is calculated"
                        >
                            {jobDataReady ? `${pctComplete}%` : '—'} Complete
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>
                        </div>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, background: palette.blue50, fontSize: 10, fontWeight: 600, color: palette.text, border: `1px solid ${palette.blue200}` }}>
                            <span style={{ color: palette.muted }}><span className="date-long">{startDateActual ? moment(startDateActual).format('MMMM D, YYYY') : '—'}</span><span className="date-short" style={{ display: 'none' }}>{startDateActual ? moment(startDateActual).format('M/D/YY') : '—'}</span></span>
                            <span style={{ color: palette.blue300 }}>–</span>
                            <span style={{ color: palette.muted }}><span className="date-long">{projectComplete ? moment(projectComplete).format('MMMM D, YYYY') : '—'}</span><span className="date-short" style={{ display: 'none' }}>{projectComplete ? moment(projectComplete).format('M/D/YY') : '—'}</span></span>
                            {daysRemaining !== null && (
                                <>
                                    <span style={{ color: palette.blue300 }}>·</span>
                                    <span style={{ fontWeight: 800, color: daysRemaining < 0 ? palette.red : daysRemaining <= 30 ? palette.orange : palette.blue700 }}>{Math.abs(daysRemaining)}</span>
                                    <span style={{ color: palette.muted }}>{daysRemaining < 0 ? 'days over' : 'days left'}</span>
                                </>
                            )}
                        </div>
                        {isVoltaGrid && (
                            <button className="no-print" onClick={handleVoltaGridExport} disabled={exportBusy} title={VG_SHOW_CASH_FLOW ? 'Export the overview as PDF, the labor graph and the cash flow as Excel' : 'Export the overview as PDF and the labor graph as Excel'}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999, background: palette.card, border: `1px solid ${palette.border}`, color: palette.muted, cursor: exportBusy ? 'wait' : 'pointer', fontSize: 10, fontWeight: 600, transition: 'all .15s' }}
                                onMouseEnter={e => { if (exportBusy) return; e.currentTarget.style.borderColor = palette.accent; e.currentTarget.style.color = palette.accent; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = palette.border; e.currentTarget.style.color = palette.muted; }}>
                                {icons.download}{exportBusy ? 'Exporting...' : 'Export'}
                            </button>
                        )}
                    </div>
                    <div style={{ fontSize: 11, display: 'flex', gap: 14, marginTop: 3, flexWrap: 'wrap' }}>
                        <span style={{ color: palette.muted }}>Job # <strong style={{ color: palette.text }}>{selectedJobDetails?.BU_ID || selectedJob}</strong></span>
                        <span style={{ color: palette.border }}>|</span>
                        <span style={{ color: palette.muted }}>Report Date: <strong style={{ color: palette.text }}>{versionInfo?.Version_Start_DateTime ? new Date(versionInfo.Version_Start_DateTime).toLocaleDateString() : '—'}</strong></span>
                        {[
                            // VoltaGrid: no Client field (the report is for the client).
                            ...(isVoltaGrid ? [] : [['Client', selectedJobDetails?.AR_AB_Name]]),
                            ['PM', selectedJobDetails?.Project_Manager_Name],
                            ['Location', selectedJobDetails?.Address_State_Name],
                            ['Team', selectedJobDetails?.Cat_Code_BU_27],
                            ['Contract', selectedJobDetails?.Cat_Code_BU_08],
                            ['Status', selectedJobDetails?.Job_Status === 'C' ? 'Complete' : 'Active'],
                        ].map(([label, value]) => (
                            <div key={label} style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                                <span style={{ fontSize: 9, fontWeight: 600, color: palette.muted, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</span>
                                <span style={{ fontSize: 11, fontWeight: 700, color: palette.text }}>{value || '—'}</span>
                            </div>
                        ))}
                    </div>
                </div>
                {!isVoltaGrid && <div className="no-print overview-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <ToolbarBtn onClick={handleCOLogExport} title="Download this version's CO log as Excel">
                        {icons.download} CO Log
                    </ToolbarBtn>
                    <ToolbarBtn onClick={handleInvoiceExport} disabled={invoiceBusy} title="Download the job's billing summaries as Excel">
                        {icons.download} {invoiceBusy ? 'Loading…' : 'Invoices'}
                    </ToolbarBtn>
                    <ToolbarBtn onClick={handleLookAheadExport} title="Download the 2 week look ahead as Excel">
                        {icons.download} Look Ahead
                    </ToolbarBtn>
                    <ToolbarBtn onClick={() => window.print()} title="Print the overview">
                        {icons.printer} Print
                    </ToolbarBtn>
                </div>}
            </div>

            {/* Content area with padding */}
            <div className="overview-content" style={{ padding: '0 24px 20px' }}>

                {/* ═══════════ PROJECT SCOPE (only shown if scope exists) ═══════════ */}
                {selectedJobDetails?.Scope_of_Work && (
                    <div className="scope-section" style={{ fontSize: 14, color: palette.text, lineHeight: 1.5, marginBottom: 20 }}>
                        {selectedJobDetails.Scope_of_Work}
                    </div>
                )}

                {/* ═══════════ ROW 1: Budget + Rework | Safety | Change Orders | Status ═══════════ */}
                <div className={`top-cards-grid${isVoltaGrid ? ' vg-grid' : ''}`} style={{ display: 'grid', gridTemplateColumns: isVoltaGrid ? 'repeat(3, minmax(0, 1fr))' : '1.3fr 1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>

                    {/* BUDGET METRICS + REWORK HOURS (VoltaGrid: revenue lives in the Change Orders card) */}
                    {!isVoltaGrid && <Card>
                        <SectionTitle icon={icons.dollar}>{isVoltaGrid ? 'Revenue (Billable)' : 'Budget & Rework'}</SectionTitle>
                        {revenueTable}
                        {/* Rework hours inline (VoltaGrid: cost-plus, rework is not a client-facing metric) */}
                        {!isVoltaGrid && <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: `linear-gradient(135deg, ${palette.blue100} 0%, ${palette.blue50} 100%)`, border: `1px solid ${palette.blue200}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {icons.wrench}
                                    <span style={{ fontSize: 10, fontWeight: 700, color: palette.text, textTransform: 'uppercase' }}>Rework Hours</span>
                                    {reworkApiData.length > 0 && <span style={{ fontSize: 8, color: palette.muted, fontWeight: 500 }}>(HCSS)</span>}
                                    {reworkApiLoading && <span style={{ fontSize: 8, color: palette.muted }}>loading...</span>}
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 800, color: totalReworkHours > 0 ? palette.blue700 : palette.muted }}>{Math.round(totalReworkHours * 10) / 10}</div>
                            </div>
                        </div>}
                    </Card>}

                    {/* SAFETY KEY METRICS */}
                    <Card className="safety-card">
                        <SectionTitle icon={icons.shield}>Safety Key Metrics</SectionTitle>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                            {[
                                { label: 'Total Hours', value: Math.round(totalActualLaborHours).toLocaleString(), shade: palette.blue50, editable: false, stateValue: null },
                                { label: 'Incidents', value: safetyIncidents, shade: palette.blue50, editable: true, stateValue: safetyIncidents },
                                { label: 'Recordables', value: safetyQAQC, shade: palette.blue50, editable: true, stateValue: safetyQAQC },
                                { label: 'First Aid', value: safetyFirstAid, shade: palette.blue50, editable: true, stateValue: safetyFirstAid },
                                { label: 'Near Miss', value: safetyNearMiss, shade: palette.blue50, editable: true, stateValue: safetyNearMiss },
                                { label: 'Stop Work', value: safetyStopWork, shade: palette.blue50, editable: true, stateValue: safetyStopWork },
                            ].map(({ label, value, shade, editable, stateValue }) => (
                                <div key={label} style={{ textAlign: 'center', padding: '8px 6px', borderRadius: 8, background: shade, border: `1px solid ${palette.blue200}`, cursor: editable && !lockedVersion ? 'pointer' : 'default' }}
                                    onClick={() => { if (editable && !lockedVersion && editingSafetyKey !== label) setEditingSafetyKey(label); }}>
                                    {editingSafetyKey === label ? (
                                        <input
                                            type="number"
                                            defaultValue={stateValue || 0}
                                            autoFocus
                                            onBlur={e => handleSafetyMetricSave(label, e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleSafetyMetricSave(label, e.target.value); if (e.key === 'Escape') setEditingSafetyKey(null); }}
                                            onClick={e => e.stopPropagation()}
                                            style={{ width: '100%', textAlign: 'center', fontSize: 17, fontWeight: 800, color: palette.blue700, border: `1px solid ${palette.accent}`, borderRadius: 4, padding: '2px 0', outline: 'none', background: 'white' }}
                                        />
                                    ) : (
                                        <div style={{ fontSize: 17, fontWeight: 800, color: palette.blue700, letterSpacing: '-0.02em' }}>{value}</div>
                                    )}
                                    <div style={{ fontSize: 8, fontWeight: 600, color: palette.muted, textTransform: 'uppercase', marginTop: 3 }}>{label}</div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* CHANGE ORDER METRICS */}
                    <Card className="co-card">
                        {isVoltaGrid && (<>
                            <SectionTitle icon={icons.dollar}>Revenue (Billable)</SectionTitle>
                            <div style={{ marginBottom: 12 }}>{revenueTable}</div>
                        </>)}
                        <SectionTitle icon={icons.fileText}>Change Orders</SectionTitle>
                        {/* Column Headers */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 8px 6px', fontSize: 8, fontWeight: 700, color: palette.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${palette.border}`, marginBottom: 6 }}>
                            <span style={{ flex: 1 }}>Status</span>
                            <span style={{ width: 50, textAlign: 'center' }}>Count</span>
                            <span style={{ width: 80, textAlign: 'right' }}>Revenue</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {[
                                { label: 'Approved', value: fNum(changeOrderSummary.approved, 1), count: changeOrderSummary.approvedCount, bg: `linear-gradient(135deg, ${palette.blue50}, ${palette.card})` },
                                // VoltaGrid: one Pending line (performed + not performed) — the client log doesn't split them.
                                ...(isVoltaGrid ? [
                                    { label: 'Pending', value: fNum((pNum(changeOrderSummary.pendingPerformed) || 0) + (pNum(changeOrderSummary.pendingNotPerformed) || 0), 1), count: (changeOrderSummary.pendingPerformedCount || 0) + (changeOrderSummary.pendingNotPerformedCount || 0), bg: `linear-gradient(135deg, ${palette.blue100}, ${palette.blue50})` },
                                ] : [
                                    { label: 'Pending (Performed)', value: fNum(changeOrderSummary.pendingPerformed, 1), count: changeOrderSummary.pendingPerformedCount, bg: `linear-gradient(135deg, ${palette.blue100}, ${palette.blue50})` },
                                    { label: 'Pending (Not Performed)', value: fNum(changeOrderSummary.pendingNotPerformed, 1), count: changeOrderSummary.pendingNotPerformedCount, bg: `linear-gradient(135deg, ${palette.blue50}, ${palette.card})` },
                                ]),
                                { label: 'Not Submitted', value: fNum(changeOrderSummary.notSubmitted, 1), count: changeOrderSummary.notSubmittedCount, bg: `linear-gradient(135deg, ${palette.blue100}, ${palette.blue50})` },
                                { label: 'Denied', value: fNum(changeOrderSummary.denied, 1), count: changeOrderSummary.deniedCount, bg: `linear-gradient(135deg, ${palette.blue50}, ${palette.card})` },
                            ].map(({ label, value, count, bg }) => (
                                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 8px', borderRadius: 6, background: bg, border: `1px solid ${palette.blue100}`, fontSize: 9 }}>
                                    <span style={{ flex: 1, fontWeight: 600, color: palette.text }}>{label}</span>
                                    <span style={{ width: 50, textAlign: 'center', fontSize: 9, color: palette.muted, fontWeight: 700 }}>{count}</span>
                                    <span style={{ width: 80, textAlign: 'right', fontWeight: 800, color: palette.blue700, fontSize: 9 }}>{value}</span>
                                </div>
                            ))}
                        </div>
                        {/* Divider */}
                        {!isVoltaGrid && <div style={{ borderTop: `1px solid ${palette.border}`, margin: '8px 0' }} />}
                        {/* Cumulative Impact Assessment (not on the VoltaGrid report) */}
                        {!isVoltaGrid && (() => {
                            const isImpact = totalRevenue > 0 && (changeOrderSummary.approved / totalRevenue) > 0.15;
                            const activeColor = isImpact ? '#d32f2f' : palette.blue500;
                            return (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 8px' }}>
                                    <span style={{ fontSize: 9, fontWeight: 700, color: palette.muted, letterSpacing: '.03em' }}>Cumulative Impact (&gt;15%)</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: activeColor, boxShadow: `0 0 5px ${activeColor}50`, flexShrink: 0 }} />
                                        <span style={{ fontSize: 10, fontWeight: 800, color: activeColor }}>{isImpact ? 'Yes' : 'No'}</span>
                                    </div>
                                </div>
                            );
                        })()}
                    </Card>

                    {/* PROJECT STATUS */}
                    <Card className="status-card">
                        <SectionTitle icon={icons.barChart}>Project Status</SectionTitle>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {[
                                { label: 'Productivity', status: statuses.productivity, key: 'productivity' },
                                { label: 'Budget / Cost', status: statuses.budget, key: 'budget' },
                                { label: 'Schedule', status: statuses.schedule, key: 'schedule' },
                                { label: 'Billing', status: statuses.billing, key: 'billing' },
                                { label: 'Safety', status: statuses.safety, key: 'safety' },
                            ].filter(({ key }) => !isVoltaGrid || key !== 'billing').map(({ label, status, key }) => {
                                const isGrey = status === 'grey';
                                const displayStatus = isGrey ? 'grey' : status;
                                const bgColor = isGrey ? '#f1f5f9' : statusBg(status);
                                const dotColor = isGrey ? '#94a3b8' : statusColor(status);
                                const textColor = isGrey ? '#94a3b8' : statusColor(status);
                                const statusLabel = isGrey ? 'N/A' : status === 'green' ? 'Good' : status === 'orange' ? 'Watch' : 'Alert';
                                return (
                                <div key={label} onClick={(e) => {
                                    if (isGrey) return;
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    if (statusPopup && statusPopup.category === key) { setStatusPopup(null); }
                                    else { setStatusPopup({ category: key, anchorRect: { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX } }); }
                                }} className="status-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', borderRadius: 6, background: bgColor, transition: 'all .2s', cursor: isGrey ? 'default' : 'pointer', opacity: isGrey ? 0.6 : 1 }}>
                                    <span className="status-label" style={{ fontSize: 10, fontWeight: 600, color: isGrey ? palette.muted : palette.text }}>{label}</span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        <span className="status-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, boxShadow: `0 0 6px ${dotColor}40` }} />
                                        <span className="status-indicator" style={{ fontSize: 9, fontWeight: 700, color: textColor, textTransform: 'uppercase' }}>
                                            {statusLabel}
                                        </span>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </Card>
                    {/* Percent Complete Popup */}
                    {pctCompletePopup && (() => {
                        const overall = quantityTrackingData?.['__overall'];
                        const disciplines = quantityTrackingData ? Object.entries(quantityTrackingData).filter(([k]) => k !== '__overall') : [];
                        return (
                            <>
                                <div onClick={() => setPctCompletePopup(null)} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, minHeight: '100vh', zIndex: 9998 }} />
                                <div style={{
                                    position: 'absolute',
                                    top: pctCompletePopup.top + 4,
                                    left: Math.min(pctCompletePopup.left, window.innerWidth - 400),
                                    zIndex: 9999,
                                    backgroundColor: '#1a1a2e',
                                    color: '#e0e0e0',
                                    border: '1px solid #2d6cdf',
                                    borderRadius: 8,
                                    padding: '14px 18px',
                                    minWidth: 520,
                                    maxWidth: 640,
                                    maxHeight: '80vh',
                                    overflowY: 'auto',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    lineHeight: 1.6,
                                }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                                        % Complete Calculation
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 10 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Overall Formula</div>
                                        <div style={{ color: '#d5d8dc' }}>Overall % = Σ(Phase Earned Hrs) ÷ Σ(Budget Hrs)</div>
                                        <div style={{ color: '#aaa', marginTop: 4 }}>Per Code:</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>Total Installed = Scorecard + HeavyJob (FP doc type)</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>Phase % = min(Total Installed ÷ Planned Qty, 100%)</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>Earned Hrs = Phase % × Budget Hrs</div>
                                    </div>
                                    {disciplines.map(([name, d]) => (
                                        <div key={name} style={{ marginBottom: 10 }}>
                                            <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '7px 10px', marginBottom: 2 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 1 }}>
                                                    <span style={{ color: '#5dade2', fontWeight: 'bold', fontSize: 11 }}>{name} — {d.pctComplete}% complete</span>
                                                    <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: d.mathMode === 'Sum' ? '#1a3a2a' : d.mathMode === 'Row' ? '#2a2a1a' : '#1a2a3a', color: d.mathMode === 'Sum' ? '#82e0aa' : d.mathMode === 'Row' ? '#f5c842' : '#7CAFDD', fontWeight: 700 }}>
                                                        {d.mathMode || 'Max'}
                                                    </span>
                                                    {(d.mathMode === 'Max' || d.mathMode === 'Row') && d.mathDrivingCode && (
                                                        <span style={{ fontSize: 9, color: '#aaa' }}>← {d.mathDrivingCode}</span>
                                                    )}
                                                </div>
                                                <div style={{ color: '#aaa', fontSize: 9, marginBottom: 2 }}>
                                                    {d.mathMode === 'Sum'
                                                        ? 'Planned = Σ all codes\' qty  |  '
                                                        : d.mathMode === 'Row'
                                                        ? `Planned = Row ${d.mathDrivingCode} qty  |  `
                                                        : `Planned = max-qty code (${d.mathDrivingCode || '?'})  |  `}
                                                    Earned ({d.earnedHours?.toLocaleString(undefined, {maximumFractionDigits: 0})} hrs) ÷ Budget ({d.budgetHours?.toLocaleString(undefined, {maximumFractionDigits: 0})} hrs) = {d.pctComplete}%
                                                </div>
                                                {d.codeDetails && d.codeDetails.length > 0 && (
                                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9 }}>
                                                        <thead>
                                                            <tr style={{ color: '#888', borderBottom: '1px solid #2a2a4a' }}>
                                                                <td style={{ padding: '2px 3px' }}>Code</td>
                                                                <td style={{ padding: '2px 3px', textAlign: 'right' }}>Planned</td>
                                                                <td style={{ padding: '2px 3px', textAlign: 'right', color: '#82e0aa' }}>Scorecard</td>
                                                                <td style={{ padding: '2px 3px', textAlign: 'right', color: '#7CAFDD' }}>HJ (FP)</td>
                                                                <td style={{ padding: '2px 3px', textAlign: 'right' }}>Total Inst</td>
                                                                <td style={{ padding: '2px 3px', textAlign: 'right' }}>Phase%</td>
                                                                <td style={{ padding: '2px 3px', textAlign: 'right' }}>Bud Hrs</td>
                                                                <td style={{ padding: '2px 3px', textAlign: 'right' }}>Earned Hrs</td>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {d.codeDetails.map(cd => {
                                                                const isDriving = (d.mathMode === 'Max' || d.mathMode === 'Row') && cd.cc === d.mathDrivingCode;
                                                                const isGray = (d.mathMode === 'Max' || d.mathMode === 'Row') && cd.cc !== d.mathDrivingCode;
                                                                return (
                                                                <tr key={cd.cc} style={{ color: cd.plannedQty === 0 ? '#e67e22' : isGray ? '#555' : '#d5d8dc', borderBottom: '1px solid #1a1a2e', background: isDriving ? '#0d2040' : 'transparent' }}>
                                                                    <td style={{ padding: '2px 3px', fontWeight: 600, color: isDriving ? '#7CAFDD' : undefined }}>{cd.cc}{isDriving ? ' ★' : ''}</td>
                                                                    <td style={{ padding: '2px 3px', textAlign: 'right' }}>{cd.plannedQty > 0 ? cd.plannedQty.toLocaleString(undefined, {maximumFractionDigits: 1}) : <span style={{color:'#e67e22'}}>—</span>}</td>
                                                                    <td style={{ padding: '2px 3px', textAlign: 'right', color: cd.scorecardInstalled > 0 ? '#82e0aa' : '#555' }}>{cd.scorecardInstalled > 0 ? cd.scorecardInstalled.toLocaleString(undefined, {maximumFractionDigits: 1}) : '—'}</td>
                                                                    <td style={{ padding: '2px 3px', textAlign: 'right', color: cd.heavyJobInstalled > 0 ? '#7CAFDD' : '#555' }}>{cd.heavyJobInstalled > 0 ? cd.heavyJobInstalled.toLocaleString(undefined, {maximumFractionDigits: 1}) : '—'}</td>
                                                                    <td style={{ padding: '2px 3px', textAlign: 'right', fontWeight: 600 }}>{cd.installedQty > 0 ? cd.installedQty.toLocaleString(undefined, {maximumFractionDigits: 1}) : '—'}</td>
                                                                    <td style={{ padding: '2px 3px', textAlign: 'right', color: cd.plannedQty === 0 ? '#e67e22' : parseFloat(cd.phasePct) >= 100 ? '#f5c842' : '#82e0aa' }}>{cd.plannedQty === 0 ? 'no qty' : cd.phasePct + '%'}</td>
                                                                    <td style={{ padding: '2px 3px', textAlign: 'right' }}>{cd.budgetHrs > 0 ? cd.budgetHrs.toLocaleString(undefined, {maximumFractionDigits: 0}) : '—'}</td>
                                                                    <td style={{ padding: '2px 3px', textAlign: 'right', color: '#f5c842' }}>{cd.earnedHrs > 0 ? cd.earnedHrs.toLocaleString(undefined, {maximumFractionDigits: 0}) : '—'}</td>
                                                                </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>
                                                )}
                                                {d.codeDetails && d.codeDetails.some(cd => cd.plannedQty === 0 && cd.budgetHrs > 0) && (
                                                    <div style={{ color: '#e67e22', fontSize: 9, marginTop: 3, fontStyle: 'italic' }}>⚠ Codes with no planned qty → Phase% = 0, Earned Hrs = 0.</div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '8px 10px', color: '#d5d8dc', fontSize: 11, marginBottom: 8 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Overall Summary</div>
                                        <div>{`Total Earned: ${overall?.earnedHours?.toLocaleString(undefined, {maximumFractionDigits: 0}) ?? '0'} hrs`}</div>
                                        <div>{`Total Budget: ${overall?.budgetHours?.toLocaleString(undefined, {maximumFractionDigits: 0}) ?? '0'} hrs`}</div>
                                        <div style={{ marginTop: 4, fontWeight: 'bold' }}>{`Overall: ${overall?.earnedHours?.toLocaleString(undefined, {maximumFractionDigits: 0}) ?? '0'} ÷ ${overall?.budgetHours?.toLocaleString(undefined, {maximumFractionDigits: 0}) ?? '0'} = ${overall?.pctComplete ?? '0.0'}%`}</div>
                                    </div>
                                    <div style={{ fontSize: 10, color: '#888', fontStyle: 'italic' }}>
                                        Excludes Contingency, Contract Amount, and Total Costs disciplines. Codes shown in orange have no planned qty and contribute 0 earned hrs.
                                    </div>
                                    <div onClick={() => setPctCompletePopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>
                                        ✕ Close
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                    {/* Fcast Debug Popup */}
                    {fcastDebugPopup && (() => {
                        const p = progressScheduleData[fcastDebugPopup.discipline] || {};
                        return (
                            <>
                                <div onClick={() => setFcastDebugPopup(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                                <div style={{ position: 'absolute', top: fcastDebugPopup.top + 4, left: fcastDebugPopup.left, zIndex: 9999, backgroundColor: '#1a1a2e', color: '#e0e0e0', border: '1px solid #2d6cdf', borderRadius: 8, padding: '14px 18px', minWidth: 480, maxWidth: 500, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.35)', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                                        Fcast Debug — {fcastDebugPopup.discipline}
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 10 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Formula</div>
                                        <div style={{ color: '#82e0aa' }}>Fcast% = Actual Hrs ÷ PM EAC</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>PM EAC = Actual Hrs + ETC ({p.etcMode === 'regular' ? 'Scorecard forecastedHrs' : 'Burn Tool Phased hrs×crew'})</div>
                                        <div style={{ color: '#888', marginTop: 3 }}>Mode: <span style={{ color: p.etcMode === 'regular' ? '#82e0aa' : '#f5c842' }}>{p.etcMode === 'regular' ? 'Scorecard' : 'Burn Tool Phased'}</span></div>
                                    </div>
                                    {p.codeDebugRows && p.codeDebugRows.length > 0 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginBottom: 10 }}>
                                            <thead>
                                                <tr style={{ color: '#888', borderBottom: '1px solid #2a2a4a' }}>
                                                    <td style={{ padding: '2px 4px' }}>Code</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#F4B183' }}>Actual Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#82e0aa' }}>{p.etcMode === 'regular' ? 'ETC (Scorecard)' : 'ETC (BTP)'}</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#f5c842' }}>PM EAC</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#7CAFDD' }}>Budget Hrs</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {p.codeDebugRows.map(cd => (
                                                    <tr key={cd.cc} style={{ borderBottom: '1px solid #1a1a2e', color: '#d5d8dc' }}>
                                                        <td style={{ padding: '2px 4px', fontWeight: 600 }}>{cd.cc}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#F4B183' }}>{cd.phaseActualHrs > 0 ? cd.phaseActualHrs.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#82e0aa' }}>{cd.fcastHrs > 0 ? cd.fcastHrs.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}{cd.fcastHrs > 0 && p.etcMode === 'regular' && !cd.versionMatch ? <span style={{ color: '#e67e22', marginLeft: 3 }}>⚠</span> : null}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#f5c842', fontWeight: 600 }}>{cd.pmEAC > 0 ? cd.pmEAC.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#7CAFDD' }}>{cd.budgetHrs > 0 ? cd.budgetHrs.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                {(() => {
                                                    const totActual = p.codeDebugRows.reduce((s, cd) => s + (cd.phaseActualHrs || 0), 0);
                                                    const totETC = p.codeDebugRows.reduce((s, cd) => s + (cd.fcastHrs || 0), 0);
                                                    const totPmEAC = p.codeDebugRows.reduce((s, cd) => s + (cd.pmEAC || 0), 0);
                                                    const totBudget = p.codeDebugRows.reduce((s, cd) => s + (cd.budgetHrs || 0), 0);
                                                    return (
                                                        <tr style={{ borderTop: '2px solid #2a2a4a', fontWeight: 700, fontSize: 10 }}>
                                                            <td style={{ padding: '3px 4px', color: '#aaa' }}>Total</td>
                                                            <td style={{ padding: '3px 4px', textAlign: 'right', color: '#F4B183' }}>{totActual.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                                            <td style={{ padding: '3px 4px', textAlign: 'right', color: '#82e0aa' }}>{totETC.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                                            <td style={{ padding: '3px 4px', textAlign: 'right', color: '#f5c842' }}>{totPmEAC.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                                            <td style={{ padding: '3px 4px', textAlign: 'right', color: '#7CAFDD' }}>{totBudget.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                                                        </tr>
                                                    );
                                                })()}
                                            </tfoot>
                                        </table>
                                    )}
                                    <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '8px 10px', fontSize: 11 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Summary</div>
                                        <div>Actual Hrs: <span style={{ color: '#F4B183', fontWeight: 700 }}>{(p.totalActualHrs || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span></div>
                                        <div>Total PM EAC: <span style={{ color: '#f5c842', fontWeight: 700 }}>{(p.totalFcastHrs || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span></div>
                                        <div style={{ marginTop: 4, fontWeight: 'bold' }}>Fcast% = {(p.totalActualHrs || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} ÷ {(p.totalFcastHrs || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} = <span style={{ color: '#82e0aa' }}>{p.cumulativeFcast || '0%'}</span></div>
                                    </div>
                                    <div onClick={() => setFcastDebugPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>✕ Close</div>
                                </div>
                            </>
                        );
                    })()}
                    {/* Actual % Debug Popup */}
                    {actualDebugPopup && (() => {
                        const q = quantityTrackingData[actualDebugPopup.discipline] || {};
                        return (
                            <>
                                <div onClick={() => setActualDebugPopup(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                                <div style={{ position: 'absolute', top: actualDebugPopup.top + 4, left: actualDebugPopup.left, zIndex: 9999, backgroundColor: '#1a1a2e', color: '#e0e0e0', border: '1px solid #2d6cdf', borderRadius: 8, padding: '14px 18px', minWidth: 520, maxWidth: 560, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.35)', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                                        Actual % Debug — {actualDebugPopup.discipline}
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 10 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Formula</div>
                                        <div style={{ color: '#82e0aa' }}>Actual% = Earned Hrs ÷ Budget Hrs</div>
                                        <div style={{ color: '#aaa', marginTop: 4 }}>Per phase: Earned Hrs = min(Installed/Planned, 1) × Budget Hrs</div>
                                        <div style={{ color: '#aaa' }}>Math mode: <span style={{ color: '#7CAFDD' }}>{q.mathMode || 'Max'}</span>{q.mathDrivingCode ? <span> — driving code: <span style={{ color: '#7CAFDD' }}>{q.mathDrivingCode}</span></span> : null}</div>
                                    </div>
                                    {q.codeDetails && q.codeDetails.length > 0 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginBottom: 10 }}>
                                            <thead>
                                                <tr style={{ color: '#888', borderBottom: '1px solid #2a2a4a' }}>
                                                    <td style={{ padding: '2px 4px' }}>Code</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>UoM</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#7CAFDD' }}>Planned Qty</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#82e0aa' }}>Installed</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#aaa' }}>Phase %</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#7CAFDD' }}>Budget Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#f5c842' }}>Earned Hrs</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {q.codeDetails.map(cd => (
                                                    <tr key={cd.cc} style={{ borderBottom: '1px solid #1a1a2e', color: '#d5d8dc' }}>
                                                        <td style={{ padding: '2px 4px', fontWeight: 600 }}>{cd.cc}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{cd.uom || '—'}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#7CAFDD' }}>{cd.plannedQty > 0 ? cd.plannedQty.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#82e0aa' }}>
                                                            {cd.installedQty > 0 ? cd.installedQty.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
                                                            {(cd.scorecardInstalled > 0 || cd.heavyJobInstalled > 0) && (
                                                                <div style={{ fontSize: 8, color: '#888' }}>
                                                                    {cd.scorecardInstalled > 0 ? `SC:${cd.scorecardInstalled.toLocaleString()}` : ''}{cd.scorecardInstalled > 0 && cd.heavyJobInstalled > 0 ? ' + ' : ''}{cd.heavyJobInstalled > 0 ? `HJ:${cd.heavyJobInstalled.toLocaleString()}` : ''}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#aaa' }}>{cd.phasePct}%</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#7CAFDD' }}>{cd.budgetHrs > 0 ? cd.budgetHrs.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#f5c842' }}>{cd.earnedHrs > 0 ? cd.earnedHrs.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '8px 10px', fontSize: 11 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Summary</div>
                                        <div>Total Earned Hrs: <span style={{ color: '#f5c842', fontWeight: 700 }}>{(q.earnedHours || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span></div>
                                        <div>Total Budget Hrs: <span style={{ color: '#7CAFDD', fontWeight: 700 }}>{(q.budgetHours || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span></div>
                                        <div style={{ marginTop: 4, fontWeight: 'bold' }}>Actual% = {(q.earnedHours || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} ÷ {(q.budgetHours || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} = <span style={{ color: '#82e0aa' }}>{q.pctComplete || '0.0'}%</span></div>
                                    </div>
                                    <div onClick={() => setActualDebugPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>✕ Close</div>
                                </div>
                            </>
                        );
                    })()}
                    {/* Plan Debug Popup */}
                    {planDebugPopup && (() => {
                        const p = progressScheduleData[planDebugPopup.discipline] || {};
                        return (
                            <>
                                <div onClick={() => setPlanDebugPopup(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                                <div style={{ position: 'absolute', top: planDebugPopup.top + 4, left: planDebugPopup.left, zIndex: 9999, backgroundColor: '#1a1a2e', color: '#e0e0e0', border: '1px solid #2d6cdf', borderRadius: 8, padding: '14px 18px', minWidth: 440, maxWidth: 460, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.35)', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                                        Plan Debug — {planDebugPopup.discipline}
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 10 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Formula</div>
                                        <div style={{ color: '#82e0aa' }}>Plan% = Σ(phase hrs up to current week) ÷ Budget Hrs (DZ + CO)</div>
                                        <div style={{ color: '#aaa', marginTop: 4 }}>Current week: <span style={{ color: '#7CAFDD' }}>{p.currentWeek || '—'}</span></div>
                                    </div>
                                    {p.codeDebugRows && p.codeDebugRows.length > 0 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginBottom: 10 }}>
                                            <thead>
                                                <tr style={{ color: '#888', borderBottom: '1px solid #2a2a4a' }}>
                                                    <td style={{ padding: '2px 4px' }}>Code</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#f5c842' }}>Cumul Hrs (≤wk)</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#aaa' }}>Total Entered Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right', color: '#7CAFDD' }}>Budget Hrs</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {p.codeDebugRows.map(cd => (
                                                    <tr key={cd.cc} style={{ borderBottom: '1px solid #1a1a2e', color: '#d5d8dc' }}>
                                                        <td style={{ padding: '2px 4px', fontWeight: 600 }}>{cd.cc}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#f5c842' }}>{cd.codeCumulativeHrs > 0 ? cd.codeCumulativeHrs.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#aaa' }}>{cd.codeTotalPhaseHrs > 0 ? cd.codeTotalPhaseHrs.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#7CAFDD' }}>{cd.budgetHrs > 0 ? cd.budgetHrs.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '8px 10px', fontSize: 11 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Summary</div>
                                        <div>Cumulative Phase Hrs (≤ current week): <span style={{ color: '#f5c842', fontWeight: 700 }}>{(p.cumulativePlanHrs || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span></div>
                                        <div>Budget Hrs (DZ + CO): <span style={{ color: '#7CAFDD', fontWeight: 700 }}>{(p.budgetHrs || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}</span></div>
                                        <div style={{ marginTop: 4, fontWeight: 'bold' }}>Plan% = {(p.cumulativePlanHrs || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} ÷ {(p.budgetHrs || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} = <span style={{ color: '#82e0aa' }}>{p.cumulativePlan || '0%'}</span></div>
                                    </div>
                                    <div onClick={() => setPlanDebugPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>✕ Close</div>
                                </div>
                            </>
                        );
                    })()}
                    {/* SPI Debug Popup */}
                    {spiDebugPopup && (() => {
                        const d = spiDebugPopup;
                        // Use quantityTrackingData pctComplete so it matches the top-of-page value
                        const _qtEntry = d.label === 'Total Costs' ? quantityTrackingData?.['__overall'] : quantityTrackingData?.[d.label];
                        const _qtCodeDetail = !_qtEntry && d.codes?.length === 1 ? Object.values(quantityTrackingData || {}).flatMap(v => v?.codeDetails || []).find(cd => cd.cc === d.codes[0].cc) : null;
                        const displayPct = _qtEntry ? parseFloat(_qtEntry.pctComplete) : (_qtCodeDetail ? parseFloat(_qtCodeDetail.phasePct) : d.pctComplete);
                        return (
                            <>
                                <div onClick={() => setSpiDebugPopup(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                                <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 9999, backgroundColor: '#1a1a2e', color: '#e0e0e0', border: '1px solid #2d6cdf', borderRadius: 8, padding: '14px 18px', minWidth: 520, maxWidth: 650, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.35)', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                                        SPI Debug — {d.label}
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 10 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Formula</div>
                                        <div style={{ color: '#82e0aa' }}>SPI = Earned Hours ÷ Planned Hours</div>
                                        <div style={{ color: '#aaa' }}>Held at 1.00 until 25% complete (same rule as PF)</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>Earned Hours = % Complete × Budget Hours</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>% Complete = Installed Qty ÷ Total Qty</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>Planned Hours = cumulative hours from the Phase Hours / Labor Graphs page up to current week</div>
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 11 }}>
                                        <div><span style={{ color: '#aaa' }}>Budget Hours:</span> <span style={{ color: '#f5c842' }}>{d.budgetHrs?.toFixed(1)}</span></div>
                                        <div><span style={{ color: '#aaa' }}>% Complete:</span> <span style={{ color: '#f5c842' }}>{displayPct?.toFixed(2)}%</span></div>
                                        <div><span style={{ color: '#aaa' }}>Earned Hours:</span> <span style={{ color: '#82e0aa' }}>{d.earnedHrs?.toFixed(1)}</span> <span style={{ color: '#666' }}>(= {displayPct?.toFixed(2)}% × {d.budgetHrs?.toFixed(1)})</span></div>
                                        <div style={{ marginTop: 4, borderTop: '1px solid #333', paddingTop: 4 }}><span style={{ color: '#aaa' }}>Planned Hours:</span> <span style={{ color: '#e57373' }}>{d.plannedHrs?.toFixed(1)}</span></div>
                                        <div style={{ marginTop: 4, borderTop: '1px solid #333', paddingTop: 4 }}><span style={{ color: '#aaa' }}>SPI:</span> <span style={{ color: '#5dade2', fontWeight: 'bold' }}>{d.spi || '-'}</span> <span style={{ color: '#666' }}>(= {d.earnedHrs?.toFixed(1)} ÷ {d.plannedHrs?.toFixed(1)})</span></div>
                                    </div>
                                    {d.codes && d.codes.length > 1 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginBottom: 10 }}>
                                            <thead>
                                                <tr style={{ color: '#888', borderBottom: '1px solid #2a2a4a' }}>
                                                    <td style={{ padding: '2px 4px' }}>Code</td>
                                                    {d.codes[0]?.disc && <td style={{ padding: '2px 4px' }}>Discipline</td>}
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Budget Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Earned Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Planned Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>SPI</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {d.codes.filter(c => c.earnedHrs > 0 || c.plannedHrs > 0).map((c, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #1a1a2e' }}>
                                                        <td style={{ padding: '2px 4px', color: '#7CAFDD' }}>{c.cc}</td>
                                                        {c.disc !== undefined && <td style={{ padding: '2px 4px', color: '#aaa' }}>{c.disc}</td>}
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{c.budgetHrs?.toFixed(1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{c.earnedHrs?.toFixed(1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{c.plannedHrs?.toFixed(1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#5dade2' }}>{c.spi > 0 ? c.spi.toFixed(2) : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    <div onClick={() => setSpiDebugPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>✕ Close</div>
                                </div>
                            </>
                        );
                    })()}
                    {/* CPI Debug Popup */}
                    {cpiDebugPopup && (() => {
                        const d = cpiDebugPopup;
                        const earnedLabor$ = d.earnedLabor;
                        return (
                            <>
                                <div onClick={() => setCpiDebugPopup(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                                <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 9999, backgroundColor: '#1a1a2e', color: '#e0e0e0', border: '1px solid #2d6cdf', borderRadius: 8, padding: '14px 18px', minWidth: 520, maxWidth: 650, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.35)', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                                        CPI Debug — {d.label}
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 10 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Formula</div>
                                        <div style={{ color: '#82e0aa' }}>CPI = Earned Labor $ ÷ Actual Labor $</div>
                                        <div style={{ color: '#aaa' }}>Held at 1.00 until 25% complete (same rule as PF)</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>Earned Labor $ = % Complete × Labor Budget $</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>% Complete = Earned Hours ÷ Budget Hours</div>
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 11 }}>
                                        <div><span style={{ color: '#aaa' }}>Budget Hours:</span> <span style={{ color: '#f5c842' }}>{d.budgetHrs?.toFixed(1)}</span></div>
                                        <div><span style={{ color: '#aaa' }}>Earned Hours:</span> <span style={{ color: '#f5c842' }}>{d.earnedHrs?.toFixed(1)}</span></div>
                                        <div><span style={{ color: '#aaa' }}>% Complete:</span> <span style={{ color: '#f5c842' }}>{(d.physPct * 100)?.toFixed(2)}%</span> <span style={{ color: '#666' }}>(= {d.earnedHrs?.toFixed(1)} ÷ {d.budgetHrs?.toFixed(1)})</span></div>
                                        <div style={{ marginTop: 4, borderTop: '1px solid #333', paddingTop: 4 }}><span style={{ color: '#aaa' }}>Labor Budget $:</span> <span style={{ color: '#f5c842' }}>{fNum(d.laborBudget, 1)}</span></div>
                                        {(d.laborBudgetDZ != null || d.laborBudgetCO != null) && (
                                            <div style={{ paddingLeft: 12, fontSize: 10 }}>
                                                <div><span style={{ color: '#888' }}>└ DZ Budget (51*):</span> <span style={{ color: '#d4a843' }}>{fNum(d.laborBudgetDZ || 0, 1)}</span></div>
                                                <div><span style={{ color: '#888' }}>└ Approved CO (51*):</span> <span style={{ color: '#d4a843' }}>{fNum(d.laborBudgetCO || 0, 1)}</span></div>
                                            </div>
                                        )}
                                        <div><span style={{ color: '#aaa' }}>Earned Labor $:</span> <span style={{ color: '#82e0aa' }}>{fNum(earnedLabor$, 1)}</span> <span style={{ color: '#666' }}>(= {(d.physPct * 100)?.toFixed(2)}% × {fNum(d.laborBudget, 1)})</span></div>
                                        <div><span style={{ color: '#aaa' }}>Actual Labor $:</span> <span style={{ color: '#e57373' }}>{fNum(d.laborActual, 1)}</span></div>
                                        <div style={{ marginTop: 4, borderTop: '1px solid #333', paddingTop: 4 }}><span style={{ color: '#aaa' }}>CPI:</span> <span style={{ color: '#5dade2', fontWeight: 'bold' }}>{d.cpi || '-'}</span> <span style={{ color: '#666' }}>(= {fNum(earnedLabor$, 1)} ÷ {fNum(d.laborActual, 1)})</span></div>
                                    </div>
                                    {d.codes && d.codes.length > 1 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginBottom: 10 }}>
                                            <thead>
                                                <tr style={{ color: '#888', borderBottom: '1px solid #2a2a4a' }}>
                                                    <td style={{ padding: '2px 4px' }}>Code</td>
                                                    {d.codes[0]?.disc && <td style={{ padding: '2px 4px' }}>Discipline</td>}
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>% Comp</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>DZ (51*)</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>CO (51*)</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Labor Budget $</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Earned Labor $</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Actual Labor $</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>CPI</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {d.codes.filter(c => c.laborBudget > 0 || c.laborActual > 0).map((c, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #1a1a2e' }}>
                                                        <td style={{ padding: '2px 4px', color: '#7CAFDD' }}>{c.cc}</td>
                                                        {c.disc !== undefined && <td style={{ padding: '2px 4px', color: '#aaa' }}>{c.disc}</td>}
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{(c.physPct * 100)?.toFixed(1)}%</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#d4a843' }}>{fNum(c.laborBudgetDZ || 0, 1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#d4a843' }}>{fNum(c.laborBudgetCO || 0, 1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{fNum(c.laborBudget, 1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{fNum(c.earnedLabor, 1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{fNum(c.laborActual, 1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: '#5dade2' }}>{c.laborActual > 0 ? (c.earnedLabor / c.laborActual).toFixed(2) : '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    <div onClick={() => setCpiDebugPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>✕ Close</div>
                                </div>
                            </>
                        );
                    })()}
                    {/* PF Debug Popup */}
                    {pfDebugPopup && (() => {
                        const d = pfDebugPopup;
                        return (
                            <>
                                <div onClick={() => setPfDebugPopup(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                                <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 9999, backgroundColor: '#1a1a2e', color: '#e0e0e0', border: '1px solid #2d6cdf', borderRadius: 8, padding: '14px 18px', minWidth: 520, maxWidth: 620, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.35)', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                                        PF Debug — {d.label}
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 10 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Formula</div>
                                        <div style={{ color: '#82e0aa' }}>PF = {d.pctComplete < 25 ? '1.00 (% Complete < 25%)' : 'Earned Hours ÷ Actual Hours'}</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>Earned Hours = % Complete × Budget Hours</div>
                                        <div style={{ color: '#82e0aa', paddingLeft: 8 }}>% Complete = Installed Qty ÷ Total Qty</div>
                                        {d.pctComplete < 25 && <div style={{ color: '#f5c842', marginTop: 4 }}>⚠ When % Complete is below 25%, PF defaults to 1.00</div>}
                                    </div>
                                    <div style={{ backgroundColor: '#0d1b2e', borderRadius: 4, padding: '8px 10px', marginBottom: 10, fontSize: 11 }}>
                                        <div><span style={{ color: '#aaa' }}>Budget Hours:</span> <span style={{ color: '#f5c842' }}>{d.budgetHrs?.toFixed(1)}</span></div>
                                        <div><span style={{ color: '#aaa' }}>% Complete:</span> <span style={{ color: '#f5c842' }}>{d.pctComplete?.toFixed(2)}%</span></div>
                                        <div><span style={{ color: '#aaa' }}>Earned Hours:</span> <span style={{ color: '#82e0aa' }}>{d.earnedHrs?.toFixed(1)}</span> <span style={{ color: '#666' }}>(= {d.pctComplete?.toFixed(2)}% × {d.budgetHrs?.toFixed(1)})</span></div>
                                        <div><span style={{ color: '#aaa' }}>Actual Hours:</span> <span style={{ color: '#e57373' }}>{d.actualHrs?.toFixed(1)}</span></div>
                                        <div style={{ marginTop: 4, borderTop: '1px solid #333', paddingTop: 4 }}><span style={{ color: '#aaa' }}>PF:</span> <span style={{ color: '#5dade2', fontWeight: 'bold' }}>{d.pf || '-'}</span> {d.pctComplete < 25 ? <span style={{ color: '#f5c842' }}>(default — % Complete {d.pctComplete?.toFixed(2)}% &lt; 25%)</span> : <span style={{ color: '#666' }}>(= {d.earnedHrs?.toFixed(1)} ÷ {d.actualHrs?.toFixed(1)})</span>}</div>
                                    </div>
                                    {d.codes && d.codes.length > 1 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 9, marginBottom: 10 }}>
                                            <thead>
                                                <tr style={{ color: '#888', borderBottom: '1px solid #2a2a4a' }}>
                                                    <td style={{ padding: '2px 4px' }}>Code</td>
                                                    {d.codes[0]?.disc && <td style={{ padding: '2px 4px' }}>Discipline</td>}
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Budget Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>% Comp</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Earned Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>Actual Hrs</td>
                                                    <td style={{ padding: '2px 4px', textAlign: 'right' }}>PF</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {d.codes.filter(c => c.budgetHrs > 0 || c.actualHrs > 0).map((c, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #1a1a2e' }}>
                                                        <td style={{ padding: '2px 4px', color: '#7CAFDD' }}>{c.cc}</td>
                                                        {c.disc !== undefined && <td style={{ padding: '2px 4px', color: '#aaa' }}>{c.disc}</td>}
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{c.budgetHrs?.toFixed(1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{c.pct?.toFixed(1)}%</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{c.earnedHrs?.toFixed(1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{c.actualHrs?.toFixed(1)}</td>
                                                        <td style={{ padding: '2px 4px', textAlign: 'right', color: c.pct < 25 ? '#f5c842' : '#5dade2' }}>{c.pct < 25 ? '1.00*' : (c.actualHrs > 0 ? (c.earnedHrs / c.actualHrs).toFixed(2) : '-')}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                    <div onClick={() => setPfDebugPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>✕ Close</div>
                                </div>
                            </>
                        );
                    })()}
                    {/* Status Popup */}
                    {overallStatusPopup && (() => {
                        const toScore = s => s === 'green' ? 1.0 : s === 'orange' ? 0.5 : 0.0;
                        const toIcon = s => s === 'green' ? '🟢' : s === 'orange' ? '🟠' : '🔴';
                        const rows = spiExpanded ? [
                            { label: 'Safety',      weight: 0.25, status: statuses.safety },
                            { label: 'Budget/Cost', weight: 0.20, status: statuses.budget },
                            { label: 'Productivity',weight: 0.25, status: statuses.productivity },
                            { label: 'Schedule',    weight: 0.20, status: statuses.schedule },
                            { label: 'Billing',     weight: 0.10, status: statuses.billing },
                        ] : [
                            { label: 'Safety',      weight: 0.3125, status: statuses.safety },
                            { label: 'Budget/Cost', weight: 0.25, status: statuses.budget },
                            { label: 'Productivity',weight: 0.3125, status: statuses.productivity },
                            { label: 'Billing',     weight: 0.125, status: statuses.billing },
                        ];
                        const score = rows.reduce((s, r) => s + toScore(r.status) * r.weight, 0);
                        const safetyGovern = statuses.safety === 'red' && overallStatus !== 'red';
                        return (
                            <>
                                <div onClick={() => setOverallStatusPopup(null)} style={{ position: 'fixed', inset: 0, zIndex: 9998 }} />
                                <div style={{ position: 'absolute', top: overallStatusPopup.top + 4, left: overallStatusPopup.left, zIndex: 9999, backgroundColor: '#1a1a2e', color: '#e0e0e0', border: '1px solid #2d6cdf', borderRadius: 8, padding: '14px 18px', minWidth: 340, maxWidth: 380, boxShadow: '0 8px 32px rgba(0,0,0,0.35)', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>Overall Project Status</div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginBottom: 10 }}>
                                        <thead>
                                            <tr style={{ color: '#888', borderBottom: '1px solid #2a2a4a', fontSize: 9 }}>
                                                <td style={{ padding: '2px 4px' }}>Category</td>
                                                <td style={{ padding: '2px 4px', textAlign: 'center' }}>Status</td>
                                                <td style={{ padding: '2px 4px', textAlign: 'right' }}>Weight</td>
                                                <td style={{ padding: '2px 4px', textAlign: 'right' }}>Score</td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map(r => (
                                                <tr key={r.label} style={{ borderBottom: '1px solid #1a1a2e' }}>
                                                    <td style={{ padding: '3px 4px', color: '#d5d8dc' }}>{r.label}</td>
                                                    <td style={{ padding: '3px 4px', textAlign: 'center' }}>{toIcon(r.status)}</td>
                                                    <td style={{ padding: '3px 4px', textAlign: 'right', color: '#aaa' }}>{(r.weight * 100).toFixed(0)}%</td>
                                                    <td style={{ padding: '3px 4px', textAlign: 'right', color: '#f5c842', fontWeight: 700 }}>{(toScore(r.status) * r.weight).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr style={{ borderTop: '2px solid #2a2a4a', fontWeight: 700 }}>
                                                <td colSpan={3} style={{ padding: '3px 4px', color: '#aaa' }}>Total Score</td>
                                                <td style={{ padding: '3px 4px', textAlign: 'right', color: '#82e0aa' }}>{score.toFixed(2)}</td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                    <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '8px 10px', fontSize: 10, marginBottom: 8 }}>
                                        <div style={{ color: '#f5c842', fontWeight: 'bold', marginBottom: 4 }}>Thresholds</div>
                                        <div>🟢 Green &nbsp; ≥ 0.85</div>
                                        <div>🟠 Orange &nbsp; 0.70 – 0.84</div>
                                        <div>🔴 Red &nbsp; &lt; 0.70</div>
                                    </div>
                                    {safetyGovern && <div style={{ backgroundColor: '#2d1a00', border: '1px solid #e67e22', borderRadius: 4, padding: '6px 8px', fontSize: 10, color: '#e67e22', marginBottom: 8 }}>⚠ Governance: Safety is Red — overall capped at Orange</div>}
                                    <div style={{ fontWeight: 'bold', fontSize: 12, color: overallStatus === 'green' ? '#82e0aa' : overallStatus === 'orange' ? '#f5c842' : '#e74c3c' }}>Result → {toIcon(overallStatus)} {overallStatus === 'green' ? 'On Track' : overallStatus === 'orange' ? 'At Risk' : 'Critical'}</div>
                                    <div onClick={() => setOverallStatusPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>✕ Close</div>
                                </div>
                            </>
                        );
                    })()}
                    {/* Status Popup */}
                    {statusPopup && (() => {
                        const formulas = {
                            productivity: { label: 'Productivity', formula: 'Based on Performance Factor (PF)', source: 'PF = Earned Hours / Actual Hours', detail: `PF Score: ${pfScore.toFixed(2)}\nRed: PF < 0.85\nOrange: PF 0.85\u20130.95\nGreen: PF \u2265 0.95` },
                            budget: { label: 'Cost / Budget', formula: 'Based on JP% variance (Budget vs EAC)', source: 'JP% Budget − JP% EAC', detail: `JP% Budget: ${jobProfitData?.jpPctBudget || 'N/A'}\nJP% EAC: ${jobProfitData?.jpPctEAC || 'N/A'}\nVariance: ${gmVariance.toFixed(2)}%\n\nCost Budget: ${jobProfitData?.costBudget || 'N/A'}\nCost EAC: ${jobProfitData?.costEAC || 'N/A'}\n\nThresholds:\n  Red: Variance > 8%\n  Orange: Variance 3\u20138%\n  Green: Variance < 3%\n\nCurrent \u2192 ${statuses.budget === 'red' ? '\ud83d\udd34 Alert' : statuses.budget === 'orange' ? '\ud83d\udfe0 Watch' : '\ud83d\udfe2 Good'}` },
                            schedule: { label: 'Schedule', formula: 'Based on Schedule Performance Index (SPI)', source: 'SPI = Earned Hours ÷ Budgeted Hours for Work Scheduled', detail: `SPI: ${pNum(budgetData?.['Total Costs']?.spi || 0).toFixed(2)}\n\nThresholds:\n  Red: SPI < 0.85\n  Orange: SPI 0.85\u20130.95\n  Green: SPI \u2265 0.95\n\nCurrent \u2192 ${statuses.schedule === 'red' ? '\ud83d\udd34 Alert' : statuses.schedule === 'orange' ? '\ud83d\udfe0 Watch' : '\ud83d\udfe2 Good'}` },
                            billing: { label: 'Billing', formula: isVoltaGrid ? 'Over/(Under) = Total Billed − Billable to date' : 'Over/(Under) = Total Billed − Est Earned Rev', source: isVoltaGrid ? 'Billable to date = the Billing tab\'s billable amount (summaries + unbilled lines, fuel included)' : 'Est Earned Rev = Contract × (JTD Costs / Cost EAC)', detail: `Total Revenue (${isVoltaGrid ? 'EAC' : 'Contract'}): ${fNum(totalRevenue, 1)}\n${isVoltaGrid ? 'Billable to date' : 'Est Earned Revenue'}: ${fNum(earnedRevenue, 1)}\nTotal Billed: ${fNum(totalBilled, 1)}\nOver/(Under) Billed: ${fNum(overUnderBilled, 1)}\n\nOver/Under %: ${(overUnder * 100).toFixed(1)}%\n\nThresholds:\n  Red: \u2265 15% underbilled\n  Orange: 5\u201315% underbilled\n  Green: < 5% underbilled or overbilled\n\nCurrent \u2192 ${statuses.billing === 'red' ? '\ud83d\udd34 Alert' : statuses.billing === 'orange' ? '\ud83d\udfe0 Watch' : '\ud83d\udfe2 Good'}` },
                            safety: { label: 'Safety', formula: 'Recordables / First Aid / Near Miss & Stop Work', source: 'Safety Key Metrics card', detail: (() => { return `Recordables: ${safetyQAQC}  \u2192  ${safetyQAQC > 0 ? '\ud83d\udd34 Red' : '\ud83d\udfe2 Green'}\nFirst Aid: ${safetyFirstAid}  \u2192  ${safetyFirstAid > 0 ? '\ud83d\udfe0 Yellow' : '\ud83d\udfe2 Green'}\nNear Miss: ${safetyNearMiss} / Stop Work: ${safetyStopWork}  \u2192  ${(safetyNearMiss === 0 && safetyStopWork === 0) ? '\ud83d\udfe0 Yellow (both zero)' : '\ud83d\udfe2 Green'}\n\nThresholds:\n  Recordables:  0 = Green, \u22651 = Red\n  First Aid:    0 = Green, \u22651 = Yellow\n  Near Miss & Stop Work: both 0 = Yellow, either >0 = Green\n\nCurrent \u2192 ${statuses.safety === 'red' ? '\ud83d\udd34 Alert' : statuses.safety === 'orange' ? '\ud83d\udfe0 Watch' : '\ud83d\udfe2 Good'}`; })() },
                        };
                        const info = formulas[statusPopup.category];
                        if (!info) return null;
                        return (
                            <>
                                <div onClick={() => setStatusPopup(null)} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, minHeight: '100vh', zIndex: 9998 }} />
                                <div style={{
                                    position: 'absolute',
                                    top: statusPopup.anchorRect.top + 4,
                                    left: Math.min(statusPopup.anchorRect.left, window.innerWidth - 380),
                                    zIndex: 9999,
                                    backgroundColor: '#1a1a2e',
                                    color: '#e0e0e0',
                                    border: '1px solid #2d6cdf',
                                    borderRadius: 8,
                                    padding: '14px 18px',
                                    minWidth: 320,
                                    maxWidth: 370,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    lineHeight: 1.6,
                                }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 13, color: '#5dade2', marginBottom: 8, borderBottom: '1px solid #333', paddingBottom: 6 }}>
                                        {info.label}
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <span style={{ color: '#aaa' }}>Formula: </span>
                                        <span style={{ color: '#f5c842', whiteSpace: 'pre-wrap' }}>{info.formula}</span>
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <span style={{ color: '#aaa' }}>Source: </span>
                                        <span style={{ color: '#82e0aa' }}>{info.source}</span>
                                    </div>
                                    <div style={{ backgroundColor: '#16213e', borderRadius: 4, padding: '8px 10px', whiteSpace: 'pre-wrap', color: '#d5d8dc', fontSize: 11 }}>
                                        {info.detail}
                                    </div>
                                    <div onClick={() => setStatusPopup(null)} style={{ textAlign: 'right', marginTop: 8, cursor: 'pointer', color: '#5dade2', fontSize: 11 }}>
                                        ✕ Close
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>

                {/* ═══════════ NOTES (full width below four cards) ═══════════ */}
                <Card style={{ marginBottom: 14 }} className={overviewNotes ? 'notes-section' : 'notes-section-empty'}>
                    {isEditingOverviewNotes ? (
                        <div>
                            <textarea
                                ref={overviewNotesRef}
                                defaultValue={overviewNotes}
                                autoFocus
                                spellCheck
                                style={{ width: '100%', minHeight: 80, padding: 10, fontSize: 12, border: `1px solid ${palette.blue300}`, borderRadius: 8, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6, outline: 'none', background: palette.blue50 }}
                                placeholder="Add notes..."
                            />
                            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <button onClick={() => { const val = overviewNotesRef.current?.value ?? overviewNotes; setOverviewNotes(val); save('OverviewNotes', val); setIsEditingOverviewNotes(false); }} style={{ padding: '5px 14px', background: `linear-gradient(135deg, ${palette.blue500}, ${palette.blue700})`, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 11 }}>Save</button>
                                <button onClick={() => setIsEditingOverviewNotes(false)} style={{ padding: '5px 14px', background: palette.blue50, color: palette.text, border: `1px solid ${palette.border}`, borderRadius: 6, cursor: 'pointer', fontSize: 11 }}>Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div onClick={() => setIsEditingOverviewNotes(true)} style={{ cursor: 'pointer', padding: 10, borderRadius: 8, border: `1px dashed ${palette.border}`, minHeight: 40, fontSize: 12, color: overviewNotes ? palette.text : palette.blue300, fontStyle: overviewNotes ? 'normal' : 'italic', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                            {overviewNotes || 'Click to add notes...'}
                        </div>
                    )}
                </Card>

                {/* ═══════════ BUDGET OVERVIEW & INDEXES ═══════════ */}
                <Card style={{ marginBottom: 14 }} className="budget-overview-section">
                    {/* Title + controls on one row. Pivot selector: Phase | LEMSO | L | E | M | S | O (VoltaGrid: bid-section grid only, no pivots or filters) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <div style={{ marginBottom: -12, marginRight: 'auto' }}><SectionTitle icon={icons.dollar}>Budget Overview & Indexes</SectionTitle></div>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                        {(isVoltaGrid ? [] : [
                            { key: 'phase', label: 'Discipline' },
                            { key: 'lemso', label: 'LEMSO' },
                            { key: 'L', label: 'L' },
                            { key: 'E', label: 'E' },
                            { key: 'M', label: 'M' },
                            { key: 'S', label: 'S' },
                            { key: 'O', label: 'O' },
                        ]).map(opt => {
                            const active = budgetPivot === opt.key;
                            return (
                                <button key={opt.key} onClick={() => {
                                    setBudgetPivot(opt.key);
                                    // Collapse all disciplines by default when switching views
                                    const allDiscs = budgetRows.filter(r => r.level === 0 && r.discipline).map(r => r.discipline);
                                    if (opt.key === 'lemso') {
                                        const init = {};
                                        ['L', 'E', 'M', 'S', 'O'].forEach(k => { init[k] = true; });
                                        setCollapsedDisciplines(init);
                                    } else if (opt.key === 'phase') {
                                        const init = {}; allDiscs.forEach(d => { init[d] = true; }); setCollapsedDisciplines(init);
                                    } else {
                                        const init = {}; allDiscs.forEach(d => { init[d] = true; }); setCollapsedDisciplines(init);
                                    }
                                }}
                                    style={{
                                        padding: '3px 10px', fontSize: 9, fontWeight: active ? 700 : 500, borderRadius: 14,
                                        border: active ? `1.5px solid ${palette.accent}` : `1px solid ${palette.border}`,
                                        background: active ? `linear-gradient(135deg, ${palette.blue500}, ${palette.blue700})` : palette.blue50,
                                        color: active ? '#fff' : palette.text, cursor: 'pointer', transition: 'all .15s ease',
                                    }}
                                    title={opt.key === 'phase' ? 'Group by discipline / phase code' : opt.key === 'lemso' ? 'Group by Labor, Equipment, Materials, Subs, Other' : `Show only ${({ L: 'Labor', E: 'Equipment', M: 'Materials', S: 'Subcontractors', O: 'Other' })[opt.key]}`}
                                >{opt.label}</button>
                            );
                        })}
                        {/* What Δ EAC Previous compares against */}
                        <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, fontWeight: 600, color: palette.muted }}
                            title="The version Δ EAC Previous and the Prev columns compare against">
                            Previous
                            <select value={prevPick} onChange={e => setPrevPick(e.target.value)} disabled={!prevOptions.length}
                                style={{ fontSize: 9, fontWeight: 600, padding: '3px 6px', borderRadius: 14, border: prevPick ? `1.5px solid ${palette.accent}` : `1px solid ${palette.border}`, background: palette.blue50, color: palette.text, outline: 'none', cursor: 'pointer' }}>
                                <option value="">{selectedWIP?.Version_Start_DateTime ? (isVoltaGrid ? '' : 'Last WIP — ') + moment(selectedWIP.Version_Start_DateTime).format('MMM D, YYYY') : 'Last WIP'}</option>
                                {prevOptions.map(o => <option key={o.value} value={o.value}>{isVoltaGrid ? o.label.replace(/ - WIP$/, '') : o.label}</option>)}
                            </select>
                            {prevLoading && <span style={{ fontWeight: 500 }}>loading…</span>}
                        </label>
                        {/* Export button */}
                        <button onClick={handleBudgetExport}
                            style={{
                                padding: '3px 10px', fontSize: 9, fontWeight: 600, borderRadius: 14,
                                border: `1px solid ${palette.border}`, background: palette.blue50,
                                color: palette.text, cursor: 'pointer', transition: 'all .15s ease',
                                display: 'flex', alignItems: 'center', gap: 4,
                            }}
                            title="Export Budget Overview to Excel">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                            Export
                        </button>
                        {/* Filter button */}
                        {!isVoltaGrid && <div style={{ position: 'relative' }}>
                            <button onClick={() => setBudgetFilterOpen(prev => !prev)}
                                style={{
                                    padding: '3px 8px', fontSize: 9, fontWeight: 600, borderRadius: 14,
                                    border: (budgetFilterPhase || budgetFilterDiscipline) ? `1.5px solid ${palette.accent}` : `1px solid ${palette.border}`,
                                    background: (budgetFilterPhase || budgetFilterDiscipline) ? `linear-gradient(135deg, ${palette.blue500}, ${palette.blue700})` : palette.blue50,
                                    color: (budgetFilterPhase || budgetFilterDiscipline) ? '#fff' : palette.text,
                                    cursor: 'pointer', transition: 'all .15s ease', display: 'flex', alignItems: 'center', gap: 4,
                                }}
                                title="Filter by phase code or discipline">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                                Filter{(budgetFilterPhase || budgetFilterDiscipline) ? ' ●' : ''}
                            </button>
                            {budgetFilterOpen && (
                                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: palette.card, border: `1px solid ${palette.border}`, borderRadius: 8, boxShadow: shadows.md, padding: 12, zIndex: 100, minWidth: 220 }}
                                    onClick={e => e.stopPropagation()}>
                                    <div style={{ fontSize: 10, fontWeight: 700, color: palette.text, marginBottom: 8 }}>Filter Budget View</div>
                                    <div style={{ marginBottom: 8 }}>
                                        <label style={{ fontSize: 9, fontWeight: 600, color: palette.blue700, display: 'block', marginBottom: 3 }}>Phase Code</label>
                                        <select value={budgetFilterPhase} onChange={e => setBudgetFilterPhase(e.target.value)}
                                            style={{ width: '100%', fontSize: 10, padding: '4px 6px', borderRadius: 4, border: `1px solid ${palette.border}`, outline: 'none' }}>
                                            <option value="">All Phase Codes</option>
                                            {(() => {
                                                const codes = filteredBudgetView.rows.filter(r => r.costCode).map(r => r.costCode);
                                                return [...new Set(codes)].sort().map(cc => (
                                                    <option key={cc} value={cc}>{cc}{jobPhaseCodes?.[cc] ? ` - ${jobPhaseCodes[cc]}` : ''}</option>
                                                ));
                                            })()}
                                        </select>
                                    </div>
                                    <div style={{ marginBottom: 8 }}>
                                        <label style={{ fontSize: 9, fontWeight: 600, color: palette.blue700, display: 'block', marginBottom: 3 }}>Discipline</label>
                                        <select value={budgetFilterDiscipline} onChange={e => setBudgetFilterDiscipline(e.target.value)}
                                            style={{ width: '100%', fontSize: 10, padding: '4px 6px', borderRadius: 4, border: `1px solid ${palette.border}`, outline: 'none' }}>
                                            <option value="">All Disciplines</option>
                                            {(() => {
                                                const discs = filteredBudgetView.rows.filter(r => r.level === 0 && r.discipline).map(r => r.discipline);
                                                return [...new Set(discs)].sort().map(d => (
                                                    <option key={d} value={d}>{d}</option>
                                                ));
                                            })()}
                                        </select>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <button onClick={() => { setBudgetFilterPhase(''); setBudgetFilterDiscipline(''); }}
                                            style={{ padding: '3px 10px', fontSize: 9, background: palette.blue50, color: palette.text, border: `1px solid ${palette.border}`, borderRadius: 4, cursor: 'pointer' }}>Clear</button>
                                        <button onClick={() => setBudgetFilterOpen(false)}
                                            style={{ padding: '3px 10px', fontSize: 9, background: `linear-gradient(135deg, ${palette.blue500}, ${palette.blue700})`, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>Done</button>
                                    </div>
                                </div>
                            )}
                        </div>}
                    </div>
                    </div>
                    {!isVoltaGrid && spiExpanded && budgetData?._meta && !budgetData._meta.hasPlannedHours && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: palette.orangeLight, color: palette.orange, fontSize: 10, fontWeight: 700, padding: '4px 10px', borderRadius: 20, marginBottom: 8, border: `1px solid ${palette.orange}` }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                            Manpower plan not entered — SPI values require planned hours from the Labor Graphs page
                        </div>
                    )}
                    {/* VoltaGrid: where the Revenue (Billable) row comes from, and what it is still missing */}
                    {isVoltaGrid && (() => {
                        const rv = budgetData?.['Contract Amount']?._revenue;
                        const warn = !!billRev.error || !!(rv && rv.noRate && rv.noRate.n > 0);
                        const pill = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 600, padding: '4px 10px', borderRadius: 20, marginBottom: 8, lineHeight: 1.4,
                            background: warn ? palette.orangeLight : palette.blue50, color: warn ? palette.orange : palette.blue700, border: `1px solid ${warn ? palette.orange : palette.blue200}` };
                        if (billRev.error) return <div style={pill}>Revenue unavailable — the job's billing rows did not load ({billRev.error}). Open the Billing tab and come back.</div>;
                        if (!rv) return <div style={{ ...pill, background: 'transparent', border: 'none', padding: '2px 0', color: palette.muted }}>Loading billable amounts from the Billing rows…</div>;
                        return null;
                    })()}
                    {/* VoltaGrid: what the Bid column is priced off */}
                    {isVoltaGrid && budgetData?.['Contract Amount'] && (() => {
                        const rb = budgetData['Contract Amount']._revenueBudget;
                        const note = { fontSize: 10, color: palette.muted, marginBottom: 8, lineHeight: 1.4 };
                        // The "Bid: Revenue Budget — $X on N lines" line came off (Aeron, 10/8).
                        // Only the off-grid warning is still printed, and only when there is one.
                        if (!rb) return null;
                        const off = rb.offGrid.reduce((a, x) => a + x.amount, 0);
                        if (!(off > 0)) return null;
                        return (
                            <div style={{ ...note, color: palette.orange, fontWeight: 600 }}>
                                ${fNum(off, 1)} of the Revenue Budget is on {rb.offGrid.length} code{rb.offGrid.length === 1 ? '' : 's'} with no row here ({rb.offGrid.slice(0, 4).map((x) => x.cc).join(', ')}{rb.offGrid.length > 4 ? ', …' : ''}) — in the Revenue total only.
                            </div>
                        );
                    })()}
                    {(() => {
                        const budgetColumns = [
                            'Bid Budget', 'Approved COs', 'Current Budget', 'Actual Costs', 'Open Commit', 'Total Commit',
                            ...(etcBreakdownExpanded ? ['ETC Labor', 'ETC Equipment', 'ETC Materials', 'ETC Rentals', 'ETC Subs', 'ETC Other'] : []),
                            'ETC Total', 'EAC', '\u0394 EAC Budget', ...(prevBreakdownExpanded ? ['Prev Actual', 'Prev Open Commit', 'Prev ETC Total', 'Prev EAC'] : []), '\u0394 EAC Previous',
                        ];
                        // VoltaGrid reads the table in billable dollars, so the actual column says so.
                        const colLabel = (c) => (isVoltaGrid && c === 'Actual Costs' ? 'Actual' : c);
                        const highlightCols = { 'Current Budget': true, 'EAC': true };
                        const thHighlight = { background: palette.blue100, color: palette.blue800 };
                        const tdHighlight = (base) => ({ background: palette.blue50, fontWeight: Math.max(base, 600) });
                        const thS = { padding: '5px 6px', textAlign: 'center', fontSize: 8, fontWeight: 700, color: palette.blue700, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${palette.blue200}`, background: palette.blue50, whiteSpace: 'normal', wordBreak: 'keep-all', overflowWrap: 'normal', position: 'sticky', top: 0, zIndex: 10 };   // wrap between words only: "Approved" stays whole (Seth 10/9)
                        const tdS = { padding: '4px 6px', fontSize: 10, borderBottom: `1px solid ${palette.border}`, whiteSpace: 'nowrap' };
                        const getDataKey = (row) => {
                            if (row.dataKey) return row.dataKey;
                            if (row.label === 'Contract Amount') return 'Contract Amount';
                            if (row.label === 'Total Costs') return 'Total Costs';
                            if (row.label === 'Contingency') return 'Contingency';
                            if (row.costCode) return `costCode_${row.costCode}`;
                            if (row.discipline && filteredBudgetView.data[row.discipline]) return row.discipline;
                            return null;
                        };
                        const isLEMSO = budgetPivot === 'lemso';
                        // Client view: no cost total and no Contingency row under the revenue rows.
                        // Nor a WBS group with nothing in it (`_empty`).
                        const allRows = isVoltaGrid ? filteredBudgetView.rows.filter((r) => r.label !== 'Total Costs' && r.label !== 'Contingency' && !(r.dataKey && filteredBudgetView.data[r.dataKey]?._empty)) : filteredBudgetView.rows;
                        const activeRows = (() => {
                            if (!budgetFilterPhase && !budgetFilterDiscipline) return allRows;
                            return allRows.filter(row => {
                                // Always keep summary rows
                                if (row.label === 'Contract Amount' || row.label === 'Total Costs' || row.label === 'Contingency') return true;
                                if (budgetFilterPhase) {
                                    // Show only the specific phase code row and its parent discipline/category
                                    if (row.costCode === budgetFilterPhase) return true;
                                    if (row.discipline && !row.costCode) {
                                        // Show discipline header only if this discipline owns the filtered code
                                        const hasCode = allRows.some(r => r.costCode === budgetFilterPhase && r.discipline === row.discipline && (!isLEMSO || r.lemsoCategory === row.lemsoCategory));
                                        return hasCode;
                                    }
                                    if (isLEMSO && row.lemsoCategory && row.level === 0 && !row.discipline) {
                                        // LEMSO category header — show if any child code matches
                                        return allRows.some(r => r.costCode === budgetFilterPhase && r.lemsoCategory === row.lemsoCategory);
                                    }
                                    return false;
                                }
                                if (budgetFilterDiscipline) {
                                    if (row.discipline === budgetFilterDiscipline) return true;
                                    if (isLEMSO && row.lemsoCategory && row.level === 0 && !row.discipline) {
                                        // LEMSO category header — show if it has this discipline
                                        return allRows.some(r => r.discipline === budgetFilterDiscipline && r.lemsoCategory === row.lemsoCategory);
                                    }
                                    return false;
                                }
                                return true;
                            });
                        })();
                        const editInputStyle = { width: '100%', textAlign: 'right', border: `1px solid ${palette.accent}`, padding: '2px 3px', fontSize: 9, borderRadius: 4, outline: 'none' };
                        return (
                            <>
                                <div>
                                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 10 }}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...thS, textAlign: 'left', minWidth: 110 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                        <span>Discipline</span>
                                                        <button onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (isLEMSO) {
                                                                setCollapsedDisciplines(prev => {
                                                                    const all = allRows.filter(r => r.level === 0 && r.lemsoCategory).map(r => r.lemsoCategory);
                                                                    const allC = all.every(k => prev[k]);
                                                                    const ns = {}; all.forEach(k => { ns[k] = !allC; }); return ns;
                                                                });
                                                            } else {
                                                                setCollapsedDisciplines(prev => {
                                                                    const all = allRows.filter(r => r.level === 0 && r.discipline).map(r => r.discipline);
                                                                    const allC = all.every(d => prev[d]);
                                                                    const ns = {}; all.forEach(d => { ns[d] = !allC; }); return ns;
                                                                });
                                                            }
                                                        }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 9, color: palette.accent, fontWeight: 'bold' }} title="Collapse/Expand all">▼</button>
                                                    </div>
                                                </th>
                                                {budgetColumns.map(col => (
                                                    <th key={col} style={{ ...thS, ...(highlightCols[col] ? thHighlight : {}) }}>
                                                        {col === 'ETC Total' ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                                                                <span>{col}</span>
                                                                <button onClick={(e) => { e.stopPropagation(); setEtcBreakdownExpanded(!etcBreakdownExpanded); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 9, color: palette.text, fontWeight: 'bold' }} title={etcBreakdownExpanded ? 'Collapse ETC' : 'Expand ETC'}>{etcBreakdownExpanded ? '▼' : '▶'}</button>
                                                            </div>
                                                        ) : col === '\u0394 EAC Previous' ? (
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                                                                <span>{col}</span>
                                                                <button onClick={(e) => { e.stopPropagation(); setPrevBreakdownExpanded(!prevBreakdownExpanded); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 9, color: palette.text, fontWeight: 'bold' }} title={prevBreakdownExpanded ? 'Collapse Prev' : 'Expand Prev'}>{prevBreakdownExpanded ? '▼' : '▶'}</button>
                                                            </div>
                                                        ) : colLabel(col)}
                                                    </th>
                                                ))}
                                                <th style={{ ...thS, whiteSpace: 'nowrap' }}>PF</th>
                                                <th style={{ ...thS, whiteSpace: 'nowrap' }}>CPI</th>
                                                {spiExpanded && <th style={{ ...thS, whiteSpace: 'nowrap' }}>SPI</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {activeRows.map((row) => {
                                                const dataKey = getDataKey(row);
                                                const rowData = dataKey ? filteredBudgetView.data[dataKey] : null;
                                                const isDiscipline = row.level === 0;
                                                const isSummaryRow = row.label === 'Contract Amount' || row.label === 'Total Costs';
                                                const baseCellBg = isSummaryRow ? palette.blue100 : isDiscipline ? palette.blue50 : 'transparent';
                                                const baseStrongBg = isSummaryRow ? palette.blue200 : isDiscipline ? palette.blue100 : palette.blue50;
                                                const cellFw = isDiscipline ? 700 : 400;
                                                const rowKey = (row.lemsoKey || '') + row.label + (row.costCode || '') + '_L' + row.level + (row.lemsoCategory || '') + (row.discipline || '');
                                                const cellBg = baseCellBg;
                                                const strongBg = baseStrongBg;
                                                // Collapse logic: LEMSO defaults to collapsed at every level; phase mode keeps explicit-collapse semantics
                                                if (isLEMSO) {
                                                    if (row.level >= 1 && row.lemsoCategory && collapsedDisciplines[row.lemsoCategory] !== false) return null;
                                                    if (row.level >= 2 && row.lemsoCategory && row.discipline && collapsedDisciplines[`${row.lemsoCategory}_${row.discipline}`] !== false) return null;
                                                } else {
                                                    if (row.level === 1 && row.discipline && collapsedDisciplines[row.discipline]) return null;
                                                }
                                                const lemsoCatExpanded = isLEMSO && row.lemsoCategory && collapsedDisciplines[row.lemsoCategory] === false;
                                                const lemsoDiscExpanded = isLEMSO && row.lemsoCategory && row.discipline && collapsedDisciplines[`${row.lemsoCategory}_${row.discipline}`] === false;
                                                return (
                                                    <React.Fragment key={rowKey}>
                                                    <tr
                                                        onMouseEnter={e => { for (const td of e.currentTarget.children) td.style.boxShadow = 'inset 0 0 0 9999px rgba(37,99,235,0.07)'; }}
                                                        onMouseLeave={e => { for (const td of e.currentTarget.children) td.style.boxShadow = ''; }}>
                                                        <td style={{ ...tdS, paddingLeft: 16 + row.level * 14, fontWeight: cellFw, background: cellBg, cursor: isDiscipline && (row.discipline || row.lemsoCategory) ? 'pointer' : 'default', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                            onClick={() => {
                                                                if (isLEMSO && row.lemsoCategory && row.level === 0) setCollapsedDisciplines(prev => ({ ...prev, [row.lemsoCategory]: prev[row.lemsoCategory] === false ? true : false }));
                                                                else if (isLEMSO && row.lemsoCategory && row.discipline && row.level === 1) setCollapsedDisciplines(prev => ({ ...prev, [`${row.lemsoCategory}_${row.discipline}`]: prev[`${row.lemsoCategory}_${row.discipline}`] === false ? true : false }));
                                                                else if (row.discipline && row.level === 0) setCollapsedDisciplines(prev => ({ ...prev, [row.discipline]: !prev[row.discipline] }));
                                                            }}>
                                                            {isLEMSO && row.lemsoCategory && row.level === 0 ? (
                                                                <span>{(lemsoCatExpanded ? '▼ ' : '▶ ') + row.label}</span>
                                                            ) : isLEMSO && row.lemsoCategory && row.discipline && row.level === 1 ? (
                                                                <span>{(lemsoDiscExpanded ? '▼ ' : '▶ ') + (disciplineTitles[row.discipline] || row.discipline)}</span>
                                                            ) : (row.level === 1 || (isLEMSO && row.level === 2)) && row.costCode ? (
                                                                <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{disciplineTitles[row.costCode] || (row.costCode + (jobPhaseCodes?.[row.costCode] ? ' - ' + jobPhaseCodes[row.costCode] : ''))}</span>
                                                            ) : row.level === 1 && row.dataKey ? (
                                                                <span style={{ display: 'block', paddingLeft: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.label}</span>
                                                            ) : row.level === 0 && row.discipline ? (
                                                                <span>{(allRows.some(r => r.level === 1 && r.discipline === row.discipline) ? (collapsedDisciplines[row.discipline] ? '▶ ' : '▼ ') : '') + (disciplineTitles[row.discipline] || row.discipline)}</span>
                                                            ) : (isVoltaGrid && row.label === 'Contract Amount' ? 'Revenue (Billable)' : row.label)}
                                                        </td>
                                                        {/* Data columns */}
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.bidBudget || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.approvedCOs || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', background: strongBg, fontWeight: Math.max(cellFw, 600) }}>{rowData?.currentBudget || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.actualCosts || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.openCommit || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.totalCommit || '-'}</td>
                                                        {etcBreakdownExpanded && <>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.etcLabor || '-'}</td>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.etcEquipment || '-'}</td>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.etcMaterials || '-'}</td>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.etcRentals || '-'}</td>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.etcSubs || '-'}</td>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.etcOther || '-'}</td>
                                                        </>}
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.etc || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', background: strongBg, fontWeight: Math.max(cellFw, 600) }}>{rowData?.eac || '-'}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{rowData?.deltaEACBudget || '-'}</td>
                                                        {prevBreakdownExpanded && <>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{prevLoading ? '…' : (rowData?.prevActualCosts || '-')}</td>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{prevLoading ? '…' : (rowData?.prevOpenCommit || '-')}</td>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{prevLoading ? '…' : (rowData?.prevEtcTotal || '-')}</td>
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{prevLoading ? '…' : (rowData?.previousEAC || '-')}</td>
                                                        </>}
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw }}>{
                                                            prevLoading ? '…'
                                                                : row.label === 'Total Costs' && (budgetPivot === 'phase' || budgetPivot === 'lemso') && wipCostVar && !prevCustom
                                                                ? wipCostVar
                                                                : (rowData?.deltaEACPrevious || '-')
                                                        }</td>
                                                        {/* PF — click to show debug popup */}
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw, cursor: rowData?._pfDebug ? 'pointer' : 'default', textDecoration: rowData?._pfDebug ? 'underline dotted' : 'none', textDecorationColor: palette.blue300 }}
                                                            onClick={() => { if (rowData?._pfDebug) setPfDebugPopup({ label: row.label || row.costCode || dataKey, ...rowData._pfDebug, pf: rowData.pf }); }}>
                                                            {rowData?.pf || '-'}
                                                        </td>
                                                        {/* CPI — click to show debug popup */}
                                                        <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw, cursor: rowData?._cpiDebug ? 'pointer' : 'default', textDecoration: rowData?._cpiDebug ? 'underline dotted' : 'none', textDecorationColor: palette.blue300 }}
                                                            onClick={() => { if (rowData?._cpiDebug) setCpiDebugPopup({ label: row.label || row.costCode || dataKey, ...rowData._cpiDebug, cpi: rowData.cpi }); }}>
                                                            {rowData?.cpi || '-'}
                                                        </td>
                                                        {/* SPI — click to show debug popup */}
                                                        {spiExpanded && (
                                                            <td style={{ ...tdS, textAlign: 'right', background: cellBg, fontWeight: cellFw, cursor: rowData?._spiDebug ? 'pointer' : 'default', textDecoration: rowData?._spiDebug ? 'underline dotted' : 'none', textDecorationColor: palette.blue300 }}
                                                                onClick={() => { if (rowData?._spiDebug) setSpiDebugPopup({ label: row.label || row.costCode || dataKey, ...rowData._spiDebug, spi: rowData.spi }); }}>
                                                                {rowData?.spi || '-'}
                                                            </td>
                                                        )}
                                                    </tr>
                                                    </React.Fragment>
                                                );
                                            })}
                                            {/* ── Job Profit & JP% rows (inside main table for alignment) ── */}
                                            {!isVoltaGrid && [
                                                { label: 'Job Profit', bid: jobProfitData?.jpBid || '-', co: jobProfitData?.jpCO || '-', budget: jobProfitData?.jpBudget || '-', eac: jobProfitData?.jpEAC || '-' },
                                                { label: 'JP %', bid: jobProfitData?.jpPctBid || '-', co: jobProfitData?.jpPctCO || '-', budget: jobProfitData?.jpPctBudget || '-', eac: jobProfitData?.jpPctEAC || '-' },
                                            ].map(jp => {
                                                const emptyBeforeEAC = 3 + (etcBreakdownExpanded ? 6 : 0) + 1;
                                                const emptyAfterEAC = 2 + (prevBreakdownExpanded ? 3 : 0) + 1 + 1 + (spiExpanded ? 1 : 0);
                                                return (
                                                    <tr key={jp.label} style={{ fontWeight: 700, background: `linear-gradient(135deg, ${palette.blue200}, ${palette.blue300})` }}>
                                                        <td style={{ ...tdS, fontWeight: 800, minWidth: 110, color: palette.blue900 }}>{jp.label}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', color: palette.blue900 }}>{jp.bid}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', color: palette.blue900 }}>{jp.co}</td>
                                                        <td style={{ ...tdS, textAlign: 'right', background: palette.blue300, fontWeight: 800, color: palette.blue900 }}>{jp.budget}</td>
                                                        {Array(emptyBeforeEAC).fill(null).map((_, i) => <td key={`b${i}`} style={{ ...tdS, textAlign: 'right', color: palette.blue900 }}>-</td>)}
                                                        <td style={{ ...tdS, textAlign: 'right', background: palette.blue300, fontWeight: 800, color: palette.blue900 }}>{jp.eac}</td>
                                                        {Array(emptyAfterEAC).fill(null).map((_, i) => <td key={`a${i}`} style={{ ...tdS, textAlign: 'right', color: palette.blue900 }}>-</td>)}
                                                    </tr>
                                                );
                                            })}
                                            {/* ── Spacer before billing rows ── */}
                                            {!isVoltaGrid && <tr><td colSpan={999} style={{ padding: '6px 0', border: 'none' }}></td></tr>}
                                            {/* ── Over/Under Billed & Total Billed with Billing Notes ── */}
                                            {!isVoltaGrid && (() => {
                                                const totalCols = 1 + budgetColumns.length + 1 + 1 + (spiExpanded ? 1 : 0);
                                                const remainCols = Math.max(2, totalCols - 2);
                                                const spanMiddle = Math.max(1, Math.round(remainCols * 0.3));
                                                const spanNotes = Math.max(1, remainCols - spanMiddle);
                                                return (
                                                    <>
                                                        <tr style={{ fontWeight: 700, background: `linear-gradient(135deg, ${palette.blue50}, ${palette.blue100})` }}>
                                                            <td style={{ ...tdS, fontWeight: 800, minWidth: 110 }}>Over/(Under) Billed</td>
                                                            <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{fNum(overUnderBilled, 1)}</td>
                                                            <td colSpan={spanMiddle} style={{ ...tdS, borderBottom: `1px solid ${palette.border}` }}></td>
                                                            <td rowSpan={2} colSpan={spanNotes} style={{ ...tdS, verticalAlign: 'top', padding: 6 }}>
                                                                {isEditingBillingNotes ? (
                                                                    <div>
                                                                        <textarea
                                                                            ref={billingNotesRef}
                                                                            rows={2}
                                                                            defaultValue={billingNotes}
                                                                            style={{ width: '100%', fontSize: 10, padding: 4, borderRadius: 4, border: `1px solid ${palette.accent}`, outline: 'none', resize: 'none', fontFamily: 'inherit', lineHeight: 1.4 }}
                                                                            autoFocus
                                                                        />
                                                                        <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                                                                            <button onClick={() => { const val = billingNotesRef.current?.value ?? billingNotes; setBillingNotes(val); save('BillingNotes', val); setIsEditingBillingNotes(false); }} style={{ padding: '2px 8px', background: `linear-gradient(135deg, ${palette.blue500}, ${palette.blue700})`, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 9 }}>Save</button>
                                                                            <button onClick={() => setIsEditingBillingNotes(false)} style={{ padding: '2px 8px', background: palette.blue50, color: palette.text, border: `1px solid ${palette.border}`, borderRadius: 4, cursor: 'pointer', fontSize: 9 }}>Cancel</button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div onClick={() => setIsEditingBillingNotes(true)} className={billingNotes ? '' : 'billing-notes-empty'} style={{ cursor: 'pointer', padding: 4, borderRadius: 4, border: `1px dashed ${palette.border}`, minHeight: 30, fontSize: 10, color: billingNotes ? palette.text : palette.blue300, fontStyle: billingNotes ? 'normal' : 'italic', lineHeight: 1.4, whiteSpace: 'pre-wrap', background: palette.blue50 }}>
                                                                        {billingNotes || 'Click to add billing notes...'}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                        <tr style={{ fontWeight: 700, background: `linear-gradient(135deg, ${palette.blue50}, ${palette.blue100})` }}>
                                                            <td style={{ ...tdS, fontWeight: 800, minWidth: 110 }}>Total Billed</td>
                                                            <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{fNum(totalBilled, 1)}</td>
                                                            <td colSpan={spanMiddle} style={{ ...tdS, borderBottom: `1px solid ${palette.border}` }}></td>
                                                        </tr>
                                                    </>
                                                );
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        );
                    })()}
                </Card>

                {/* ═══════════ PRODUCTIVITY COMBINED (collapsed by default) ═══════════ */}
                <Card style={{ marginBottom: 14 }}>
                    <SectionTitle icon={icons.barChart}>Productivity Tracker</SectionTitle>
                    <div style={{ marginTop: 8, overflowX: 'auto', width: '100%' }}>
                        {/* Keyed by job: the tracker keeps its rows while it is handed an
                            empty data set, so a new job starts it fresh */}
                        {!jobDataReady ? <div style={{ fontSize: 11, color: palette.muted, padding: '6px 2px' }}>Loading {selectedJob}…</div> : <ProductivityCombined
                            key={selectedJob}
                            collapsedParent={false}
                            overview={true}
                            ovVoltaGrid={isVoltaGrid}
                            ovJobUserData={jobUserData}
                            ovJobCostDetail={jobCostDetailRaw}
                            ovJobPhaseCodes={jobPhaseCodes}
                            ovSelectedJobData={selectedJobDetails}
                            ovSelectedJob={selectedJob}
                            ovSelectedVersion={selectedVersion}
                            ovProjectComplete={projectComplete}
                            ovVersionInfo={versionInfo}
                            ovRegular={regular}
                            ovLockedVersion={lockedVersion}
                        />}
                    </div>
                </Card>


                {/* ═══════════ ROW 4: Quantity Tracking & Progress Schedule (combined) ═══════════ */}
                {false && <Card style={{ marginBottom: 14 }} className="quantity-tracking-section">
                    <SectionTitle icon={icons.box}>Quantity Tracking & Progress</SectionTitle>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 10 }}>
                            <thead>
                                <tr>
                                    {['Discipline', 'UoM', 'Budget', 'Installed', 'Remaining', 'Actual Hrs', 'Earned Hrs', 'Plan', 'Actual', 'Fcast'].map(col => (
                                        <th key={col} style={{ padding: '5px 6px', textAlign: col === 'Discipline' ? 'left' : 'center', fontSize: 8, fontWeight: 700, color: palette.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${palette.border}` }}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {budgetRows.filter(r => r.level === 0 && r.discipline && r.discipline !== 'Contingency' && r.label !== 'Contract Amount' && r.label !== 'Total Costs')
                                    .map(row => {
                                        const q = quantityTrackingData[row.discipline] || {};
                                        const pct = parseFloat(q.pctComplete || 0);
                                        const p = progressScheduleData[row.discipline] || {};
                                        const userPlan = progressData[`${row.discipline}_ProgressCumulativePlan`];
                                        return (
                                            <tr key={row.discipline} style={{ transition: 'background .15s' }}
                                                onMouseEnter={e => e.currentTarget.style.background = palette.blue50}
                                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                <td style={{ padding: '5px 6px', fontWeight: 600, fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{row.discipline}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}`, color: palette.muted }}>{q.uom || '—'}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{q.planned > 0 ? Math.round(q.planned).toLocaleString() : '—'}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{q.actual > 0 ? Math.round(q.actual).toLocaleString() : '—'}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}`, fontWeight: 600, color: palette.text }}>{q.planned > 0 || q.actual > 0 ? Math.round(q.variance || 0).toLocaleString() : '—'}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{q.actualHours > 0 ? Math.round(q.actualHours).toLocaleString() : '—'}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{q.earnedHours > 0 ? Math.round(q.earnedHours).toLocaleString() : '—'}</td>
                                                <td
                                                    onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setPlanDebugPopup({ discipline: row.discipline, top: r.bottom + window.scrollY, left: Math.min(r.left + window.scrollX, window.innerWidth - 460) }); }}
                                                    style={{ padding: '5px 6px', borderBottom: `1px solid ${palette.border}`, cursor: 'pointer' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                        <ProgressBar pct={parseFloat((userPlan || p.cumulativePlan || '0').replace('%', ''))} height={6} />
                                                        <div style={{ fontSize: 9, fontWeight: 600, color: palette.text, whiteSpace: 'nowrap', flexShrink: 0, textDecoration: 'underline dotted' }}>{Math.round(parseFloat((userPlan || p.cumulativePlan || '0').replace('%', ''))) + '%'}</div>
                                                    </div>
                                                </td>
                                                <td
                                                    onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setActualDebugPopup({ discipline: row.discipline, top: r.bottom + window.scrollY, left: Math.min(r.left + window.scrollX, window.innerWidth - 460) }); }}
                                                    style={{ padding: '5px 6px', borderBottom: `1px solid ${palette.border}`, cursor: 'pointer' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                        <ProgressBar pct={parseFloat(q.pctComplete || 0)} height={6} />
                                                        <div style={{ fontSize: 9, fontWeight: 600, color: palette.text, whiteSpace: 'nowrap', flexShrink: 0, textDecoration: 'underline dotted' }}>{Math.round(parseFloat(q.pctComplete || 0)) + '%'}</div>
                                                    </div>
                                                </td>
                                                <td
                                                    onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setFcastDebugPopup({ discipline: row.discipline, top: r.bottom + window.scrollY, left: Math.min(r.left + window.scrollX, window.innerWidth - 460) }); }}
                                                    style={{ padding: '5px 6px', borderBottom: `1px solid ${palette.border}`, cursor: 'pointer' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                        <ProgressBar pct={parseFloat((p.cumulativeFcast || '0').replace('%', ''))} height={6} />
                                                        <div style={{ fontSize: 9, fontWeight: 600, color: palette.text, whiteSpace: 'nowrap', flexShrink: 0, textDecoration: 'underline dotted' }}>{Math.round(parseFloat((p.cumulativeFcast || '0').replace('%', ''))) + '%'}</div>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                {(() => {
                                    const o = quantityTrackingData['__overall'] || {};
                                    const pct = parseFloat(o.pctComplete || 0);
                                    const op = progressScheduleData['__overall'] || {};
                                    const userPlan = progressData['Overall_ProgressCumulativePlan'];
                                    return (
                                        <tr style={{ background: palette.blue50 }}>
                                            <td style={{ padding: '6px 6px', fontWeight: 800, fontSize: 10 }}>Overall</td>
                                            <td style={{ padding: '6px 6px', textAlign: 'center' }}>—</td>
                                            <td style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 700, fontSize: 10 }}>—</td>
                                            <td style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 700, fontSize: 10 }}>—</td>
                                            <td style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 700, fontSize: 10 }}>—</td>
                                            <td style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 700, fontSize: 10 }}>{o.actualHours > 0 ? Math.round(o.actualHours).toLocaleString() : '—'}</td>
                                            <td style={{ padding: '6px 6px', textAlign: 'center', fontWeight: 700, fontSize: 10 }}>{o.earnedHours > 0 ? Math.round(o.earnedHours).toLocaleString() : '—'}</td>
                                            <td style={{ padding: '6px 6px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                    <ProgressBar pct={parseFloat((userPlan || op.cumulativePlan || '0').replace('%', ''))} height={7} />
                                                    <div style={{ fontSize: 9, fontWeight: 700, color: palette.text, whiteSpace: 'nowrap', flexShrink: 0 }}>{Math.round(parseFloat((userPlan || op.cumulativePlan || '0').replace('%', ''))) + '%'}</div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '6px 6px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                    <ProgressBar pct={parseFloat(o.pctComplete || 0)} height={7} />
                                                    <div style={{ fontSize: 9, fontWeight: 700, color: palette.text, whiteSpace: 'nowrap', flexShrink: 0 }}>{Math.round(parseFloat(o.pctComplete || 0)) + '%'}</div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '6px 6px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                                    <ProgressBar pct={parseFloat((op.cumulativeFcast || '0').replace('%', ''))} height={7} />
                                                    <div style={{ fontSize: 9, fontWeight: 700, color: palette.text, whiteSpace: 'nowrap', flexShrink: 0 }}>{Math.round(parseFloat((op.cumulativeFcast || '0').replace('%', ''))) + '%'}</div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })()}
                            </tbody>
                        </table>
                    </div>
                </Card>}

                {/* ═══════════ KEY MILESTONES ═══════════ */}
                <Card style={{ marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                        <SectionTitle icon={icons.target}>Key Milestones</SectionTitle>
                        <button onClick={handleAddMilestone}
                            className="no-print"
                            style={{ padding: '3px 10px', fontSize: 9, cursor: 'pointer', background: 'transparent', color: palette.muted, border: `1px solid ${palette.border}`, borderRadius: 5, fontWeight: 600, letterSpacing: '.02em' }}>
                            + Add
                        </button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 10 }}>
                            <thead>
                                <tr>
                                    {['Key Milestones', 'Owner', 'Current Baseline', 'Actual / Forecast', 'Prev F/C', 'Remarks', 'Weekly Δ', 'Baseline Δ', ''].map((col, i) => (
                                        <th key={i} style={{ padding: '5px 6px', textAlign: i === 0 || i === 5 ? 'left' : 'center', fontSize: 8, fontWeight: 700, color: palette.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `2px solid ${palette.border}`, whiteSpace: 'nowrap' }}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {milestones.map(ms => (
                                    <tr key={ms.id} style={{ transition: 'background .15s' }}
                                        onMouseEnter={e => { if (editingMilestoneId !== ms.id) e.currentTarget.style.background = palette.blue50; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                                        {editingMilestoneId === ms.id ? (
                                            <>
                                                <td style={{ padding: '4px 6px', borderBottom: `1px solid ${palette.border}` }}>
                                                    <input type="text" defaultValue={ms.milestone || ''} onChange={e => { editingMilestoneRef.current.milestone = e.target.value; }}
                                                        style={{ width: '100%', padding: '3px 5px', fontSize: 10, border: `1px solid ${palette.blue300}`, borderRadius: 5, outline: 'none' }} />
                                                </td>
                                                <td style={{ padding: '4px 6px', borderBottom: `1px solid ${palette.border}` }}>
                                                    <input type="text" defaultValue={ms.owner || ''} onChange={e => { editingMilestoneRef.current.owner = e.target.value; }}
                                                        style={{ width: '100%', padding: '3px 5px', fontSize: 10, border: `1px solid ${palette.blue300}`, borderRadius: 5, outline: 'none' }} />
                                                </td>
                                                <td style={{ padding: '4px 6px', borderBottom: `1px solid ${palette.border}` }}>
                                                    <input type="date" defaultValue={ms.currentBaseline || ''} onChange={e => { editingMilestoneRef.current.currentBaseline = e.target.value; }}
                                                        style={{ padding: '3px 5px', fontSize: 10, border: `1px solid ${palette.blue300}`, borderRadius: 5, outline: 'none' }} />
                                                </td>
                                                <td style={{ padding: '4px 6px', borderBottom: `1px solid ${palette.border}` }}>
                                                    <input type="date" defaultValue={ms.actualForecast || ''} onChange={e => { editingMilestoneRef.current.actualForecast = e.target.value; }}
                                                        style={{ padding: '3px 5px', fontSize: 10, border: `1px solid ${palette.blue300}`, borderRadius: 5, outline: 'none' }} />
                                                </td>
                                                <td style={{ padding: '4px 6px', borderBottom: `1px solid ${palette.border}` }}>
                                                    <input type="date" defaultValue={ms.prevForecast || ''} onChange={e => { editingMilestoneRef.current.prevForecast = e.target.value; }}
                                                        style={{ padding: '3px 5px', fontSize: 10, border: `1px solid ${palette.blue300}`, borderRadius: 5, outline: 'none' }} />
                                                </td>
                                                <td style={{ padding: '4px 6px', borderBottom: `1px solid ${palette.border}` }}>
                                                    <textarea defaultValue={ms.remarks || ''} onChange={e => { editingMilestoneRef.current.remarks = e.target.value; }} spellCheck
                                                        style={{ width: '100%', padding: '3px 5px', fontSize: 10, border: `1px solid ${palette.blue300}`, borderRadius: 5, outline: 'none', minHeight: 40, resize: 'vertical' }} />
                                                </td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, fontWeight: 600, borderBottom: `1px solid ${palette.border}`, color: palette.muted, fontStyle: 'italic' }}>
                                                    Auto
                                                </td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, fontWeight: 600, borderBottom: `1px solid ${palette.border}`, color: palette.muted, fontStyle: 'italic' }}>
                                                    Auto
                                                </td>
                                                <td className="no-print" style={{ padding: '4px 6px', borderBottom: `1px solid ${palette.border}`, whiteSpace: 'nowrap' }}>
                                                    <button onClick={handleSaveMilestone}
                                                        style={{ marginRight: 3, padding: '3px 8px', fontSize: 9, cursor: 'pointer', background: palette.blue600, color: '#fff', border: 'none', borderRadius: 5, fontWeight: 600 }}>Save</button>
                                                    <button onClick={() => { if (!editingMilestoneRef.current?.milestone) setMilestones(milestones.filter(m => m.id !== editingMilestoneRef.current?.id)); setEditingMilestoneId(null); editingMilestoneRef.current = null; }}
                                                        style={{ padding: '3px 8px', fontSize: 9, cursor: 'pointer', background: palette.blue100, color: palette.blue700, border: 'none', borderRadius: 5, fontWeight: 600 }}>Cancel</button>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td style={{ padding: '5px 6px', fontWeight: 600, fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{ms.milestone}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{ms.owner}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{ms.currentBaseline ? moment(ms.currentBaseline).format('DD-MMM-YY') : ''}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{ms.actualForecast ? moment(ms.actualForecast).format('DD-MMM-YY') : ''}</td>
                                                <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, borderBottom: `1px solid ${palette.border}` }}>{ms.prevForecast ? moment(ms.prevForecast).format('DD-MMM-YY') : ''}</td>
                                                <td style={{ padding: '5px 6px', fontSize: 10, borderBottom: `1px solid ${palette.border}`, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ms.remarks}</td>
                                                {(() => {
                                                    const wkDays = ms.actualForecast && ms.prevForecast ? moment(ms.actualForecast).diff(moment(ms.prevForecast), 'days') : null;
                                                    const blDays = ms.actualForecast && ms.currentBaseline ? moment(ms.actualForecast).diff(moment(ms.currentBaseline), 'days') : null;
                                                    const fmtWeeks = (d) => `${Math.round(d / 7)}w`;
                                                    return (
                                                        <>
                                                            <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, fontWeight: 600, borderBottom: `1px solid ${palette.border}`, color: wkDays > 0 ? '#ef4444' : wkDays < 0 ? '#22c55e' : palette.text }}>
                                                                {wkDays !== null ? fmtWeeks(wkDays) : ''}
                                                            </td>
                                                            <td style={{ padding: '5px 6px', textAlign: 'center', fontSize: 10, fontWeight: 600, borderBottom: `1px solid ${palette.border}`, color: blDays > 0 ? '#ef4444' : blDays < 0 ? '#22c55e' : palette.text }}>
                                                                {blDays !== null ? fmtWeeks(blDays) : ''}
                                                            </td>
                                                        </>
                                                    );
                                                })()}
                                                <td className="no-print" style={{ padding: '5px 6px', borderBottom: `1px solid ${palette.border}`, whiteSpace: 'nowrap' }}>
                                                    <button onClick={() => { setEditingMilestoneId(ms.id); editingMilestoneRef.current = { ...ms }; }} title="Edit"
                                                        style={{ marginRight: 3, padding: '2px 5px', fontSize: 10, cursor: 'pointer', background: 'transparent', color: palette.text, border: `1px solid ${palette.border}`, borderRadius: 5, display: 'inline-flex', alignItems: 'center' }}>
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                    </button>
                                                    <button onClick={() => handleDeleteMilestone(ms.id)} title="Delete"
                                                        style={{ padding: '2px 5px', fontSize: 10, cursor: 'pointer', background: 'transparent', color: palette.muted, border: `1px solid ${palette.border}`, borderRadius: 5 }}>✕</button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                                {milestones.length > 0 && <tr>
                                    <td colSpan={9} style={{ padding: 0 }} />
                                </tr>}
                            </tbody>
                        </table>
                    </div>
                </Card>

                {/* ═══════════ PSR SECTIONS (VoltaGrid) — look ahead, risks & issues, accomplishments (above the labor graph) ═══════════ */}
                {isVoltaGrid && (
                    <VoltaGridPsrSections palette={palette} jobUserData={jobUserData} selectedJob={selectedJob} selectedVersion={selectedVersion}
                        versionInfo={versionInfo} locked={!!lockedVersion} save={save} />
                )}

                {/* ═══════════ LABOR GRAPH ═══════════ */}
                <Card style={{ marginBottom: 14 }} className="labor-chart-section">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <SectionTitle icon={icons.barChart}>Labor Hours</SectionTitle>
                        <div className="no-print" style={{ display: 'flex', gap: 6 }}>
                            <button onClick={handleLaborExport} style={{ padding: '3px 10px', fontSize: 9, cursor: 'pointer', background: 'transparent', color: palette.muted, border: `1px solid ${palette.border}`, borderRadius: 5, fontWeight: 600 }}>⬇ Export</button>
                            <button onClick={() => laborUploadRef.current?.click()} style={{ padding: '3px 10px', fontSize: 9, cursor: 'pointer', background: 'transparent', color: palette.muted, border: `1px solid ${palette.border}`, borderRadius: 5, fontWeight: 600 }}>⬆ Upload</button>
                            <input ref={laborUploadRef} type="file" accept=".xlsx,.xls" onChange={handleLaborUpload} style={{ display: 'none' }} />
                        </div>
                    </div>
                    {laborChartConfig ? (
                        <>
                            <div style={{ width: '100%', height: 300 }}>
                                <Bar
                                    plugins={[{
                                        id: 'trendingEACLabel',
                                        afterDraw(chart) {
                                            const dsIndex = chart.data.datasets.findIndex(d => d.label === 'Trending EAC Hours');
                                            if (dsIndex === -1) return;
                                            const meta = chart.getDatasetMeta(dsIndex);
                                            const lastIdx = meta.data.length - 1;
                                            if (lastIdx < 0) return;
                                            const pt = meta.data[lastIdx];
                                            if (!pt || pt.skip) return;
                                            const ctx = chart.ctx;
                                            const x = pt.x;
                                            const y = pt.y;
                                            const val = chart.data.datasets[dsIndex].data[lastIdx];
                                            if (val == null) return;
                                            const label = 'Trending EAC Hours';
                                            const hours = Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
                                            ctx.save();
                                            ctx.font = 'bold 11px sans-serif';
                                            const labelWidth = Math.max(ctx.measureText(label).width, ctx.measureText(hours + ' hrs').width) + 16;
                                            const boxH = 36;
                                            const boxX = x - 14 - labelWidth;
                                            const boxY = Math.max(chart.chartArea.top, y - boxH / 2);
                                            ctx.fillStyle = '#f3f4f6';
                                            ctx.strokeStyle = '#9ca3af';
                                            ctx.lineWidth = 1;
                                            ctx.beginPath();
                                            ctx.rect(boxX, boxY, labelWidth, boxH);
                                            ctx.fill();
                                            ctx.stroke();
                                            ctx.fillStyle = '#374151';
                                            ctx.textAlign = 'left';
                                            ctx.textBaseline = 'top';
                                            ctx.fillText(label, boxX + 8, boxY + 5);
                                            ctx.font = '11px sans-serif';
                                            ctx.fillStyle = '#6b7280';
                                            ctx.fillText(hours + ' hrs', boxX + 8, boxY + 20);
                                            ctx.restore();
                                        }
                                    }]}
                                    data={laborChartData()} options={laborChartConfig.options} />
                            </div>
                            {/* Custom print-only legend */}
                            <div className="chart-legend-print" style={{ display: 'none', flexWrap: 'wrap', gap: '4px 16px', padding: '6px 0', justifyContent: 'center' }}>
                                {[
                                    ...(isVoltaGrid
                                        ? VG_CRAFTS.map(c => ({ label: `Actual ${c.label}`, color: c.actual, type: 'bar' }))
                                        : [{ label: 'Actual Weekly Hours', color: '#F4B183', type: 'bar' }]),
                                    { label: 'Planned Weekly Hours', color: '#A5A5A5', type: 'bar' },
                                    ...(isVoltaGrid
                                        ? VG_CRAFTS.map(c => ({ label: `Forecasted ${c.label}`, color: c.forecast, type: 'bar' }))
                                        : [{ label: 'Forecasted Weekly Hours', color: '#7CAFDD', type: 'bar' }]),
                                    { label: 'Current Planned Cumulative', color: '#597097', type: 'line' },
                                    { label: 'Planned Cumulative', color: '#b4b4b4', type: 'line' },
                                    { label: 'Actual Cumulative', color: '#f4b183', type: 'line' },
                                    { label: 'Forecast Cumulative', color: '#7CAFDD', type: 'line', dashed: true },
                                    ...(isVoltaGrid ? [] : [{ label: 'Trending EAC Hours', color: '#6b7280', type: 'dot' }]),
                                ].map(item => (
                                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                        {item.type === 'bar' ? (
                                            <span style={{ display: 'inline-block', width: 12, height: 10, backgroundColor: item.color, borderRadius: 2 }} />
                                        ) : item.type === 'dot' ? (
                                            <span style={{ display: 'inline-block', width: 10, height: 10, backgroundColor: item.color, borderRadius: '50%' }} />
                                        ) : (
                                            <span style={{ display: 'inline-block', width: 20, height: 0, borderTop: `2.5px ${item.dashed ? 'dashed' : 'solid'} ${item.color}` }} />
                                        )}
                                        <span style={{ fontSize: 8, fontWeight: 600, color: palette.text }}>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                            {/* Screen legend */}
                            <div className="chart-legend-screen" style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', padding: '8px 0 0', justifyContent: 'center' }}>
                                {[
                                    ...(isVoltaGrid
                                        ? VG_CRAFTS.map(c => ({ label: `Actual ${c.label}`, color: c.actual, type: 'bar' }))
                                        : [{ label: 'Actual Weekly', color: '#F4B183', type: 'bar' }]),
                                    { label: 'Planned Weekly', color: '#A5A5A5', type: 'bar' },
                                    ...(isVoltaGrid
                                        ? VG_CRAFTS.map(c => ({ label: `Forecasted ${c.label}`, color: c.forecast, type: 'bar' }))
                                        : [{ label: 'Forecasted Weekly', color: '#7CAFDD', type: 'bar' }]),
                                    { label: 'Planned Cumulative', color: '#597097', type: 'line' },
                                    { label: 'Original Cumulative', color: '#b4b4b4', type: 'line' },
                                    { label: 'Actual Cumulative', color: '#f4b183', type: 'line' },
                                    { label: 'Forecast Cumulative', color: '#7CAFDD', type: 'line', dashed: true },
                                    ...(isVoltaGrid ? [] : [{ label: 'Trending EAC Hours', color: '#6b7280', type: 'dot' }]),
                                ].map(item => (
                                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                        {item.type === 'bar' ? (
                                            <span style={{ display: 'inline-block', width: 10, height: 8, backgroundColor: item.color, borderRadius: 2 }} />
                                        ) : item.type === 'dot' ? (
                                            <span style={{ display: 'inline-block', width: 8, height: 8, backgroundColor: item.color, borderRadius: '50%' }} />
                                        ) : (
                                            <span style={{ display: 'inline-block', width: 18, height: 0, borderTop: `2px ${item.dashed ? 'dashed' : 'solid'} ${item.color}` }} />
                                        )}
                                        <span style={{ fontSize: 9, fontWeight: 600, color: palette.text }}>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                            {/* Weekly data table – transposed: metrics as rows, weeks as columns */}
                            <div className="labor-table-scroll" style={{ overflowX: 'auto', marginTop: 10 }}>
                                <div className="labor-table-inner" style={{ display: 'flex', flexDirection: 'row', width: 'fit-content' }}>
                                    {/* Left legend / row-label column */}
                                    <div style={{ minWidth: 200, flexShrink: 0, position: 'sticky', left: 0, zIndex: 2, background: palette.card }}>
                                        {/* Header row (aligns with week date labels) */}
                                        <div style={{ height: 22, borderBottom: `1px solid ${palette.border}` }}></div>
                                        {/* Weekly rows */}
                                        {[
                                            { label: 'Actual Weekly Hours', color: '#F4B183', width: 15 },
                                            { label: 'Planned Weekly Hours', color: '#A5A5A5', width: 15 },
                                            { label: 'Forecasted Weekly Hours', color: '#7CAFDD', width: 15 },
                                        ].map(r => (
                                            <div key={r.label} style={{ display: 'flex', alignItems: 'center', height: 22, borderBottom: `1px solid ${palette.border}`, paddingLeft: 4 }}>
                                                <span style={{ display: 'inline-block', width: r.width, height: 12, marginRight: 8, backgroundColor: r.color, borderRadius: 2 }}></span>
                                                <span style={{ fontSize: 10, fontWeight: 600 }}>{r.label}</span>
                                            </div>
                                        ))}
                                        {/* FTE rows (VoltaGrid): weekly hours / 60 */}
                                        {isVoltaGrid && [
                                            { label: 'Actual FTE', color: '#F4B183' },
                                            { label: 'Planned FTE', color: '#A5A5A5' },
                                            { label: 'Forecasted FTE', color: '#7CAFDD' },
                                        ].map(r => (
                                            <div key={r.label} title={`Weekly hours / ${FTE_HOURS_PER_WEEK}`} style={{ display: 'flex', alignItems: 'center', height: 22, borderBottom: `1px solid ${palette.border}`, paddingLeft: 4 }}>
                                                <span style={{ display: 'inline-block', width: 15, height: 12, marginRight: 8, border: `2px solid ${r.color}`, boxSizing: 'border-box', borderRadius: 2 }}></span>
                                                <span style={{ fontSize: 10, fontWeight: 600 }}>{r.label}</span>
                                                <span style={{ fontSize: 8, fontWeight: 600, color: palette.muted, marginLeft: 5 }}>hrs / {FTE_HOURS_PER_WEEK}</span>
                                            </div>
                                        ))}
                                        {/* Cumulative rows */}
                                        {[
                                            { label: 'Current Planned Cumulative', color: '#597097', width: 35 },
                                            { label: 'Planned Cumulative Hours', color: '#b4b4b4', width: 35 },
                                            { label: 'Actual Cumulative Hours', color: '#f4b183', width: 35 },
                                            { label: 'Forecasted Cumulative Hours', color: '#7CAFDD', width: 35 },
                                        ].map(r => (
                                            <div key={r.label} style={{ display: 'flex', alignItems: 'center', height: 22, borderBottom: `1px solid ${palette.border}`, paddingLeft: 4 }}>
                                                <span style={{ display: 'inline-block', width: r.width, height: 3, marginRight: 8, backgroundColor: r.color, borderRadius: 1 }}></span>
                                                <span style={{ fontSize: 10, fontWeight: 600 }}>{r.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Week columns */}
                                    {laborChartConfig.tableRows.map((r, i) => {
                                        const isLast = i === laborChartConfig.tableRows.length - 1;
                                        const cellStyle = { height: 22, fontSize: 10, padding: '0 6px', textAlign: 'right', borderBottom: `1px solid ${palette.border}`, borderRight: isLast ? `1px solid ${palette.border}` : 'none', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', whiteSpace: 'nowrap' };
                                        return (
                                            <div key={i} style={{ minWidth: 62 }}>
                                                <div style={{ ...cellStyle, fontWeight: 700, fontSize: 9, justifyContent: 'center', textAlign: 'center' }}>{r.label}</div>
                                                <div style={cellStyle}>{r.actual ? fNum(r.actual, 0) : ''}</div>
                                                <div style={cellStyle}>{r.planned ? fNum(r.planned, 0) : ''}</div>
                                                <div style={cellStyle}>{r.forecasted ? fNum(r.forecasted, 0) : ''}</div>
                                                {isVoltaGrid && <div style={cellStyle}>{r.actualFte ? fNum(r.actualFte, 1) : ''}</div>}
                                                {isVoltaGrid && <div style={cellStyle}>{r.plannedFte ? fNum(r.plannedFte, 1) : ''}</div>}
                                                {isVoltaGrid && <div style={cellStyle}>{r.forecastedFte ? fNum(r.forecastedFte, 1) : ''}</div>}
                                                <div style={cellStyle}>{r.cumCurrentPlanned ? fNum(r.cumCurrentPlanned, 0) : ''}</div>
                                                <div style={cellStyle}>{r.cumPlanned ? fNum(r.cumPlanned, 0) : ''}</div>
                                                <div style={cellStyle}>{r.cumActual ? fNum(r.cumActual, 0) : ''}</div>
                                                <div style={cellStyle}>{r.cumForecast ? fNum(r.cumForecast, 0) : ''}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ padding: 20, textAlign: 'center', color: palette.muted, fontSize: 11 }}>No labor data available</div>
                    )}
                </Card>

                {/* ═══════════ PROGRESS S-CURVE (VoltaGrid) — Plan / Actual / Forecast cumulative % complete by labor hours, the client's S-curve workbook (after the labor S-curve) ═══════════ */}
                {isVoltaGrid && VG_SHOW_PROGRESS_CURVE && (
                    <VoltaGridProgressCurve palette={palette} versionInfo={versionInfo} jobUserData={jobUserData} selectedJob={selectedJob} selectedVersion={selectedVersion} />
                )}


                {/* ═══════ PF OVER TIME ═══════ */}
                {pfOverTimeData && Object.keys(pfOverTimeData).length > 0 && (() => {
                    // Sort versions by start date
                    const sortedVersions = Object.entries(pfOverTimeData)
                        .sort(([, a], [, b]) => new Date(a.startDate) - new Date(b.startDate));

                    // Collect all discipline names
                    const allDisciplines = new Set();
                    sortedVersions.forEach(([, v]) => {
                        Object.keys(v.disciplines).forEach(d => allDisciplines.add(d));
                    });
                    const disciplineList = Array.from(allDisciplines);

                    // Only keep versions where at least one discipline has BOTH actual and forecast
                    const validVersionIndices = [];
                    sortedVersions.forEach(([, v], idx) => {
                        const hasBoth = disciplineList.some(disc => {
                            const d = v.disciplines[disc];
                            return d && pNum(d.actualPF) && pNum(d.pmPF);
                        });
                        if (hasBoth) validVersionIndices.push(idx);
                    });
                    const filteredVersions = validVersionIndices.map(i => sortedVersions[i]);
                    const labels = filteredVersions.map(([, v]) => moment(v.startDate).format('MM/DD/YY'));

                    // Split disciplines into those with data vs no data
                    const disciplinesWithData = disciplineList.filter(disc =>
                        filteredVersions.some(([, v]) => {
                            const d = v.disciplines[disc];
                            return d && pNum(d.actualPF);
                        })
                    );
                    const disciplinesNoData = disciplineList.filter(disc =>
                        !filteredVersions.some(([, v]) => {
                            const d = v.disciplines[disc];
                            return d && pNum(d.actualPF);
                        })
                    );

                    // Color palette
                    const lineColors = [
                        '#0078d4', '#e81123', '#107c10', '#ff8c00', '#5c2d91',
                        '#008272', '#d83b01', '#0063b1', '#b4009e', '#767676',
                        '#00b7c3', '#e3008c', '#4c4a48', '#ca5010', '#038387'
                    ];

                    // Show Actual PF values — only disciplines that have data
                    const datasets = disciplinesWithData.map((disc, i) => {
                        const color = lineColors[i % lineColors.length];
                        return {
                            label: disc,
                            data: filteredVersions.map(([, v]) => {
                                const d = v.disciplines[disc];
                                const actual = d ? pNum(d.actualPF) : 0;
                                return actual ? parseFloat(actual.toFixed(3)) : null;
                            }),
                            borderColor: color,
                            backgroundColor: color + '33',
                            tension: 0.3,
                            spanGaps: false,
                            pointRadius: 5,
                            pointHoverRadius: 7,
                            borderWidth: 2.5,
                        };
                    });

                    const chartOptions = {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                            title: {
                                display: true,
                                text: 'Actual PF Over Time',
                                font: { size: 13, weight: '600' },
                                color: palette.text,
                                padding: { bottom: 12 }
                            },
                            legend: {
                                position: 'bottom',
                                labels: {
                                    usePointStyle: true,
                                    boxWidth: 10,
                                    font: { size: 10 },
                                    padding: 10
                                }
                            },
                            tooltip: {
                                callbacks: {
                                    label: (ctx) => {
                                        const v = ctx.parsed.y;
                                        const disc = ctx.dataset.label;
                                        const versionEntry = filteredVersions[ctx.dataIndex];
                                        const d = versionEntry ? versionEntry[1].disciplines[disc] : null;
                                        const actual = d ? pNum(d.actualPF) : null;
                                        const forecast = d ? pNum(d.pmPF) : null;
                                        const detail = forecast ? ` (Forecast: ${forecast.toFixed(2)})` : '';
                                        return `${disc}: ${v != null ? v.toFixed(3) : 'N/A'}${detail}`;
                                    }
                                }
                            }
                        },
                        scales: {
                            y: {
                                title: { display: true, text: 'Actual PF', font: { size: 11 } },
                                grid: { color: palette.border + '44' }
                            },
                            x: {
                                title: { display: true, text: 'Version Date', font: { size: 11 } },
                                grid: { display: false }
                            }
                        }
                    };

                    if (filteredVersions.length === 0) return null;

                    return (
                        <Card style={{ marginTop: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <SectionTitle icon={icons.trending}>PF Over Time</SectionTitle>
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <button onClick={() => { const c = pfChartRef.current; if (!c) return; c.data.datasets.forEach((_, i) => { c.setDatasetVisibility(i, true); }); c.update(); }} style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, border: `1px solid ${palette.border}`, background: palette.card, color: palette.text, cursor: 'pointer', fontWeight: 600 }}>All On</button>
                                    <button onClick={() => { const c = pfChartRef.current; if (!c) return; c.data.datasets.forEach((_, i) => { c.setDatasetVisibility(i, false); }); c.update(); }} style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, border: `1px solid ${palette.border}`, background: palette.card, color: palette.text, cursor: 'pointer', fontWeight: 600 }}>All Off</button>
                                </div>
                            </div>
                            <div style={{ height: 300 }}>
                                <Line ref={pfChartRef} data={{ labels, datasets }} options={chartOptions} />
                            </div>
                            <div style={{ fontSize: 10, color: palette.textLight, marginTop: 8, textAlign: 'center' }}>
                                Showing Actual PF tracked across forecast versions &nbsp;|&nbsp; Hover to see PM Forecasted PF
                            </div>
                            {disciplinesNoData.length > 0 && (
                                <div style={{ marginTop: 12, padding: '8px 12px', background: palette.blue50, borderRadius: 6, border: `1px solid ${palette.border}` }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: palette.textLight, marginBottom: 4 }}>No Data</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                                        {disciplinesNoData.map(disc => (
                                            <span key={disc} style={{ fontSize: 11, color: palette.textLight }}>{disc}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Card>
                    );
                })()}

                {/* SPI Toggle */}
                {/* SPI toggle + 2 Week Look Ahead: hidden on the VoltaGrid tab — cost-plus, no schedule index or crew-by-phase plan there. */}
                {!isVoltaGrid && (<>
                <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, marginBottom: 8 }}>
                    <button onClick={() => { const next = !spiExpanded; setSpiExpanded(next); updateUserDataArray({ userId: '154045', userName: 'Seth Bailey', tab: 'Overview', fieldName: 'SpiEnabled', value: next + '', version: '', id: selectedJob || '', rowId: '1', lockedVersion: false }); }} style={{ padding: '5px 10px', background: spiExpanded ? palette.blue50 : palette.card, border: `1px solid ${spiExpanded ? palette.blue300 : palette.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 10, fontWeight: 600, color: spiExpanded ? palette.blue700 : palette.muted, display: 'flex', alignItems: 'center', gap: 4, transition: 'all .15s' }}
                        title={spiExpanded ? 'Hide SPI / Schedule tracking' : 'Show SPI / Schedule tracking'}>
                        <span style={{ width: 22, height: 12, borderRadius: 6, background: spiExpanded ? palette.blue500 : '#cbd5e1', position: 'relative', display: 'inline-block', transition: 'background .2s' }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: spiExpanded ? 12 : 2, transition: 'left .2s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
                        </span>
                        SPI
                    </button>
                </div>

                {/* ═══════════ 2 WEEK LOOK AHEAD (collapsed by default) ═══════════ */}
                {/* Crew per phase for the report week and the next one. Closed
                    on load and it stays out of the print sheet while it is —
                    the report is a fixed one-pager and this is a planning aid
                    someone opens when they want it. */}
                <div className="look-ahead-section" style={{ marginBottom: 14 }}>
                    <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: lookAheadOpen ? 8 : 0 }}>
                        <button onClick={() => setLookAheadOpen(!lookAheadOpen)}
                            style={{ padding: '5px 10px', background: lookAheadOpen ? palette.blue50 : palette.card, border: `1px solid ${lookAheadOpen ? palette.blue300 : palette.border}`, borderRadius: 8, cursor: 'pointer', fontSize: 10, fontWeight: 600, color: lookAheadOpen ? palette.blue700 : palette.muted, display: 'flex', alignItems: 'center', gap: 4, transition: 'all .15s' }}
                            title={lookAheadOpen ? 'Hide the 2 week look ahead' : 'Show the 2 week look ahead'}>
                            <span style={{ width: 22, height: 12, borderRadius: 6, background: lookAheadOpen ? palette.blue500 : '#cbd5e1', position: 'relative', display: 'inline-block', transition: 'background .2s' }}>
                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: lookAheadOpen ? 12 : 2, transition: 'left .2s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
                            </span>
                            2 Week Look Ahead
                        </button>
                    </div>
                    {lookAheadOpen && (
                        <Card className="look-ahead-card">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <SectionTitle icon={icons.users}>2 Week Look Ahead — Crew by Phase</SectionTitle>
                                <button className="no-print" onClick={handleLookAheadExport}
                                    style={{ padding: '3px 10px', fontSize: 9, cursor: 'pointer', background: 'transparent', color: palette.muted, border: `1px solid ${palette.border}`, borderRadius: 5, fontWeight: 600, marginBottom: 12 }}>⬇ Export</button>
                            </div>
                            {lookAhead.rows.length === 0 ? (
                                <div style={{ fontSize: 11, color: palette.muted, padding: '4px 0' }}>No staffed phases in this window.</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, fontVariantNumeric: 'tabular-nums' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 9, fontWeight: 700, color: palette.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${palette.border}` }}>Phase</th>
                                            <th style={{ textAlign: 'left', padding: '4px 6px', fontSize: 9, fontWeight: 700, color: palette.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${palette.border}` }}>Description</th>
                                            {lookAhead.weeks.map(w => (
                                                <th key={w} style={{ textAlign: 'right', width: 90, padding: '4px 6px', fontSize: 9, fontWeight: 700, color: palette.muted, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: `1px solid ${palette.border}` }}>Week of {moment(w).format('M/D')}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lookAhead.rows.map(r => (
                                            <tr key={r.phase}>
                                                <td style={{ padding: '3px 6px', color: palette.muted, fontWeight: 600, borderBottom: `1px solid ${palette.blue50}` }}>{r.phase}</td>
                                                <td style={{ padding: '3px 6px', color: palette.text, borderBottom: `1px solid ${palette.blue50}`, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description}</td>
                                                <td style={{ padding: '3px 6px', textAlign: 'right', color: palette.text, borderBottom: `1px solid ${palette.blue50}` }}>{r.c0 || ''}</td>
                                                <td style={{ padding: '3px 6px', textAlign: 'right', color: palette.text, borderBottom: `1px solid ${palette.blue50}` }}>{r.c1 || ''}</td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td style={{ padding: '4px 6px', fontWeight: 800, color: palette.blue700 }} colSpan={2}>Total crew</td>
                                            <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 800, color: palette.blue700 }}>{lookAhead.tot0}</td>
                                            <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 800, color: palette.blue700 }}>{lookAhead.tot1}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </Card>
                    )}
                </div>
                </>)}

                {/* ═══════════ AI INSIGHTS ═══════════ */}
                {/* Hidden on the VoltaGrid tab — the manpower loading curve closes that page instead. */}
                {!isVoltaGrid && (
                    <div style={{ background: palette.card, borderRadius: 10, boxShadow: shadows.md, border: `1px solid ${palette.border}`, padding: 16, marginBottom: 14 }}>
                        <AISummary buildPrompt={buildOverviewPrompt} palette={palette} pageKey={`overview_${selectedJob}`} />
                    </div>
                )}

                {/* ═══════════ CASH FLOW FORECAST (VoltaGrid) — the Revenue Curve tab's two curves as the cash flow workbook (external view) ═══════════ */}
                {isVoltaGrid && VG_SHOW_CASH_FLOW && (
                    <VoltaGridCashFlow palette={palette} versionInfo={versionInfo} jobUserData={jobUserData} selectedJob={selectedJob} exportRef={cashFlowExportRef} />
                )}

                {/* ═══════════ CREW & EQUIPMENT ON THE JOB (VoltaGrid) — labor + equipment rollups, same basis as the weekly summary tickets ═══════════ */}
                {isVoltaGrid && <VoltaGridEquipment palette={palette} billRev={billRev} versionStart={versionInfo.Version_Start_DateTime} />}

                {/* ═══════════ INTERNAL REVIEW (VoltaGrid) — the Revenue Budget Review and the Field Leadership Review, for Strike's eyes:
                    screen only. `no-print` keeps the block off the browser's print; `export-drop` takes it out of the
                    Export's capture altogether (handleVoltaGridExport), so the client PDF never carries it. ═══════════ */}
                {isVoltaGrid && (
                    <div className="no-print export-drop internal-review" style={{ marginTop: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: palette.text, padding: '3px 10px', border: `1px solid ${palette.border}`, borderRadius: 999, background: palette.card }}>Internal review</span>
                            <span style={{ fontSize: 11, color: palette.muted }}>Strike only — left out of the Export and the printed overview</span>
                            <div style={{ flex: 1, height: 1, background: palette.border }} />
                        </div>
                        <VoltaGridRevenueReview palette={palette} jobUserData={jobUserData} selectedJob={selectedJob} jobName={selectedJobDetails?.BU_Description || ''}
                            selectedVersion={selectedVersion} versionStart={versionInfo.Version_Start_DateTime} billRev={billRev} />
                        <VoltaGridLeadership palette={palette} jobUserData={jobUserData} selectedJob={selectedJob} selectedVersion={selectedVersion}
                            versionStart={versionInfo.Version_Start_DateTime} billRev={billRev} locked={!!lockedVersion} save={save} />
                    </div>
                )}

            </div>{/* end content area */}
        </div>
    );
}

export default Overview;
